import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import ActualsUploader from "./ActualsUploader";
import BudgetUploader from "./BudgetUploader";
import AdminUsers from "./AdminUsers";
import AdminBusinessUnits from "./AdminBusinessUnits";
import ActualsDashboard from "./ActualsDashboard";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from "recharts";
import "./AdminPanel.css";
import "./Dashboard.css";

// ─── Category colors (shared with Dashboard) ─────────────────────────────────
const CAT_COLOR = (cat: string): string => ({
  COMPENSATION:     "#4A90E2",
  OCCUPANCY:        "#e84393",
  OPERATING:        "#ea580c",
  ADVERTISING:      "#7c3aed",
  ALLOCATED:        "#475569",
  AMORT:            "#64748b",
} as Record<string, string>)[cat.toUpperCase().replace(/\s/g, "")] ?? "#94a3b8";

const EXCLUDED_CATS = new Set(["Corp OH", "CORP_OH", "Uncategorized", "OtherAdjustments", "OTHERADJUSTMENTS"]);

// ─── Types ───────────────────────────────────────────────────────────────────

interface Account {
  account:  string;
  category: string;
  cc_count: number;
  fiscal_years?: string | null;
}

interface UnmappedAccount {
  id:          number;
  account:     string;
  category:    string;
  budget_rows: number;
  actual_rows: number;
}

interface AcctDraft {
  account:  string;
  category: string;
  editing:  boolean;
  saving:   boolean;
  feedback: { ok: boolean; msg: string } | null;
}

interface DimWindow {
  dimension: string;
  fiscal_year?: string;
  open_from: string; // "YYYY-MM-DD"
  open_to:   string;
}

interface CostCenter {
  cost_center_number: string;
  cost_center_name:   string;
  cost_center_owner?: string;
  business_unit:      string;
  business_unit_id?:  number;
  email?:             string;
  role?:              string;
  is_first_login:     number;
  deactivated_from_fy?: string | null;
}

interface BUOption { id: number; code: string; name: string; }
interface UserOption { id: number; name: string; email: string; owned_cost_center?: string; }

// Draft state per CC row (keyed by original CC # in ccDrafts map)
interface CCDraft {
  cost_center_number: string; // editable — may differ from key during edit
  cost_center_name:   string;
  business_unit:      string;
  business_unit_id:   number | null;
  editing:   boolean;
  saving:    boolean;
  resetting: boolean;
  feedback: { ok: boolean; msg: string } | null;
}

interface CategoryKPI {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
  top_over:  CCRow[];
  top_under: CCRow[];
}

interface CCRow {
  cost_center:      string;
  cost_center_name: string;
  business_unit:    string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface CCCatBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface AcctRow {
  account:        string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface VRAccountRow {
  account:        string;
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface VRCategoryBreakdown {
  category:       string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface VRCostCenter {
  cost_center:          string;
  cost_center_name:     string;
  business_unit:        string;
  month_budget:         number;
  month_actual:         number;
  month_variance:       number;
  ytd_budget:           number;
  ytd_actual:           number;
  ytd_variance:         number;
  annual_budget:        number;
  forecast_total:       number;
  mpy_forecast?:        number;
  top_unfavorable:      VRAccountRow[];
  top_favorable:        VRAccountRow[];
  category_breakdown?:  VRCategoryBreakdown[];
}

interface VRActionPlanItem {
  id: number;
  cost_center: string;
  issue_text: string | null;
  action_text: string;
  category: string | null;
  closeout_milestone: string | null;
  status: "OPEN" | "CLOSED";
  created_by: string | null;
}

interface BUSummaryRow {
  business_unit:  string;
  month_budget:   number;
  month_actual:   number;
  month_variance: number;
  ytd_budget:     number;
  ytd_actual:     number;
  ytd_variance:   number;
}

interface ForecastCatKPI {
  category:      string;
  dim_a:         number;
  dim_b:         number;
  change:        number;
  top_increased: ForecastCCRow[];
  top_decreased: ForecastCCRow[];
  all_ccs:       ForecastCCRow[];
}

interface ForecastCCRow {
  cost_center:      string;
  cost_center_name: string;
  business_unit:    string;
  dim_a:  number;
  dim_b:  number;
  change: number;
}

interface ForecastBUSummaryRow {
  business_unit: string;
  dim_a:  number;
  dim_b:  number;
  change: number;
}

interface ActualsCatKPI {
  category:     string;
  actual:       number;
  top_spenders: ActualsCCRow[];
}

interface ActualsCCRow {
  cost_center:      string;
  cost_center_name: string;
  actual: number;
}

interface MonthlyTrendPoint {
  month:      string;
  short:      string;
  budget:     number;
  actual:     number;
  hasActuals: boolean;
}

interface ActualsAcctRow {
  account: string;
  actual:  number;
}

// Draft state per dimension row
interface DimDraft {
  open_from:      string;
  open_to:        string;
  orig_open_from: string; // server value — restored on Cancel
  orig_open_to:   string;
  editing:  boolean;
  saving:   boolean;
  feedback: { ok: boolean; msg: string } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ALL_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"] as const;

/** Returns the most relevant forecast cycle for today's date. */
function getDefaultDim(): string {
  const m = new Date().getMonth() + 1; // 1–12
  if (m >= 4 && m <= 6) return "July Forecast";
  if (m >= 7 && m <= 9) return "October Forecast";
  return "January Forecast"; // Jan–Mar and Oct–Dec
}

function fmtDate(d: string): string {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function fmtMoney(n: number): string {
  const abs = Math.abs(n);
  return "$" + Math.round(abs).toLocaleString("en-US");
}

/** Accounting notation: unfavorable (positive variance) in brackets, favorable plain */
function fmtVar(n: number): string {
  const val = fmtMoney(Math.abs(n));
  return n > 0 ? `(${val})` : val;
}

// ─── Fiscal-year helpers ─────────────────────────────────────────────────────

// FY{N} = April{N} → March{N+1}  (e.g. FY26 = Apr 2026 – Mar 2027)
// FY is named after the calendar year it starts in:
//   Apr 2026 – Mar 2027 = FY26.  So May 2026 → FY26.
function autoFY(): string {
  const now = new Date();
  const yr  = now.getFullYear();
  return now.getMonth() < 3          // Jan–Mar → still in previous year's FY
    ? `FY${String(yr - 1).slice(-2)}`
    : `FY${String(yr).slice(-2)}`;    // Apr–Dec → current year's FY
}

function buildFYOptions(): string[] {
  const yr = new Date().getFullYear();
  const opts: string[] = [];
  for (let y = yr - 1; y <= yr + 3; y++) opts.push(`FY${String(y).slice(-2)}`);
  return opts;
}
const FY_OPTIONS = buildFYOptions();

// ─── Component ───────────────────────────────────────────────────────────────

type Tab = "forecast" | "costcenters" | "accounts" | "dashboard" | "buforecast" | "upload" | "salary" | "finance" | "businessunits" | "users" | "variancereport" | "auditlog";

const TAB_FROM_URL: Record<string, Tab> = {
  "forecast-windows": "forecast",
  "cost-centers":     "costcenters",
  "accounts":         "accounts",
  "bu-dashboard":     "dashboard",
  "bu-forecast":      "buforecast",
  "data-upload":      "upload",
  "salary-config":    "salary",
  "finance-view":     "finance",
  "business-units":   "businessunits",
  "users":            "users",
  "variance-report":  "variancereport",
  "audit-log":        "auditlog",
};

const URL_FROM_TAB: Record<Tab, string> = {
  forecast:        "forecast-windows",
  costcenters:     "cost-centers",
  accounts:        "accounts",
  dashboard:       "bu-dashboard",
  buforecast:      "bu-forecast",
  upload:          "data-upload",
  salary:          "salary-config",
  finance:         "finance-view",
  businessunits:   "business-units",
  users:           "users",
  variancereport:  "variance-report",
  auditlog:        "audit-log",
};

// ─── BU Leader Card sub-component (used in Cost Centers tab) ─────────────────

interface BULeaderCardProps {
  leader: CostCenter;
  allCostCenters: CostCenter[];
  onRefresh: () => void;
}

const BULeaderCard: React.FC<BULeaderCardProps> = ({ leader, allCostCenters, onRefresh }) => {
  const [assignments, setAssignments] = useState<{ assigned_cc: string; cost_center_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingCC, setAddingCC] = useState("");

  useEffect(() => {
    api.get(`/admin/bu-leaders/${leader.cost_center_number}/assignments`)
      .then(res => { setAssignments(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [leader.cost_center_number]);

  const assignCC = () => {
    if (!addingCC) return;
    api.post(`/admin/bu-leaders/${leader.cost_center_number}/assignments`, { assigned_ccs: [addingCC] })
      .then(() => {
        const ccInfo = allCostCenters.find(c => c.cost_center_number === addingCC);
        setAssignments(prev => [...prev, { assigned_cc: addingCC, cost_center_name: ccInfo?.cost_center_name || addingCC }]);
        setAddingCC("");
      })
      .catch(() => {});
  };

  const removeAssignment = (assignedCC: string) => {
    api.delete(`/admin/bu-leaders/${leader.cost_center_number}/assignments/${assignedCC}`)
      .then(() => setAssignments(prev => prev.filter(a => a.assigned_cc !== assignedCC)))
      .catch(() => {});
  };

  const demote = () => {
    api.put(`/admin/cost-centers/${leader.cost_center_number}/role`, { role: "user" })
      .then(() => onRefresh())
      .catch(() => {});
  };

  // CCs available to assign (not already assigned and not the leader itself)
  const assignedSet = new Set(assignments.map(a => a.assigned_cc));
  const availableCCs = allCostCenters.filter(
    cc => cc.cost_center_number !== leader.cost_center_number && !assignedSet.has(cc.cost_center_number)
  );

  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span className="material-icons" style={{ fontSize: "18px", color: "#6366f1" }}>person</span>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
            {leader.cost_center_number} – {leader.cost_center_name}
          </span>
          <span style={{ fontSize: "12px", color: "#6b7280" }}>({leader.cost_center_owner})</span>
        </div>
        <button
          className="admin-btn admin-btn--ghost"
          onClick={demote}
          title="Remove BU Leader role"
          style={{ fontSize: "11px" }}
        >
          <span className="material-icons" style={{ fontSize: "14px" }}>arrow_downward</span>
          Demote
        </button>
      </div>

      {/* Assigned CCs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
        {loading ? (
          <span style={{ fontSize: "12px", color: "#9ca3af" }}>Loading…</span>
        ) : assignments.length === 0 ? (
          <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>No cost centers assigned yet</span>
        ) : (
          assignments.map(a => (
            <span
              key={a.assigned_cc}
              style={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                background: "#e0e7ff", color: "#3730a3", fontSize: "12px",
                padding: "2px 8px", borderRadius: "4px", fontWeight: 500,
              }}
            >
              {a.assigned_cc} – {a.cost_center_name}
              <button
                onClick={() => removeAssignment(a.assigned_cc)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}
                title="Remove assignment"
              >
                <span className="material-icons" style={{ fontSize: "14px", color: "#6366f1" }}>close</span>
              </button>
            </span>
          ))
        )}
      </div>

      {/* Add CC */}
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <select
          className="admin-select"
          value={addingCC}
          onChange={e => setAddingCC(e.target.value)}
          style={{ fontSize: "12px" }}
        >
          <option value="">Assign a cost center…</option>
          {availableCCs.map(cc => (
            <option key={cc.cost_center_number} value={cc.cost_center_number}>
              {cc.cost_center_number} – {cc.cost_center_name}
            </option>
          ))}
        </select>
        <button
          className="admin-btn admin-btn--primary"
          onClick={assignCC}
          disabled={!addingCC}
          style={{ fontSize: "11px", padding: "4px 10px" }}
        >
          <span className="material-icons" style={{ fontSize: "14px" }}>add</span>
          Assign
        </button>
      </div>
    </div>
  );
};

// ─── Main AdminPanel Component ───────────────────────────────────────────────

// Tab grouping
const ADMIN_SETUP_TABS: Tab[] = ["businessunits", "costcenters", "users", "accounts", "auditlog"];
const FORECAST_SETUP_TABS: Tab[] = ["forecast", "salary", "upload", "dashboard", "variancereport", "buforecast", "finance"];

interface AdminPanelProps {
  onImpersonate?: (ccNum: string, ccName: string, ccOwner: string) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ onImpersonate }) => {
  const { tab: tabParam } = useParams<{ tab: string }>();
  const navigate          = useNavigate();
  const activeTab: Tab    = (tabParam && TAB_FROM_URL[tabParam]) ? TAB_FROM_URL[tabParam] : "dashboard";
  const setActiveTab      = (tab: Tab) => navigate(`/admin/${URL_FROM_TAB[tab]}`);
  const isAdminSetupGroup = ADMIN_SETUP_TABS.includes(activeTab);

  // ── Dimension Windows state ──────────────────────────────────────────────
  const [dimDrafts, setDimDrafts] = useState<Record<string, DimDraft>>({});
  const [dimsLoading, setDimsLoading] = useState(true);
  const [forecastActive, setForecastActive] = useState(false);
  const [selectedDim, setSelectedDim] = useState<string | null>(null);
  const [selectionMsg, setSelectionMsg] = useState<string | null>(null);
  const selectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Active Fiscal Year state ─────────────────────────────────────────────
  const [currentFY,  setCurrentFY]  = useState<string>(autoFY());
  const [fyDraft,    setFyDraft]    = useState<string>(autoFY());
  const [fyLoading,  setFyLoading]  = useState(false);
  const [fyLoaded,   setFyLoaded]   = useState(false); // true once FY fetched from backend
  const [fyFeedback, setFyFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // ── Password-reset email setting ──────────────────────────────────────────
  const [resetEmail,         setResetEmail]         = useState("");
  const [resetEmailDraft,    setResetEmailDraft]    = useState("");
  const [resetEmailLoading,  setResetEmailLoading]  = useState(false);
  const [resetEmailFeedback, setResetEmailFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // ── Forecast variance allowed setting ─────────────────────────────────────
  const [varianceAllowed,        setVarianceAllowed]        = useState<number>(0);
  const [varianceAllowedDraft,   setVarianceAllowedDraft]   = useState<string>("0");
  const [varianceAllowedLoading, setVarianceAllowedLoading] = useState(false);
  const [varianceAllowedFeedback,setVarianceAllowedFeedback]= useState<{ ok: boolean; msg: string } | null>(null);

  // ── Help URL settings ───────────────────────────────────────────────────
  const [helpUrlAdmin,         setHelpUrlAdmin]         = useState("");
  const [helpUrlAdminDraft,    setHelpUrlAdminDraft]    = useState("");
  const [helpUrlUser,          setHelpUrlUser]          = useState("");
  const [helpUrlUserDraft,     setHelpUrlUserDraft]     = useState("");
  const [helpUrlLoading,       setHelpUrlLoading]       = useState(false);
  const [helpUrlFeedback,      setHelpUrlFeedback]      = useState<{ ok: boolean; msg: string } | null>(null);

  // ── Audit Log state ─────────────────────────────────────────────────────
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter]   = useState<{ cc: string; action: string }>({ cc: "", action: "" });

  const loadAuditLog = useCallback(() => {
    setAuditLoading(true);
    const params = new URLSearchParams();
    if (auditFilter.cc) params.set("cost_center", auditFilter.cc);
    if (auditFilter.action) params.set("action", auditFilter.action);
    params.set("limit", "200");
    api.get(`/admin/audit-log?${params.toString()}`)
      .then(r => setAuditEntries(r.data))
      .catch(() => {})
      .finally(() => setAuditLoading(false));
  }, [auditFilter]);

  const loadDimWindows = useCallback(() => {
    setDimsLoading(true);
    api.get(`/admin/dimension-windows?fy=${encodeURIComponent(currentFY)}`)
      .then(res => {
        const drafts: Record<string, DimDraft> = {};
        (res.data as DimWindow[]).forEach(w => {
          const from = w.open_from.slice(0, 10);
          const to   = w.open_to.slice(0, 10);
          drafts[w.dimension] = {
            open_from: from, open_to: to,
            orig_open_from: from, orig_open_to: to,
            editing: false, saving: false, feedback: null,
          };
        });
        ALL_DIMS.forEach(dim => {
          if (!drafts[dim]) {
            drafts[dim] = {
              open_from: "", open_to: "",
              orig_open_from: "", orig_open_to: "",
              editing: false, saving: false, feedback: null,
            };
          }
        });
        setDimDrafts(drafts);
        setDimsLoading(false);
      })
      .catch(() => setDimsLoading(false));
  }, [currentFY]);

  const loadForecastConfig = useCallback(() => {
    api.get("/settings/forecast-config")
      .then(res => {
        setForecastActive(res.data.active);
        const dim = res.data.dimension || null;
        if (dim) {
          setSelectedDim(dim);
        } else {
          // Nothing set in DB — auto-select a sensible default and persist it
          const defaultDim = getDefaultDim();
          setSelectedDim(defaultDim);
          api.put("/admin/active-dimension", { dimension: defaultDim }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDimWindows();
    loadForecastConfig();
    api.get("/admin/fiscal-year")
      .then(r => {
        const fy = r.data.fiscalYear || autoFY();
        setCurrentFY(fy);
        setFyDraft(fy);
      })
      .catch(() => {})
      .finally(() => setFyLoaded(true));
    api.get("/admin/settings/password-reset-email")
      .then(r => {
        setResetEmail(r.data.email || "");
        setResetEmailDraft(r.data.email || "");
      })
      .catch(() => {});
    api.get("/admin/settings/forecast-variance-allowed")
      .then(r => {
        const v = r.data.variance_allowed ?? 0;
        setVarianceAllowed(v);
        setVarianceAllowedDraft(String(v));
      })
      .catch(() => {});
    api.get("/admin/settings/help-urls")
      .then(r => {
        setHelpUrlAdmin(r.data.help_url_admin || "");
        setHelpUrlAdminDraft(r.data.help_url_admin || "");
        setHelpUrlUser(r.data.help_url_user || "");
        setHelpUrlUserDraft(r.data.help_url_user || "");
      })
      .catch(() => {});
  }, [loadDimWindows, loadForecastConfig]);

  const handleSaveFY = useCallback(async () => {
    setFyLoading(true);
    setFyFeedback(null);
    try {
      await api.put("/admin/fiscal-year", { fiscalYear: fyDraft });
      setCurrentFY(fyDraft);
      setFyFeedback({ ok: true, msg: `Active fiscal year set to ${fyDraft}` });
      // Notify header to update FY badge
      window.dispatchEvent(new CustomEvent("fy-changed", { detail: fyDraft }));
    } catch (err: any) {
      const msg = err.response?.data?.error || "Failed to save fiscal year";
      setFyFeedback({ ok: false, msg });
    } finally {
      setFyLoading(false);
    }
  }, [fyDraft]);

  const handleSaveResetEmail = async () => {
    setResetEmailLoading(true);
    setResetEmailFeedback(null);
    try {
      await api.put("/admin/settings/password-reset-email", { email: resetEmailDraft });
      setResetEmail(resetEmailDraft);
      setResetEmailFeedback({ ok: true, msg: "Saved" });
    } catch (err: any) {
      setResetEmailFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to save" });
    } finally {
      setResetEmailLoading(false);
    }
  };

  const handleSaveVarianceAllowed = async () => {
    const val = parseFloat(varianceAllowedDraft);
    if (isNaN(val) || val < 0) {
      setVarianceAllowedFeedback({ ok: false, msg: "Enter a valid dollar amount (0 or more)" });
      return;
    }
    setVarianceAllowedLoading(true);
    setVarianceAllowedFeedback(null);
    try {
      await api.put("/admin/settings/forecast-variance-allowed", { variance_allowed: val });
      setVarianceAllowed(val);
      setVarianceAllowedFeedback({ ok: true, msg: "Saved" });
    } catch (err: any) {
      setVarianceAllowedFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to save" });
    } finally {
      setVarianceAllowedLoading(false);
    }
  };

  const handleSaveHelpUrls = async () => {
    setHelpUrlLoading(true);
    setHelpUrlFeedback(null);
    try {
      await api.put("/admin/settings/help-urls", {
        help_url_admin: helpUrlAdminDraft.trim(),
        help_url_user: helpUrlUserDraft.trim(),
      });
      setHelpUrlAdmin(helpUrlAdminDraft.trim());
      setHelpUrlUser(helpUrlUserDraft.trim());
      setHelpUrlFeedback({ ok: true, msg: "Saved" });
    } catch (err: any) {
      setHelpUrlFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to save" });
    } finally {
      setHelpUrlLoading(false);
    }
  };

  const handleToggleForecast = () => {
    const next = !forecastActive;
    setForecastActive(next); // optimistic — fire and forget, no revert
    api.put("/admin/forecast-active", { active: next }).catch(() => {});
  };

  const handleSelectDim = (dim: string) => {
    setSelectedDim(dim);
    api.put("/admin/active-dimension", { dimension: dim }).catch(() => {});

    // Build the toast message using already-loaded window dates
    const draft = dimDrafts[dim];
    let msg: string;
    if (dim === "Annual Budget") {
      msg = "Annual Budget selected — always open for input";
    } else if (draft?.open_from && draft?.open_to) {
      msg = `${dim} selected — open for input from ${fmtDate(draft.open_from)} to ${fmtDate(draft.open_to)}`;
    } else {
      msg = `${dim} selected`;
    }
    setSelectionMsg(msg);
    if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current);
    selectionTimerRef.current = setTimeout(() => setSelectionMsg(null), 5000);
  };

  const startEditing = (dim: string) => {
    setDimDrafts(prev => ({ ...prev, [dim]: { ...prev[dim], editing: true, feedback: null } }));
  };

  const cancelEditing = (dim: string) => {
    setDimDrafts(prev => ({
      ...prev,
      [dim]: {
        ...prev[dim],
        open_from: prev[dim].orig_open_from,
        open_to:   prev[dim].orig_open_to,
        editing: false,
        feedback: null,
      },
    }));
  };

  const updateDimField = (dim: string, field: "open_from" | "open_to", val: string) => {
    setDimDrafts(prev => ({
      ...prev,
      [dim]: { ...prev[dim], [field]: val, feedback: null },
    }));
  };

  const saveDimWindow = async (dim: string) => {
    const draft = dimDrafts[dim];
    setDimDrafts(prev => ({ ...prev, [dim]: { ...prev[dim], saving: true, feedback: null } }));
    try {
      await api.put(`/admin/dimension-windows/${encodeURIComponent(dim)}`, {
        open_from:    draft.open_from,
        open_to:      draft.open_to,
        fiscal_year:  currentFY,
      });
      setDimDrafts(prev => ({
        ...prev,
        [dim]: {
          ...prev[dim],
          saving: false,
          editing: false,
          orig_open_from: prev[dim].open_from,
          orig_open_to:   prev[dim].open_to,
          feedback: { ok: true, msg: "Saved" },
        },
      }));
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to save";
      setDimDrafts(prev => ({
        ...prev,
        [dim]: { ...prev[dim], saving: false, feedback: { ok: false, msg } },
      }));
    }
  };

  // ── Cost Centers state ───────────────────────────────────────────────────
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [ccDrafts, setCCDrafts] = useState<Record<string, CCDraft>>({});
  const [ccLoading, setCCLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [buFilter, setBuFilter] = useState("");

  // ── Temp password modal state ─────────────────────────────────────────────
  interface TempPwModal { cc: string; owner: string; email: string; password: string; }
  const [tempPwModal,   setTempPwModal]   = useState<TempPwModal | null>(null);
  const [tempPwSetting, setTempPwSetting] = useState(false);
  const [tempPwModalErr,setTempPwModalErr]= useState("");
  const [tempPwDoneMsg, setTempPwDoneMsg] = useState<string | null>(null);
  const tempPwDoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Add-form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addDraft, setAddDraft] = useState({ cost_center_number: "", cost_center_name: "", business_unit_id: null as number | null });
  const [addSaving, setAddSaving] = useState(false);
  const [addFeedback, setAddFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  // BU & User options for dropdowns
  const [buOptions, setBuOptions] = useState<BUOption[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);

  const loadCostCenters = useCallback(() => {
    setCCLoading(true);
    api.get("/admin/cost-centers")
      .then(res => {
        const ccs: CostCenter[] = res.data;
        setCostCenters(ccs);
        const drafts: Record<string, CCDraft> = {};
        ccs.forEach(cc => {
          drafts[cc.cost_center_number] = {
            cost_center_number: cc.cost_center_number,
            cost_center_name:   cc.cost_center_name,
            business_unit:      cc.business_unit,
            business_unit_id:   cc.business_unit_id ?? null,
            editing: false, saving: false, resetting: false, feedback: null,
          };
        });
        setCCDrafts(drafts);
        setCCLoading(false);
      })
      .catch(() => setCCLoading(false));
  }, []);

  const loadBUOptions = useCallback(() => {
    api.get("/business-units").then(res => setBuOptions(res.data)).catch(() => {});
  }, []);

  const loadUserOptions = useCallback(() => {
    api.get("/users").then(res => setUserOptions(res.data)).catch(() => {});
  }, []);

  useEffect(() => { loadCostCenters(); loadBUOptions(); loadUserOptions(); }, [loadCostCenters, loadBUOptions, loadUserOptions]);

  const startEditingCC = (origCC: string) => {
    setCCDrafts(prev => ({ ...prev, [origCC]: { ...prev[origCC], editing: true, feedback: null } }));
  };

  const cancelEditingCC = (origCC: string) => {
    const orig = costCenters.find(c => c.cost_center_number === origCC);
    setCCDrafts(prev => ({
      ...prev,
      [origCC]: {
        ...prev[origCC],
        cost_center_number: origCC,
        cost_center_name:   orig?.cost_center_name  ?? prev[origCC].cost_center_name,
        business_unit:      orig?.business_unit      ?? prev[origCC].business_unit,
        business_unit_id:   orig?.business_unit_id  ?? prev[origCC].business_unit_id,
        editing: false, feedback: null,
      },
    }));
  };

  const updateCCField = (origCC: string, field: keyof CCDraft, val: any) => {
    setCCDrafts(prev => ({
      ...prev,
      [origCC]: { ...prev[origCC], [field]: val, feedback: null },
    }));
  };

  const saveCC = async (origCC: string) => {
    const draft = ccDrafts[origCC];
    setCCDrafts(prev => ({ ...prev, [origCC]: { ...prev[origCC], saving: true, feedback: null } }));
    const buName = buOptions.find(b => b.id === draft.business_unit_id)?.name || draft.business_unit;
    try {
      await api.put(`/admin/cost-centers/${origCC}`, {
        cost_center_number: draft.cost_center_number,
        cost_center_name:   draft.cost_center_name,
        business_unit:      buName,
        business_unit_id:   draft.business_unit_id,
      });
      loadCostCenters();
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to save";
      setCCDrafts(prev => ({
        ...prev,
        [origCC]: { ...prev[origCC], saving: false, feedback: { ok: false, msg } },
      }));
    }
  };

  const toggleCCActive = async (cc: string, currentlyDeactivated: boolean) => {
    try {
      if (currentlyDeactivated) {
        await api.post(`/admin/cost-centers/${cc}/reactivate`);
      } else {
        await api.post(`/admin/cost-centers/${cc}/deactivate`, { fiscal_year: currentFY });
      }
      loadCostCenters();
    } catch {
      // silently fail — row won't change
    }
  };

  const deleteCC = (cc: string) => {
    setDeleteConfirm({ type: "cc", key: cc });
    setDeletePw("");
    setDeletePwErr("");
  };

  const deleteAcct = (account: string) => {
    setDeleteConfirm({ type: "account", key: account });
    setDeletePw("");
    setDeletePwErr("");
  };

  const confirmDeleteAction = async () => {
    if (!deleteConfirm || !deletePw) return;
    setDeletePwBusy(true);
    setDeletePwErr("");
    try {
      await api.post("/admin/verify-password", { password: deletePw });
    } catch {
      setDeletePwErr("Incorrect password. Please try again.");
      setDeletePwBusy(false);
      return;
    }
    try {
      if (deleteConfirm.type === "cc") {
        await api.delete(`/admin/cost-centers/${deleteConfirm.key}`);
        loadCostCenters();
      } else {
        await api.delete(`/admin/accounts/${encodeURIComponent(deleteConfirm.key)}`);
        setAccounts(prev => prev.filter(a => a.account !== deleteConfirm.key));
        setAcctDrafts(prev => { const n = { ...prev }; delete n[deleteConfirm.key]; return n; });
      }
      setDeleteConfirm(null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Delete failed";
      setDeletePwErr(msg);
    } finally {
      setDeletePwBusy(false);
    }
  };

  const generateTempPw = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  };

  const openTempPwModal = (cc: string) => {
    const ccObj = costCenters.find(c => c.cost_center_number === cc);
    if (!ccObj) return;
    setTempPwModal({ cc, owner: ccObj.cost_center_name, email: ccObj.email || "", password: generateTempPw() });
    setTempPwModalErr("");
  };

  const doSetTempPassword = async () => {
    if (!tempPwModal) return;
    setTempPwSetting(true);
    setTempPwModalErr("");
    try {
      await api.post(`/admin/cost-centers/${tempPwModal.cc}/set-temp-password`, { temp_password: tempPwModal.password });
      const emailBody =
        `Subject: Temp QFT Password\n\n` +
        `Hello ${tempPwModal.owner},\n\n` +
        `Your temporary QFT password is: ${tempPwModal.password}\n\n` +
        `You will be prompted to create a new password the next time you log in. ` +
        `Please keep this password secure.\n\n` +
        `Best regards,\nQFT Admin`;
      navigator.clipboard.writeText(emailBody).catch(() => {});
      loadCostCenters();
      setTempPwModal(null);
      const doneMsg = tempPwModal.email
        ? `Email message copied to clipboard — send to ${tempPwModal.email} with subject: Temp QFT Password`
        : `Email message copied to clipboard.`;
      setTempPwDoneMsg(doneMsg);
      if (tempPwDoneTimer.current) clearTimeout(tempPwDoneTimer.current);
      tempPwDoneTimer.current = setTimeout(() => setTempPwDoneMsg(null), 6000);
    } catch (err: any) {
      setTempPwModalErr(err?.response?.data?.error || "Failed to set password. Please try again.");
    } finally {
      setTempPwSetting(false);
    }
  };

  const addCostCenter = async () => {
    if (!addDraft.cost_center_number || !addDraft.cost_center_name) {
      setAddFeedback({ ok: false, msg: "CC# and Name are required" });
      return;
    }
    setAddSaving(true);
    setAddFeedback(null);
    const buName = buOptions.find(b => b.id === addDraft.business_unit_id)?.name || "";
    try {
      await api.post("/admin/cost-centers", { ...addDraft, business_unit: buName });
      setShowAddForm(false);
      setAddDraft({ cost_center_number: "", cost_center_name: "", business_unit_id: null });
      loadCostCenters();
    } catch (err: any) {
      setAddFeedback({ ok: false, msg: err?.response?.data?.error || "Failed to add" });
    } finally {
      setAddSaving(false);
    }
  };

  const allBUs = Array.from(new Set(costCenters.map(cc => cc.business_unit).filter(Boolean))).sort();

  const filteredCCs = costCenters.filter(cc => {
    const matchSearch = search === "" ||
      cc.cost_center_number.includes(search) ||
      cc.cost_center_name.toLowerCase().includes(search.toLowerCase());
    const matchBU = buFilter === "" || cc.business_unit === buFilter;
    return matchSearch && matchBU;
  });

  // ── Accounts state ───────────────────────────────────────────────────────
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [acctDrafts, setAcctDrafts] = useState<Record<string, AcctDraft>>({});
  const [acctLoading, setAcctLoading] = useState(false);
  const [acctSearch, setAcctSearch] = useState("");

  const loadAccounts = useCallback(() => {
    setAcctLoading(true);
    api.get("/admin/accounts")
      .then(res => {
        const accts: Account[] = res.data;
        setAccounts(accts);
        const drafts: Record<string, AcctDraft> = {};
        accts.forEach(a => {
          drafts[a.account] = { account: a.account, category: a.category, editing: false, saving: false, feedback: null };
        });
        setAcctDrafts(drafts);
        setAcctLoading(false);
      })
      .catch(() => setAcctLoading(false));
  }, []);

  // ── Unmapped accounts state ─────────────────────────────────────────────
  const [unmappedAccts, setUnmappedAccts] = useState<UnmappedAccount[]>([]);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [unmappedOpen, setUnmappedOpen] = useState(false);
  const [unmappedSelected, setUnmappedSelected] = useState<Set<number>>(new Set());
  const [unmappedAssigning, setUnmappedAssigning] = useState(false);

  const loadUnmapped = useCallback(() => {
    setUnmappedLoading(true);
    api.get("/admin/accounts/unmapped")
      .then(res => { setUnmappedAccts(res.data); setUnmappedLoading(false); })
      .catch(() => setUnmappedLoading(false));
  }, []);

  const toggleUnmappedSelect = (id: number) => {
    setUnmappedSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllUnmapped = () => {
    if (unmappedSelected.size === unmappedAccts.length) {
      setUnmappedSelected(new Set());
    } else {
      setUnmappedSelected(new Set(unmappedAccts.map(a => a.id)));
    }
  };

  const assignUnmappedToFY = async (fy: string) => {
    if (unmappedSelected.size === 0) return;
    setUnmappedAssigning(true);
    try {
      await api.post("/admin/accounts/assign-fy", { account_ids: Array.from(unmappedSelected), fiscal_year: fy });
      setUnmappedSelected(new Set());
      loadUnmapped();
      loadAccounts();
    } catch { /* ignore */ }
    setUnmappedAssigning(false);
  };

  const deleteUnmappedAccounts = async () => {
    if (unmappedSelected.size === 0) return;
    const count = unmappedSelected.size;
    if (!window.confirm(`Delete ${count} unmapped account${count > 1 ? "s" : ""} and all their budget/actuals data?`)) return;
    setUnmappedAssigning(true);
    let deleted = 0;
    for (const id of Array.from(unmappedSelected)) {
      const acct = unmappedAccts.find(a => a.id === id);
      if (!acct) continue;
      try {
        await api.delete(`/admin/accounts/${encodeURIComponent(acct.account)}`);
        deleted++;
      } catch { /* ignore */ }
    }
    setUnmappedSelected(new Set());
    loadUnmapped();
    loadAccounts();
    setUnmappedAssigning(false);
  };

  useEffect(() => {
    if (activeTab === "accounts" && accounts.length === 0) loadAccounts();
  }, [activeTab, accounts.length, loadAccounts]);

  useEffect(() => {
    if (activeTab === "accounts") {
      api.get("/settings/forecast-config")
        .then(res => { setSalaryAutoCompute(res.data.salary_auto_compute !== false); })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "accounts" && unmappedOpen && unmappedAccts.length === 0) loadUnmapped();
  }, [activeTab, unmappedOpen, unmappedAccts.length, loadUnmapped]);

  useEffect(() => {
    if (activeTab === "auditlog") loadAuditLog();
  }, [activeTab, loadAuditLog]);

  const startEditingAcct = (key: string) => {
    setAcctDrafts(prev => ({ ...prev, [key]: { ...prev[key], editing: true, feedback: null } }));
  };

  const cancelEditingAcct = (key: string) => {
    const orig = accounts.find(a => a.account === key);
    setAcctDrafts(prev => ({
      ...prev,
      [key]: { ...prev[key], account: orig?.account ?? prev[key].account, category: orig?.category ?? prev[key].category, editing: false, feedback: null },
    }));
  };

  const updateAcctField = (key: string, field: "account" | "category", val: string) => {
    setAcctDrafts(prev => ({ ...prev, [key]: { ...prev[key], [field]: val, feedback: null } }));
  };

  const saveAcct = async (origAccount: string) => {
    const draft = acctDrafts[origAccount];
    setAcctDrafts(prev => ({ ...prev, [origAccount]: { ...prev[origAccount], saving: true, feedback: null } }));
    try {
      await api.put("/admin/accounts/bulk", { old_account: origAccount, account: draft.account, category: draft.category });
      // Re-key drafts if name changed
      setAccounts(prev => prev.map(a => a.account === origAccount ? { ...a, account: draft.account, category: draft.category } : a));
      setAcctDrafts(prev => {
        const next = { ...prev };
        delete next[origAccount];
        const ccCount = accounts.find(a => a.account === origAccount)?.cc_count ?? 0;
        next[draft.account] = { ...draft, saving: false, editing: false, feedback: { ok: true, msg: `Saved — updated ${ccCount} CCs` } };
        return next;
      });
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to save";
      setAcctDrafts(prev => ({ ...prev, [origAccount]: { ...prev[origAccount], saving: false, feedback: { ok: false, msg } } }));
    }
  };

  const allCategories = Array.from(new Set(accounts.map(a => a.category))).sort();

  const ACCT_CATEGORY_ORDER = ["COMPENSATION","OCCUPANCY","OPERATING","ADVERTISING","ALLOCATED","AMORT","CORP_OH","OTHERADJUSTMENTS"];
  const filteredAccts = accounts.filter(a =>
    !acctSearch ||
    a.account.toLowerCase().includes(acctSearch.toLowerCase()) ||
    a.category.toLowerCase().includes(acctSearch.toLowerCase())
  );
  // Group filtered accounts by category in canonical order
  const acctsByCategory: { category: string; accounts: Account[] }[] = (() => {
    const groups: Record<string, Account[]> = {};
    filteredAccts.forEach(a => { (groups[a.category] = groups[a.category] || []).push(a); });
    const seen = new Set<string>();
    const ordered: { category: string; accounts: Account[] }[] = [];
    ACCT_CATEGORY_ORDER.forEach(cat => {
      const key = Object.keys(groups).find(k => k.toUpperCase() === cat);
      if (key) { ordered.push({ category: key, accounts: groups[key] }); seen.add(key); }
    });
    Object.keys(groups).filter(k => !seen.has(k)).forEach(k => ordered.push({ category: k, accounts: groups[k] }));
    return ordered;
  })();

  // ── Shared CCs-by-BU (loaded once, used by Dashboard + Forecast CC filters) ──
  const [ccsByBU, setCCsByBU] = useState<Record<string, { cost_center_number: string; cost_center_name: string }[]>>({});

  useEffect(() => {
    if (!currentFY) return;
    api.get("/admin/cost-centers-by-bu", { params: { fy: currentFY } })
      .then(res => setCCsByBU(res.data.by_bu || {}))
      .catch(() => {});
  }, [currentFY]);

  // ── Variance tab state ───────────────────────────────────────────────────
  interface VarRow { cost_center_number: string; cost_center_name: string; business_unit: string; dims: Record<string, number>; }
  const [varRows,        setVarRows]        = useState<VarRow[]>([]);
  const [varLoading,     setVarLoading]     = useState(false);
  const [varDrafts,      setVarDrafts]      = useState<Record<string, string>>({}); // key: "cc|dim"
  const [varSaving,      setVarSaving]      = useState<Record<string, boolean>>({}); // key: "cc|dim"
  const [varFeedback,    setVarFeedback]    = useState<Record<string, boolean>>({}); // key: "cc|dim"
  const [varEditingRows, setVarEditingRows] = useState<Record<string, boolean>>({}); // key: cc number
  const [varBU,          setVarBU]          = useState("");

  const loadVarRows = (bu = varBU) => {
    setVarLoading(true);
    const params = bu ? `?bu=${encodeURIComponent(bu)}` : "";
    api.get(`/admin/forecast-variance${params}`)
      .then(res => {
        const rows: VarRow[] = res.data;
        setVarRows(rows);
        const drafts: Record<string, string> = {};
        rows.forEach(r => {
          ["July Forecast", "October Forecast", "January Forecast"].forEach(dim => {
            drafts[`${r.cost_center_number}|${dim}`] = String(r.dims[dim] ?? 0);
          });
        });
        setVarDrafts(drafts);
        setVarFeedback({});
        setVarEditingRows({});
      })
      .catch(() => {})
      .finally(() => setVarLoading(false));
  };

  useEffect(() => {
    if (activeTab === "salary")  loadSalaryCfg();
    if (activeTab === "finance") { loadFinanceTargets(); loadVarRows(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Salary Config state ───────────────────────────────────────────────────
  const [salaryCfgFY,       setSalaryCfgFY]       = useState<string>(autoFY());
  const [salaryRaiseBefore, setSalaryRaiseBefore] = useState<string>("0");
  const [salaryRaiseAfter,  setSalaryRaiseAfter]  = useState<string>("3.5");
  const [salaryBipRaise,    setSalaryBipRaise]    = useState<string>("0");
  const [salaryCfgLoading,  setSalaryCfgLoading]  = useState(false);
  const [salaryCfgFeedback, setSalaryCfgFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [payrollTax, setPayrollTax] = useState<Record<string, string>>({
    apr:"8.50", may:"8.50", jun:"13.37", jul:"7.60", aug:"7.60", sep:"7.60",
    oct:"7.60", nov:"7.60", dec:"7.60", jan:"9.04", feb:"9.04", mar:"7.50",
  });
  const [insurancePerHead, setInsurancePerHead] = useState<string>("1003.33");
  const [retirementRate,   setRetirementRate]   = useState<string>("6.37");
  const [salaryAutoCompute, setSalaryAutoCompute] = useState(true);
  const [salaryAutoSaving, setSalaryAutoSaving] = useState(false);
  const PAYROLL_TAX_MONTHS = [
    { key:"apr", label:"Apr" }, { key:"may", label:"May" }, { key:"jun", label:"Jun" },
    { key:"jul", label:"Jul" }, { key:"aug", label:"Aug" }, { key:"sep", label:"Sep" },
    { key:"oct", label:"Oct" }, { key:"nov", label:"Nov" }, { key:"dec", label:"Dec" },
    { key:"jan", label:"Jan" }, { key:"feb", label:"Feb" }, { key:"mar", label:"Mar" },
  ];

  // ── Finance View state ────────────────────────────────────────────────────
  interface FinanceRow {
    cost_center_number:     string;
    cost_center_name:       string;
    cost_center_owner?:     string;
    business_unit:          string;
    dimension:              string;   // current forecast dim name
    annual_budget_dim:      string;   // e.g. "Annual Budget FY26"
    ref_dimension:          string;   // e.g. "October Forecast"
    dimension_total:        number;
    annual_budget_total:    number;
    ref_forecast_total:     number;
    july_av:                number;   // from forecast_variance
    october_av:             number;
    january_av:             number;   // editable — saves to forecast_variance
    adjusted_annual_budget: number;   // AB + July AV + Oct AV (read-only)
    submission_status:      string | null; // draft | submitted | null
    met:                    boolean;
  }
  const [financeRows,      setFinanceRows]      = useState<FinanceRow[]>([]);
  const [financeLoading,   setFinanceLoading]   = useState(false);
  const [editingJanAV, setEditingJanAV] = useState<Record<string, string>>({});
  const [savingTarget, setSavingTarget] = useState<string | null>(null);
  const [downloadingCC,  setDownloadingCC]  = useState<string | null>(null);
  const [financeFilterBU, setFinanceFilterBU] = useState("");
  const [fvSortCol, setFvSortCol] = useState<"cc" | "annual" | "ab" | "forecast" | "variance" | "status">("cc");
  const [fvSortDir, setFvSortDir] = useState<"asc" | "desc">("asc");
  const [fvChartCC, setFvChartCC] = useState<string | null>(null);
  const [fvChartData, setFvChartData] = useState<any[] | null>(null);
  const [fvChartLoading, setFvChartLoading] = useState(false);
  const [fvChartType, setFvChartType] = useState<"bar" | "line">("bar");
  const [ccEditOverrides, setCCEditOverrides] = useState<Record<string, boolean>>({});
  const [fvComments, setFvComments] = useState<Record<string, string>>({});
  const [fvCommentDraft, setFvCommentDraft] = useState<Record<string, string>>({});
  const [fvCommentOpen, setFvCommentOpen] = useState<string | null>(null);
  const [fvCommentSaving, setFvCommentSaving] = useState<Record<string, boolean>>({});
  const [fvAdminNotes, setFvAdminNotes] = useState<Record<string, string>>({});
  const [fvAdminNoteDim, setFvAdminNoteDim] = useState("");
  const [fvAdminNoteDraft, setFvAdminNoteDraft] = useState("");
  const [fvAdminNoteOpen, setFvAdminNoteOpen] = useState(false);
  const [fvAdminNoteSaving, setFvAdminNoteSaving] = useState(false);
  const [fvAdminNoteSaved, setFvAdminNoteSaved] = useState(false);
  const [fvCCNoteCC, setFvCCNoteCC] = useState<string | null>(null);
  const [fvCCNoteDim, setFvCCNoteDim] = useState("");
  const [fvCCNoteDraft, setFvCCNoteDraft] = useState("");
  const [fvCCNoteSaving, setFvCCNoteSaving] = useState(false);
  const [fvCCNoteSaved, setFvCCNoteSaved] = useState(false);
  const [fvJournalOpen, setFvJournalOpen] = useState(false);
  const [fvJournalData, setFvJournalData] = useState<Record<string, Record<string, string>> | null>(null);
  const [fvJournalLoading, setFvJournalLoading] = useState(false);
  const [fvJournalCopied, setFvJournalCopied] = useState(false);
  const toggleFvSort = (col: typeof fvSortCol) => {
    if (fvSortCol === col) setFvSortDir(d => d === "asc" ? "desc" : "asc");
    else { setFvSortCol(col); setFvSortDir("asc"); }
  };

  // Active variance dimension: use the forecast_config dimension if loaded, else date-based fallback
  const varActiveDim = financeRows[0]?.dimension || getDefaultDim();

  // ── Export modal state ─────────────────────────────────────────────────────
  const [exportModalCC, setExportModalCC] = useState<string | null>(null);
  const [exportFile, setExportFile] = useState<File | null>(null);
  const [exportPassword, setExportPassword] = useState("");
  const [exportNeedsPassword, setExportNeedsPassword] = useState(false);
  const [exportDimension, setExportDimension] = useState<string>("Annual Budget");
  const [exportValidating, setExportValidating] = useState(false);
  const [exportValidation, setExportValidation] = useState<{
    valid: boolean; ccMatch: boolean; fileCC: string | null;
    dimension: string; filename: string; fy: string;
    sheets: { salary: boolean; bip: boolean; plan: boolean; planSheetName: string | null; planSheets?: string[] };
    writeTargets?: { sheet: string; description: string }[];
    planMonthStartCol?: number;
    allSheets: string[]; counts: { employees: number; budgetAccounts: number };
  } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportPlanSheet, setExportPlanSheet] = useState<string>("");

  // ── Delete confirmation (password-protected) ─────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "cc" | "account"; key: string } | null>(null);
  const [deletePw,      setDeletePw]      = useState("");
  const [deletePwErr,   setDeletePwErr]   = useState("");
  const [deletePwBusy,  setDeletePwBusy]  = useState(false);

  const loadFinanceTargets = () => {
    setFinanceLoading(true);
    api.get("/admin/finance-targets")
      .then(r => setFinanceRows(r.data))
      .catch(() => {})
      .finally(() => setFinanceLoading(false));
  };

  const activeDimForAV = financeRows[0]?.dimension || "January Forecast";
  const avKeyForDim = (dim: string) =>
    dim.startsWith("July") ? "july_av" : dim.startsWith("October") ? "october_av" : "january_av";
  const activeAVKey = avKeyForDim(activeDimForAV);

  useEffect(() => {
    if (activeTab !== "finance" || !activeDimForAV) return;
    api.get("/admin/cc-edit-overrides", { params: { dimension: activeDimForAV } })
      .then(r => setCCEditOverrides(r.data.overrides || {}))
      .catch(() => {});
    api.get("/admin/forecast-comments", { params: { dimension: activeDimForAV } })
      .then(r => { setFvComments(r.data.comments || {}); setFvCommentDraft(r.data.comments || {}); })
      .catch(() => {});
    const noteDims = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];
    Promise.all(noteDims.map(d => api.get("/admin/forecast-admin-note", { params: { dimension: d } }).then(r => [d, r.data.note || ""] as [string, string]).catch(() => [d, ""] as [string, string])))
      .then(pairs => { const m: Record<string, string> = {}; pairs.forEach(([d, n]) => { m[d] = n; }); setFvAdminNotes(m); });
  }, [activeTab, activeDimForAV]);

  const toggleCCEditOverride = (cc: string) => {
    const next = !ccEditOverrides[cc];
    setCCEditOverrides(prev => ({ ...prev, [cc]: next }));
    api.put("/admin/cc-edit-override", { dimension: activeDimForAV, cost_center: cc, enabled: next }).catch(() => {});
  };

  const saveFvComment = (cc: string) => {
    setFvCommentSaving(prev => ({ ...prev, [cc]: true }));
    api.put("/admin/forecast-comment", { dimension: activeDimForAV, cost_center: cc, comment: fvCommentDraft[cc] || "" })
      .then(() => { setFvComments(prev => ({ ...prev, [cc]: fvCommentDraft[cc] || "" })); setFvCommentOpen(null); })
      .catch(() => {})
      .finally(() => setFvCommentSaving(prev => ({ ...prev, [cc]: false })));
  };

  const saveFvAdminNote = () => {
    const dim = fvAdminNoteDim || activeDimForAV;
    setFvAdminNoteSaving(true);
    api.put("/admin/forecast-admin-note", { dimension: dim, note: fvAdminNoteDraft })
      .then(() => {
        setFvAdminNotes(prev => ({ ...prev, [dim]: fvAdminNoteDraft }));
        setFvAdminNoteSaved(true);
        setTimeout(() => setFvAdminNoteSaved(false), 2500);
      })
      .catch(() => {})
      .finally(() => setFvAdminNoteSaving(false));
  };

  const openJournal = () => {
    setFvJournalOpen(true);
    setFvJournalLoading(true);
    const dims = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];
    Promise.all([
      ...dims.map(d =>
        api.get("/admin/forecast-comments", { params: { dimension: d } })
          .then(r => ({ type: "cc" as const, dim: d, data: r.data.comments || {} }))
          .catch(() => ({ type: "cc" as const, dim: d, data: {} as Record<string, string> }))
      ),
      ...dims.map(d =>
        api.get("/admin/forecast-admin-note", { params: { dimension: d } })
          .then(r => ({ type: "admin" as const, dim: d, data: r.data.note || "" }))
          .catch(() => ({ type: "admin" as const, dim: d, data: "" }))
      ),
    ]).then(results => {
      const ccMap: Record<string, Record<string, string>> = {};
      const adminMap: Record<string, string> = {};
      results.forEach(r => {
        if (r.type === "cc") ccMap[r.dim] = r.data as Record<string, string>;
        else adminMap[r.dim] = r.data as string;
      });
      setFvJournalData(ccMap);
      setFvAdminNotes(prev => ({ ...prev, ...adminMap }));
    }).finally(() => setFvJournalLoading(false));
  };

  const saveJanAV = (cc: string) => {
    const raw = editingJanAV[cc];
    if (raw === undefined) return;
    const newAV = parseFloat(raw);
    if (isNaN(newAV)) { setEditingJanAV(e => { const n = { ...e }; delete n[cc]; return n; }); return; }
    setSavingTarget(cc);
    api.put("/admin/forecast-variance", { cost_center_number: cc, dimension: activeDimForAV, variance_allowed: newAV })
      .then(() => {
        setFinanceRows(rows => rows.map(r =>
          r.cost_center_number === cc
            ? { ...r, [activeAVKey]: newAV }
            : r
        ));
        setEditingJanAV(e => { const n = { ...e }; delete n[cc]; return n; });
      })
      .finally(() => setSavingTarget(null));
  };

  const handleFvChart = (cc: string) => {
    if (fvChartCC === cc) { setFvChartCC(null); return; }
    setFvChartCC(cc);
    setFvChartData(null);
    setFvChartLoading(true);
    const params = new URLSearchParams();
    params.set("cc", cc);
    if (currentFY) params.set("fiscalYear", currentFY.toUpperCase());
    api.get(`/admin/bu-dashboard/monthly-trend?${params}`)
      .then(res => setFvChartData(res.data.months || []))
      .catch(() => setFvChartData(null))
      .finally(() => setFvChartLoading(false));
  };

  const handleFinanceDownload = (cc: string) => {
    setExportModalCC(cc);
    setExportFile(null);
    setExportPassword("");
    setExportNeedsPassword(false);
    setExportDimension("Annual Budget");
    setExportValidation(null);
    setExportError(null);
    setExporting(false);
  };

  const handleExportValidate = async (file: File, pw?: string) => {
    if (!exportModalCC) return;
    setExportFile(file);
    setExportValidating(true);
    setExportValidation(null);
    setExportError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("cost_center", exportModalCC);
      if (pw || exportPassword) form.append("password", pw || exportPassword);
      const res = await api.post("/export/validate-template", form);
      setExportValidation(res.data);
      setExportPlanSheet(res.data.sheets?.planSheetName || "");
      setExportNeedsPassword(false);
    } catch (err: any) {
      console.error("[Export validate]", err?.response?.status, err?.response?.data, err?.message);
      const respData = err?.response?.data;
      const status = err?.response?.status;
      if (respData?.needsPassword || status === 422) {
        setExportNeedsPassword(true);
        setExportError(null);
      } else if (!err?.response) {
        // Network error - backend not reachable
        setExportError("Cannot reach server. Please check the backend is running.");
      } else {
        setExportError(respData?.error || `Validation failed (${status})`);
      }
    } finally {
      setExportValidating(false);
    }
  };

  const handleExportPasswordSubmit = () => {
    if (!exportFile || !exportPassword) return;
    handleExportValidate(exportFile, exportPassword);
  };

  const handleExportDownload = async () => {
    if (!exportModalCC || !exportFile) return;
    setExporting(true);
    setExportError(null);
    try {
      const form = new FormData();
      form.append("file", exportFile);
      form.append("cost_center", exportModalCC);
      if (exportPassword) form.append("password", exportPassword);
      form.append("dimension", exportDimension);
      if (exportPlanSheet) form.append("planSheetOverride", exportPlanSheet);
      const res = await api.post("/export/populate-template", form, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${exportModalCC}_export.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setExportModalCC(null);
    } catch (err: any) {
      setExportError(err?.response?.data?.error || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const loadSalaryCfg = (fy?: string) => {
    const fyParam = fy || salaryCfgFY;
    setSalaryCfgLoading(true);
    setSalaryCfgFeedback(null);
    Promise.all([
      api.get("/admin/salary-config", { params: { fy: fyParam } }),
      api.get("/settings/forecast-config"),
    ])
      .then(([r, fc]) => {
        setSalaryRaiseBefore(((r.data.raise_before_m3 ?? 0) * 100).toFixed(2));
        setSalaryRaiseAfter(((r.data.raise_after_m3  ?? 0.035) * 100).toFixed(2));
        setSalaryBipRaise(((r.data.bip_raise         ?? 0) * 100).toFixed(2));
        setInsurancePerHead(((r.data.insurance_per_head ?? 1003.33)).toFixed(2));
        setRetirementRate(((r.data.retirement_rate ?? 0.0637) * 100).toFixed(4));
        if (r.data.payroll_tax) {
          const pt: Record<string, string> = {};
          Object.entries(r.data.payroll_tax).forEach(([k, v]) => {
            pt[k] = ((v as number) * 100).toFixed(2);
          });
          setPayrollTax(pt);
        }
        setSalaryAutoCompute(fc.data.salary_auto_compute !== false);
      })
      .catch(() => {})
      .finally(() => setSalaryCfgLoading(false));
  };

  const saveSalaryCfg = () => {
    setSalaryCfgFeedback(null);
    const pt: Record<string, number> = {};
    Object.entries(payrollTax).forEach(([k, v]) => { pt[k] = parseFloat(v) / 100 || 0; });
    api.put("/admin/salary-config", {
      fy: salaryCfgFY,
      raise_before_m3:  parseFloat(salaryRaiseBefore) / 100 || 0,
      raise_after_m3:   parseFloat(salaryRaiseAfter)  / 100 || 0,
      bip_raise:        parseFloat(salaryBipRaise)    / 100 || 0,
      insurance_per_head: parseFloat(insurancePerHead) || 1003.33,
      retirement_rate:    parseFloat(retirementRate)   / 100 || 0.0637,
      payroll_tax: pt,
    })
      .then(() => setSalaryCfgFeedback({ ok: true, msg: `Saved for ${salaryCfgFY}.` }))
      .catch(() => setSalaryCfgFeedback({ ok: false, msg: "Save failed." }));
  };

  // ── Upload inventory state ────────────────────────────────────────────────
  interface ActualsInvRow { month: string; cc_count: number; last_upload: string; fiscal_year: string; }
  interface BudgetInvCC   { cost_center_number: string; cost_center_name: string; total_budget: number; }
  interface BudgetInvBU   { business_unit: string; ccs: BudgetInvCC[]; total: number; }
  interface BudgetInvDim  { dimension: string; business_units: BudgetInvBU[]; }

  const [actualsInv,    setActualsInv]    = useState<ActualsInvRow[]>([]);
  const [actualsInvFY,  setActualsInvFY]  = useState<string>("");
  const [actualsInvBU,  setActualsInvBU]  = useState<string>("");
  const [budgetInv,     setBudgetInv]     = useState<BudgetInvDim[]>([]);
  const [uploadInvFY,   setUploadInvFY]   = useState<string>("");
  const [collapsedBUs,  setCollapsedBUs]  = useState<Set<string>>(new Set());
  const [uploadInvLoad, setUploadInvLoad] = useState(false);
  const [uploadSubTab,  setUploadSubTab]  = useState<"actuals" | "budget">("actuals");
  const [budgetInvFilter, setBudgetInvFilter] = useState("");

  const FISCAL_MONTHS = ["April","May","June","July","August","September","October","November","December","January","February","March"];

  const getFiscalQuarter = (month: string): string => {
    const q1 = ["April", "May", "June"];
    const q2 = ["July", "August", "September"];
    const q3 = ["October", "November", "December"];
    const q4 = ["January", "February", "March"];
    if (q1.includes(month)) return "Q1";
    if (q2.includes(month)) return "Q2";
    if (q3.includes(month)) return "Q3";
    if (q4.includes(month)) return "Q4";
    return "YTD";
  };

  const refreshInventory = useCallback((bu?: string) => {
    const buParam = bu !== undefined ? bu : actualsInvBU;
    setUploadInvLoad(true);
    const buQuery = buParam ? `?bu=${encodeURIComponent(buParam)}` : "";
    Promise.all([
      api.get(`/admin/actuals-inventory${buQuery}`).then(r => {
        setActualsInv(r.data);
        const fys = Array.from(new Set<string>((r.data as ActualsInvRow[]).map(x => x.fiscal_year))).sort().reverse();
        if (fys.length > 0) setActualsInvFY(prev => prev || fys[0]);
      }).catch(() => {}),
      api.get("/admin/budget-inventory").then(r => {
        setBudgetInv(r.data);
        const fyDims = r.data.filter((d: BudgetInvDim) => /FY\d+/i.test(d.dimension));
        if (fyDims.length > 0) setUploadInvFY(prev => prev || fyDims[0].dimension);
      }).catch(() => {}),
    ]).finally(() => setUploadInvLoad(false));
  }, [actualsInvBU]);

  useEffect(() => {
    if (activeTab !== "upload") return;
    refreshInventory();
  }, [activeTab, refreshInventory]);

  const handleVarSaveRow = async (cc: string) => {
    const key = `${cc}|${varActiveDim}`;
    const val = parseFloat(varDrafts[key] ?? "0");
    const saved = varRows.find(r => r.cost_center_number === cc)?.dims[varActiveDim] ?? 0;
    const saves = (!isNaN(val) && val !== saved)
      ? [{ dim: varActiveDim, val }]
      : [];

    if (saves.length === 0) {
      setVarEditingRows(r => { const n = { ...r }; delete n[cc]; return n; });
      return;
    }
    saves.forEach(s => setVarSaving(sv => ({ ...sv, [`${cc}|${s.dim}`]: true })));
    try {
      await Promise.all(saves.map(s =>
        api.put("/admin/forecast-variance", { cost_center_number: cc, dimension: s.dim, variance_allowed: s.val })
      ));
      setVarRows(rows => rows.map(r =>
        r.cost_center_number === cc
          ? { ...r, dims: { ...r.dims, ...Object.fromEntries(saves.map(s => [s.dim, s.val])) } }
          : r
      ));
      saves.forEach(s => setVarFeedback(f => ({ ...f, [`${cc}|${s.dim}`]: true })));
      setTimeout(() => saves.forEach(s =>
        setVarFeedback(f => { const n = { ...f }; delete n[`${cc}|${s.dim}`]; return n; })
      ), 2000);
      setVarEditingRows(r => { const n = { ...r }; delete n[cc]; return n; });
    } catch { /* keep editing */ }
    finally { saves.forEach(s => setVarSaving(sv => { const n = { ...sv }; delete n[`${cc}|${s.dim}`]; return n; })); }
  };

  // ── BU Dashboard state ──────────────────────────────────────────────────
  const [dashMonth, setDashMonth] = useState<string>("");
  const [dashAvailableMonths, setDashAvailableMonths] = useState<string[]>([]);
  const [dashBU, setDashBU] = useState<string>("");
  const [dashSelectedCCs, setDashSelectedCCs] = useState<string[]>([]);
  const [dashBusinessUnits, setDashBusinessUnits] = useState<string[]>([]);
  const [dashBUSummary, setDashBUSummary] = useState<BUSummaryRow[]>([]);
  const [dashData, setDashData] = useState<CategoryKPI[]>([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashMonthsLoading, setDashMonthsLoading] = useState(false);
  const [drillCC, setDrillCC] = useState<string | null>(null);
  const [drillCategory, setDrillCategory] = useState<string | null>(null);
  const [drillData, setDrillData] = useState<AcctRow[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillTitle, setDrillTitle] = useState("");
  const [buChartVisible, setBuChartVisible] = useState(false);
  const [buChartData, setBuChartData] = useState<MonthlyTrendPoint[] | null>(null);
  const [buChartLoading, setBuChartLoading] = useState(false);
  const [buChartError, setBuChartError] = useState<string | null>(null);
  const [buChartHoverIdx, setBuChartHoverIdx] = useState<number | null>(null);
  const [dashMonthlyTrend, setDashMonthlyTrend] = useState<MonthlyTrendPoint[] | null>(null);
  const [dashAnnualBudgetTotal, setDashAnnualBudgetTotal] = useState<number>(0);
  const [dashCCSummary, setDashCCSummary] = useState<{cost_center: string; cost_center_name: string; business_unit: string; month_budget: number; month_actual: number; ytd_budget: number; ytd_actual: number; month_variance: number; ytd_variance: number; categories: CCCatBreakdown[]}[]>([]);
  const [dashChartFilterCC, setDashChartFilterCC] = useState<string | null>(null);
  const [expandedCCs, setExpandedCCs] = useState<Set<string>>(new Set());

  const doLoadDashboard = useCallback((month: string, bu: string, selectedCCs: string[] = [], ytd = false) => {
    if (!month) return;
    setDashLoading(true);
    setDashData([]);
    setDashBUSummary([]);
    setDrillData(null);
    setDrillCC(null);
    setDrillCategory(null);
    setExpandedCCs(new Set());
    const params = new URLSearchParams({ month });
    if (bu) params.set("bu", bu);
    if (selectedCCs.length > 0) params.set("cc", selectedCCs.join(","));
    if (currentFY) params.set("fiscalYear", currentFY);
    if (ytd) params.set("ytd", "1");
    api.get(`/admin/bu-dashboard?${params}`)
      .then(res => {
        setDashData(res.data.categories || []);
        setDashBusinessUnits(res.data.business_units || []);
        setDashBUSummary(res.data.bu_summary || []);
        setDashCCSummary(res.data.cc_summary || []);
        setDashLoading(false);
      })
      .catch(() => setDashLoading(false));
    // Fetch monthly trend in parallel for projection KPIs (no month/ytd filter needed)
    const trendParams = new URLSearchParams();
    if (bu) trendParams.set("bu", bu);
    if (selectedCCs.length > 0) trendParams.set("cc", selectedCCs.join(","));
    if (currentFY) trendParams.set("fiscalYear", currentFY);
    api.get(`/admin/bu-dashboard/monthly-trend?${trendParams}`)
      .then(res => {
        setDashMonthlyTrend(res.data.months || []);
        setDashAnnualBudgetTotal(res.data.annual_budget_total || 0);
      })
      .catch(() => { setDashMonthlyTrend(null); setDashAnnualBudgetTotal(0); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFY]);

  const doLoadAvailableMonths = useCallback((bu: string) => {
    setDashMonthsLoading(true);
    const params = new URLSearchParams();
    if (bu) params.set("bu", bu);
    if (currentFY) params.set("fy", currentFY);
    api.get(`/admin/bu-dashboard/available-months?${params}`)
      .then(res => {
        const months: string[] = res.data.months || [];
        setDashAvailableMonths(months);
        // pick latest month as default
        const latest = months[months.length - 1] || "";
        setDashMonth(latest);
        setDashMonthsLoading(false);
        if (latest) doLoadDashboard(latest, bu);
      })
      .catch(() => setDashMonthsLoading(false));
  }, [doLoadDashboard, currentFY]);

  useEffect(() => {
    if (activeTab === "dashboard" && fyLoaded && dashAvailableMonths.length === 0 && !dashMonthsLoading) {
      doLoadAvailableMonths(dashBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fyLoaded]);

  // ── Projected Year-End: YTD Actuals + remaining months' budget ──────────────
  // Uses actual spend for uploaded months, then fills remaining months with
  // their budget values. As actuals replace budget month by month, the
  // projection becomes increasingly grounded in real data.
  const dashProjection = useMemo(() => {
    if (!dashMonthlyTrend || dashMonthlyTrend.length === 0) return null;
    const forecastTotal = dashMonthlyTrend.reduce((s, p) => s + (p.budget || 0), 0);
    const actMonths = dashMonthlyTrend.filter(p => p.hasActuals);
    const ytdActualTotal = actMonths.reduce((s, p) => s + (p.actual || 0), 0);
    const n = actMonths.length;
    const remaining = dashMonthlyTrend.filter(p => !p.hasActuals);
    const remainingBudgetTotal = remaining.reduce((s, p) => s + (p.budget || 0), 0);

    // Projected Year-End = YTD Actuals + reference budget for remaining months
    const projectedYearEnd = n === 0 ? forecastTotal : ytdActualTotal + remainingBudgetTotal;

    return {
      forecastTotal,
      ytdActualTotal,
      annualBudgetTotal: dashAnnualBudgetTotal,
      projectedYearEnd: Math.round(projectedYearEnd),
      projectionVariance: Math.round(projectedYearEnd - forecastTotal),
      budgetVariance: Math.round(projectedYearEnd - forecastTotal),
      actualMonths: n,
      remainingMonths: remaining.length,
    };
  }, [dashMonthlyTrend, dashAnnualBudgetTotal]);

  const loadBuChart = useCallback((bu: string, ccs: string[], category: string | null, filterCC: string | null = null) => {
    setBuChartLoading(true);
    setBuChartError(null);
    const params = new URLSearchParams();
    if (currentFY) params.set("fiscalYear", currentFY);
    if (bu) params.set("bu", bu);
    // If a CC is selected from the offenders chart, use that; otherwise use the pill filter
    const effectiveCCs = filterCC ? [filterCC] : ccs;
    if (effectiveCCs.length > 0) params.set("cc", effectiveCCs.join(","));
    if (category) params.set("category", category);
    api.get(`/admin/bu-dashboard/monthly-trend?${params}`)
      .then(res => { setBuChartData(res.data.months || []); setBuChartLoading(false); })
      .catch((err) => { setBuChartError(err?.response?.data?.error || "Failed to load chart data"); setBuChartLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFY]);

  useEffect(() => {
    if (buChartVisible) loadBuChart(dashBU, dashSelectedCCs, drillCategory, dashChartFilterCC);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buChartVisible, dashBU, dashSelectedCCs, drillCategory, currentFY, dashChartFilterCC]);

  const doLoadDrilldown = (category: string, cc?: string) => {
    // Toggle off: clicking the same category+CC when it's already active
    if (drillCategory === category && ((!cc && !drillCC) || (cc && drillCC === cc))) {
      setDrillData(null);
      setDrillCC(null);
      setDrillCategory(null);
      return;
    }
    const catData = dashData.find(c => c.category === category);
    const ccRow = catData?.top_over.find(r => r.cost_center === cc)
               || catData?.top_under.find(r => r.cost_center === cc);
    const title = cc
      ? `${category} — CC ${cc}${ccRow?.cost_center_name ? ` (${ccRow.cost_center_name})` : ""}`
      : `${category} — All Cost Centers`;
    setDrillCategory(category);
    setDrillCC(cc || null);
    setDrillTitle(title);
    setDrillLoading(true);
    setDrillData([]);
    const effectiveMon = dashMonth === "YTD" ? dashAvailableMonths[dashAvailableMonths.length - 1] : dashMonth;
    const params = new URLSearchParams({ category, month: effectiveMon });
    if (cc) params.set("cc", cc);
    if (dashMonth === "YTD") params.set("ytd", "1");
    api.get(`/admin/bu-dashboard/cc-detail?${params}`)
      .then(res => { setDrillData(res.data.accounts || []); setDrillLoading(false); })
      .catch(() => setDrillLoading(false));
  };

  // ── Variance Report state ─────────────────────────────────────────────────
  const [vrData, setVrData] = useState<VRCostCenter[]>([]);
  const [vrLoading, setVrLoading] = useState(false);
  const [vrMonth, setVrMonth] = useState<string>("");
  const [vrBU, setVrBU] = useState<string>("");
  const [vrAvailableMonths, setVrAvailableMonths] = useState<string[]>([]);
  const [vrBusinessUnits, setVrBusinessUnits] = useState<string[]>([]);
  const [vrMonthsLoading, setVrMonthsLoading] = useState(false);
  const [vrSortBy, setVrSortBy] = useState<"variance" | "costCenter">("variance");
  const [vrSortDir, setVrSortDir] = useState<"asc" | "desc">("asc");
  const [vrActionPlans, setVrActionPlans] = useState<Record<string, VRActionPlanItem[]>>({});

  // Actions Report modal
  const [vrArOpen, setVrArOpen] = useState(false);
  const [vrArData, setVrArData] = useState<{ summary: { total: number; open: number; closed: number }; by_month: { month: string; open: number; closed: number }[]; items: { id: number; cost_center: string; cost_center_name: string; business_unit: string; cost_center_owner: string | null; month: string; issue_text: string | null; action_text: string; category: string | null; account_number: string | null; closeout_milestone: string | null; status: "OPEN" | "CLOSED"; created_by: string | null }[] } | null>(null);
  const [vrArLoading, setVrArLoading] = useState(false);
  const [vrArFilter, setVrArFilter] = useState<"ALL" | "OPEN" | "CLOSED">("ALL");
  const [vrArCopied, setVrArCopied] = useState(false);
  const [vrArSortCol, setVrArSortCol] = useState<"month" | "owner" | "category" | "closeout" | "status">("month");
  const [vrArSortDir, setVrArSortDir] = useState<"asc" | "desc">("asc");

  const AR_FY_MONTHS: Record<string, number> = { April: 0, May: 1, June: 2, July: 3, August: 4, September: 5, October: 6, November: 7, December: 8, January: 9, February: 10, March: 11 };

  const handleArToggleSort = (col: typeof vrArSortCol) => {
    if (vrArSortCol === col) {
      setVrArSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setVrArSortCol(col);
      setVrArSortDir("asc");
    }
  };

  const sortArItems = (items: typeof vrArData extends null ? never : NonNullable<typeof vrArData>["items"]) => {
    return [...items].sort((a, b) => {
      let cmp = 0;
      const dir = vrArSortDir === "asc" ? 1 : -1;

      switch (vrArSortCol) {
        case "month":
          cmp = (AR_FY_MONTHS[a.month] ?? 99) - (AR_FY_MONTHS[b.month] ?? 99);
          break;
        case "owner":
          cmp = (a.cost_center_owner || "").localeCompare(b.cost_center_owner || "");
          break;
        case "category":
          cmp = (a.category || "").localeCompare(b.category || "");
          break;
        case "closeout":
          if (!a.closeout_milestone && !b.closeout_milestone) cmp = 0;
          else if (!a.closeout_milestone) cmp = 1;
          else if (!b.closeout_milestone) cmp = -1;
          else cmp = a.closeout_milestone.localeCompare(b.closeout_milestone);
          break;
        case "status":
          cmp = a.status.localeCompare(b.status);
          break;
      }
      if (cmp !== 0) return cmp * dir;
      // Secondary sort: always CC ascending
      return (a.cost_center || "").localeCompare(b.cost_center || "");
    });
  };

  const handleActionsReport = async () => {
    setVrArOpen(true);
    setVrArFilter("ALL");
    setVrArSortCol("month");
    setVrArSortDir("asc");
    setVrArLoading(true);
    setVrArCopied(false);
    try {
      const params: Record<string, string> = { fy: currentFY };
      if (vrBU && vrData.length > 0) {
        const ccs = vrData.filter(d => d.business_unit === vrBU).map(d => d.cost_center);
        if (ccs.length > 0) params.cost_centers = ccs.join(",");
      }
      const res = await api.get("/action-plans/report", { params });
      setVrArData(res.data);
    } catch (err) {
      console.error("Failed to load actions report:", err);
    } finally {
      setVrArLoading(false);
    }
  };

  const handleArCopy = () => {
    if (!vrArData) return;
    const { items } = vrArData;
    const filtered = vrArFilter === "ALL" ? items : items.filter(i => i.status === vrArFilter);
    const sorted = sortArItems(filtered);

    const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const fmtDate = (d: string | null) =>
      d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

    const thStyle = 'style="background:#2d3748;color:#fff;padding:6px 10px;font-size:11px;font-weight:700;text-align:left;border:1px solid #4a5568"';
    const tdStyle = 'style="padding:5px 10px;font-size:11px;border:1px solid #e2e8f0"';

    const headerCols = ["Month", "CC", "CC Name", "Owner", "Issue", "Action", "Category", "Closeout Target", "Status"];
    const thead = `<tr>${headerCols.map(h => `<th ${thStyle}>${h}</th>`).join("")}</tr>`;
    const tbody = sorted.map(i => {
      const cells = [
        esc(i.month || ""),
        esc(i.cost_center || ""),
        esc(i.cost_center_name || ""),
        esc(i.cost_center_owner || ""),
        esc(i.issue_text || ""),
        esc(i.action_text || "(no description)"),
        esc(i.category || ""),
        fmtDate(i.closeout_milestone),
        i.status,
      ];
      return `<tr>${cells.map(c => `<td ${tdStyle}>${c}</td>`).join("")}</tr>`;
    }).join("");

    const html = `<table style="border-collapse:collapse;font-family:Calibri,Arial,sans-serif">${thead}${tbody}</table>`;

    // Plain text fallback (tab-separated)
    const T = "\t";
    const plain = [
      headerCols.join(T),
      ...sorted.map(i =>
        [i.month || "", i.cost_center || "", i.cost_center_name || "", i.cost_center_owner || "", i.issue_text || "", i.action_text || "(no description)", i.category || "", fmtDate(i.closeout_milestone), i.status].join(T)
      ),
    ].join("\n");

    const htmlBlob = new Blob([html], { type: "text/html" });
    const textBlob = new Blob([plain], { type: "text/plain" });

    navigator.clipboard.write([
      new ClipboardItem({ "text/html": htmlBlob, "text/plain": textBlob }),
    ]).then(() => {
      setVrArCopied(true);
      setTimeout(() => setVrArCopied(false), 2000);
    });
  };

  const doLoadVRData = useCallback((month: string, bu: string, availMonths?: string[]) => {
    if (!month) return;
    setVrLoading(true);
    const isYTD = month === "YTD";
    const effectiveMonths = availMonths || vrAvailableMonths;
    const effectiveMonth = isYTD ? effectiveMonths[effectiveMonths.length - 1] || "" : month;
    const params = new URLSearchParams({ month: effectiveMonth });
    if (bu) params.set("bu", bu);
    if (currentFY) params.set("fiscalYear", currentFY);
    if (isYTD) params.set("ytd", "1");
    api.get(`/admin/bu-dashboard/variance-report?${params}`)
      .then(res => {
        const ccs: VRCostCenter[] = res.data.cost_centers || [];
        setVrData(ccs);
        setVrBusinessUnits(res.data.business_units || []);
        setVrLoading(false);
        // Bulk-fetch action plans for all CCs in the report
        const ccNums = ccs.map(c => c.cost_center).filter(Boolean);
        if (ccNums.length > 0 && currentFY && effectiveMonth) {
          api.get("/action-plans/bulk", { params: { cost_centers: ccNums.join(","), fy: currentFY, month: effectiveMonth } })
            .then(apRes => setVrActionPlans(apRes.data || {}))
            .catch(() => setVrActionPlans({}));
        } else {
          setVrActionPlans({});
        }
      })
      .catch(() => { setVrLoading(false); setVrActionPlans({}); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFY, vrAvailableMonths]);

  const doLoadVRMonths = useCallback((bu: string) => {
    setVrMonthsLoading(true);
    const params = new URLSearchParams();
    if (bu) params.set("bu", bu);
    if (currentFY) params.set("fy", currentFY);
    api.get(`/admin/bu-dashboard/available-months?${params}`)
      .then(res => {
        const months: string[] = res.data.months || [];
        setVrAvailableMonths(months);
        const latest = months[months.length - 1] || "";
        setVrMonth(latest);
        setVrMonthsLoading(false);
        if (latest) doLoadVRData(latest, bu, months);
      })
      .catch(() => setVrMonthsLoading(false));
  }, [doLoadVRData, currentFY]);

  useEffect(() => {
    if (activeTab === "variancereport" && fyLoaded && vrAvailableMonths.length === 0 && !vrMonthsLoading) {
      doLoadVRMonths(vrBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fyLoaded]);

  const handleVRPrint = () => window.print();

  // ── BU Forecast state ───────────────────────────────────────────────────
  const [fcDimA, setFcDimA] = useState<string>("Annual Budget");
  const [fcDimB, setFcDimB] = useState<string>("July Forecast");
  const [fcFYA,  setFcFYA]  = useState<string>(autoFY());
  const [fcFYB,  setFcFYB]  = useState<string>(autoFY());
  const [fcBU,   setFcBU]   = useState<string>("");
  const [fcSelectedCCs, setFcSelectedCCs] = useState<string[]>([]);
  const [fcBusinessUnits, setFcBusinessUnits] = useState<string[]>([]);
  const [fcData,         setFcData]         = useState<ForecastCatKPI[]>([]);
  const [fcBUSummary,    setFcBUSummary]    = useState<ForecastBUSummaryRow[]>([]);
  const [fcLoading,      setFcLoading]      = useState(false);
  const [fcDrillCategory, setFcDrillCategory] = useState<string | null>(null);

  /** Build the full dimension string for the API (always appends FY). */
  const buildDimName = (dim: string, fy: string) => `${dim} ${fy}`;

  const doLoadForecast = useCallback((dimA: string, dimB: string, bu: string, selectedCCs: string[] = [], fyA?: string, fyB?: string) => {
    setFcLoading(true);
    setFcData([]);
    setFcBUSummary([]);
    setFcDrillCategory(null);
    const fullDimA = buildDimName(dimA, fyA ?? fcFYA);
    const fullDimB = buildDimName(dimB, fyB ?? fcFYB);
    const params = new URLSearchParams({ dimA: fullDimA, dimB: fullDimB });
    if (bu) params.set("bu", bu);
    if (selectedCCs.length > 0) params.set("cc", selectedCCs.join(","));
    api.get(`/admin/bu-forecast?${params}`)
      .then(res => {
        setFcData(res.data.categories || []);
        setFcBUSummary(res.data.bu_summary || []);
        setFcBusinessUnits(res.data.business_units || []);
        setFcLoading(false);
      })
      .catch(() => setFcLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fcFYA, fcFYB]);

  useEffect(() => {
    if (activeTab === "buforecast" && fcData.length === 0 && !fcLoading) {
      doLoadForecast(fcDimA, fcDimB, fcBU);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Actuals Dashboard state ──────────────────────────────────────────────
  const [actualsBU, setActualsBU] = useState<string>("");
  const [actualsData, setActualsData] = useState<ActualsCatKPI[]>([]);
  const [actualsBusinessUnits, setActualsBusinessUnits] = useState<string[]>([]);
  const [actualsLoading, setActualsLoading] = useState(false);
  const [actDrillCC, setActDrillCC] = useState<string | null>(null);
  const [actDrillCategory, setActDrillCategory] = useState<string | null>(null);
  const [actDrillData, setActDrillData] = useState<ActualsAcctRow[] | null>(null);
  const [actDrillLoading, setActDrillLoading] = useState(false);
  const [actDrillTitle, setActDrillTitle] = useState("");

  const doLoadActuals = useCallback((bu: string) => {
    setActualsLoading(true);
    setActualsData([]);
    setActDrillData(null);
    setActDrillCC(null);
    setActDrillCategory(null);
    const qs = bu ? `?bu=${encodeURIComponent(bu)}` : "";
    api.get(`/admin/actuals-dashboard${qs}`)
      .then(res => {
        setActualsData(res.data.categories || []);
        setActualsBusinessUnits(res.data.business_units || []);
        setActualsLoading(false);
      })
      .catch(() => setActualsLoading(false));
  }, []);


  const doLoadActualsDrilldown = (category: string, cc?: string) => {
    const catData = actualsData.find(c => c.category === category);
    const ccRow = catData?.top_spenders.find(r => r.cost_center === cc);
    const title = cc
      ? `${category} — CC ${cc}${ccRow?.cost_center_name ? ` (${ccRow.cost_center_name})` : ""}`
      : `${category} — All Cost Centers`;
    setActDrillCategory(category);
    setActDrillCC(cc || null);
    setActDrillTitle(title);
    setActDrillLoading(true);
    setActDrillData([]);
    const params = new URLSearchParams({ category });
    if (cc) params.set("cc", cc);
    api.get(`/admin/actuals-dashboard/cc-detail?${params}`)
      .then(res => { setActDrillData(res.data.accounts || []); setActDrillLoading(false); })
      .catch(() => setActDrillLoading(false));
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="admin-panel">

      {/* ── Tab bar ──────────────────────────────────────────────────────── */}
      <div className="admin-tabs">
        {isAdminSetupGroup ? (
          <>
            <button className={`admin-tab${activeTab === "businessunits" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("businessunits")}>
              <span className="material-icons">business</span> Business Units
            </button>
            <button className={`admin-tab${activeTab === "costcenters" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("costcenters")}>
              <span className="material-icons">store</span> Cost Centers
            </button>
            <button className={`admin-tab${activeTab === "users" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("users")}>
              <span className="material-icons">people</span> Users
            </button>
            <button className={`admin-tab${activeTab === "accounts" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("accounts")}>
              <span className="material-icons">category</span> Accounts
            </button>
            <div className="admin-tab-divider" />
            <button className={`admin-tab${activeTab === "auditlog" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("auditlog")}>
              <span className="material-icons">history</span> Audit Log
            </button>
          </>
        ) : (
          <>
            <button className={`admin-tab${activeTab === "dashboard" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("dashboard")}>
              <span className="material-icons">bar_chart</span> Actuals Dashboard
            </button>
            <button className={`admin-tab${activeTab === "variancereport" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("variancereport")}>
              <span className="material-icons">assessment</span> Variance Report
            </button>
            <div className="admin-tab-divider" />
            <button className={`admin-tab${activeTab === "finance" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("finance")}>
              <span className="material-icons">savings</span> Forecast Status
            </button>
            <button className={`admin-tab${activeTab === "buforecast" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("buforecast")}>
              <span className="material-icons">compare_arrows</span> Forecast Comparison
            </button>
            <button className={`admin-tab${activeTab === "forecast" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("forecast")}>
              <span className="material-icons">date_range</span> Forecast Windows
            </button>
            <div className="admin-tab-divider" />
            <button className={`admin-tab${activeTab === "upload" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("upload")}>
              <span className="material-icons">upload_file</span> Data Upload
            </button>
            <button className={`admin-tab${activeTab === "salary" ? " admin-tab--active" : ""}`} onClick={() => setActiveTab("salary")}>
              <span className="material-icons">payments</span> Salary Config
            </button>
          </>
        )}
      </div>

      {/* ── Tab: Forecast Windows ────────────────────────────────────────── */}
      {activeTab === "forecast" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">event</span>
            <span className="admin-section-title">Forecast Dimension Windows</span>
            <span className="admin-section-subtitle">
              Select the active planning cycle · Edit dates for reference
            </span>
          </div>

          {/* ── Active Fiscal Year selector ── */}
          <div className="admin-forecast-bar admin-fy-bar">
            <span className="admin-forecast-bar-label">Active Fiscal Year</span>
            <select
              className="admin-fy-select"
              value={fyDraft}
              onChange={e => { setFyDraft(e.target.value); setFyFeedback(null); }}
            >
              {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
            <button
              className={`admin-fy-btn${fyLoading ? " admin-fy-btn--loading" : ""}`}
              onClick={handleSaveFY}
              disabled={fyLoading || fyDraft === currentFY}
            >
              {fyLoading ? "Saving…" : fyDraft === currentFY ? "Active" : "Set"}
            </button>
            {fyFeedback && (
              <span className={`admin-fy-feedback${fyFeedback.ok ? "" : " admin-fy-feedback--err"}`}>
                {fyFeedback.msg}
              </span>
            )}
          </div>

          {/* ── Password Reset Email ── */}
          <div className="admin-forecast-bar">
            <span className="admin-forecast-bar-label">Password Reset Email</span>
            <input
              className="admin-text-input"
              type="email"
              placeholder="firstname.last@daikinapplied.com"
              style={{ width: "260px" }}
              value={resetEmailDraft}
              onChange={e => { setResetEmailDraft(e.target.value); setResetEmailFeedback(null); }}
            />
            <button
              className={`admin-fy-btn${resetEmailLoading ? " admin-fy-btn--loading" : ""}`}
              onClick={handleSaveResetEmail}
              disabled={resetEmailLoading || resetEmailDraft === resetEmail || !resetEmailDraft}
            >
              {resetEmailLoading ? "Saving…" : resetEmailDraft === resetEmail && resetEmail ? "Saved" : "Save"}
            </button>
            {resetEmailFeedback && (
              <span className={`admin-fy-feedback${resetEmailFeedback.ok ? "" : " admin-fy-feedback--err"}`}>
                {resetEmailFeedback.msg}
              </span>
            )}
          </div>

          {/* ── Main Forecast Planning toggle ── */}
          <div className="admin-forecast-bar">
            <span className="admin-forecast-bar-label">Forecast Planning</span>
            <button
              className={`admin-toggle${forecastActive ? " admin-toggle--on" : ""}`}
              onClick={handleToggleForecast}
              title={forecastActive ? "Click to disable editing for CC owners" : "Click to enable editing for CC owners"}
            />
            <span className={`admin-forecast-status${forecastActive ? " admin-forecast-status--on" : ""}`}>
              {forecastActive ? "Editing enabled — CC owners can edit the selected dimension" : "Editing disabled — CC owners cannot edit"}
            </span>
          </div>

          {/* ── Notes Journal ── */}
          <div className="admin-forecast-bar">
            <span className="admin-forecast-bar-label">Notes Journal</span>
            <button
              onClick={openJournal}
              title="View all admin and CC owner notes across all forecast dimensions"
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 14px", fontSize: 12, fontWeight: 600, border: "1px solid oklch(0.42 0.12 280)", background: "oklch(0.42 0.12 280)", color: "#fff", borderRadius: 6, cursor: "pointer" }}
            >
              <span className="material-icons" style={{ fontSize: 16 }}>auto_stories</span>
              Open Journal
            </button>
            <span style={{ fontSize: 11, color: "oklch(0.55 0.02 250)" }}>All notes organized by forecast dimension</span>
          </div>

          {/* ── Help Page URLs ── */}
          <div className="admin-forecast-bar" style={{ flexWrap: "wrap", gap: "8px 12px" }}>
            <span className="admin-forecast-bar-label" style={{ width: "100%" }}>Help Page URLs</span>
            <label style={{ fontSize: "12px", color: "#64748b", minWidth: "80px" }}>Admin URL</label>
            <input
              className="admin-text-input"
              type="url"
              placeholder="https://sharepoint.com/admin-help"
              style={{ width: "340px" }}
              value={helpUrlAdminDraft}
              onChange={e => { setHelpUrlAdminDraft(e.target.value); setHelpUrlFeedback(null); }}
            />
            <label style={{ fontSize: "12px", color: "#64748b", minWidth: "80px" }}>User URL</label>
            <input
              className="admin-text-input"
              type="url"
              placeholder="https://sharepoint.com/user-help"
              style={{ width: "340px" }}
              value={helpUrlUserDraft}
              onChange={e => { setHelpUrlUserDraft(e.target.value); setHelpUrlFeedback(null); }}
            />
            <button
              className={`admin-fy-btn${helpUrlLoading ? " admin-fy-btn--loading" : ""}`}
              onClick={handleSaveHelpUrls}
              disabled={helpUrlLoading || (helpUrlAdminDraft === helpUrlAdmin && helpUrlUserDraft === helpUrlUser)}
            >
              {helpUrlLoading ? "Saving…" : (helpUrlAdminDraft === helpUrlAdmin && helpUrlUserDraft === helpUrlUser) ? "Saved" : "Save"}
            </button>
            {helpUrlFeedback && (
              <span className={`admin-fy-feedback${helpUrlFeedback.ok ? "" : " admin-fy-feedback--err"}`}>
                {helpUrlFeedback.msg}
              </span>
            )}
          </div>

          <div className="admin-section-body" style={{ padding: 0 }}>
            {dimsLoading ? (
              <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
            ) : (
              <table className="admin-dim-table">
                <thead>
                  <tr>
                    <th style={{ width: "48px", textAlign: "center" }}>Select</th>
                    <th style={{ width: "28%" }}>Dimension</th>
                    <th style={{ width: "22%" }}>Open From</th>
                    <th style={{ width: "22%" }}>Open To</th>
                    <th style={{ width: "180px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ALL_DIMS.map(dim => {
                    const draft = dimDrafts[dim];
                    if (!draft) return null;
                    const isSelected = selectedDim === dim;
                    return (
                      <tr key={dim} className={isSelected && forecastActive ? "admin-dim-row--live" : ""}>
                        {/* Radio */}
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="radio"
                            name="forecast-dim"
                            className="admin-radio"
                            checked={isSelected}
                            onChange={() => handleSelectDim(dim)}
                            title={`Set ${dim} as active planning cycle`}
                          />
                        </td>
                        {/* Dimension name */}
                        <td>
                          <span className="admin-dim-name">{dim}</span>
                          {isSelected && forecastActive && (
                            <span className="admin-dim-live-badge">Live</span>
                          )}
                          {isSelected && !forecastActive && (
                            <span className="admin-dim-selected-badge">Selected</span>
                          )}
                        </td>
                        {/* Open From */}
                        <td>
                          {draft.editing ? (
                            <input
                              type="date"
                              className="admin-date-input"
                              value={draft.open_from}
                              onChange={e => updateDimField(dim, "open_from", e.target.value)}
                            />
                          ) : (
                            <span className="admin-date-text">{fmtDate(draft.open_from)}</span>
                          )}
                        </td>
                        {/* Open To */}
                        <td>
                          {draft.editing ? (
                            <input
                              type="date"
                              className="admin-date-input"
                              value={draft.open_to}
                              onChange={e => updateDimField(dim, "open_to", e.target.value)}
                            />
                          ) : (
                            <span className="admin-date-text">{fmtDate(draft.open_to)}</span>
                          )}
                        </td>
                        {/* Actions */}
                        <td>
                          <div className="admin-btn-row">
                            {draft.editing ? (
                              <>
                                <button
                                  className="admin-btn admin-btn--primary"
                                  onClick={() => saveDimWindow(dim)}
                                  disabled={draft.saving || !draft.open_from || !draft.open_to}
                                >
                                  <span className="material-icons">save</span>
                                  {draft.saving ? "Saving…" : "Save"}
                                </button>
                                <button
                                  className="admin-btn admin-btn--ghost"
                                  onClick={() => cancelEditing(dim)}
                                  disabled={draft.saving}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className="admin-btn admin-btn--ghost"
                                onClick={() => startEditing(dim)}
                              >
                                <span className="material-icons">edit</span>
                                Edit
                              </button>
                            )}
                          </div>
                          {draft.feedback && (
                            <div className={`admin-toast admin-toast--${draft.feedback.ok ? "ok" : "err"}`} style={{ marginTop: "4px" }}>
                              <span className="material-icons" style={{ fontSize: "13px" }}>
                                {draft.feedback.ok ? "check_circle" : "error"}
                              </span>
                              {draft.feedback.msg}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Cost Centers ────────────────────────────────────────────── */}
      {activeTab === "costcenters" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">store</span>
            <span className="admin-section-title">Cost Centers</span>
            <span className="admin-kpi-chip">
              <span className="material-icons">groups</span>
              {costCenters.length} Cost Centers
            </span>
          </div>
          <div className="admin-section-body">

            {/* ── Toolbar ── */}
            <div className="admin-cc-toolbar">
              <div className="admin-search-wrap">
                <span className="material-icons">search</span>
                <input
                  className="admin-search"
                  type="text"
                  placeholder="Search by number or name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              {allBUs.length > 1 && (
                <select
                  className="admin-select"
                  value={buFilter}
                  onChange={e => setBuFilter(e.target.value)}
                >
                  <option value="">All Business Units</option>
                  {allBUs.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                </select>
              )}
              <button
                className="admin-btn admin-btn--primary"
                onClick={() => { setShowAddForm(f => !f); setAddFeedback(null); }}
              >
                <span className="material-icons">{showAddForm ? "close" : "add"}</span>
                {showAddForm ? "Cancel" : "Add Cost Center"}
              </button>
            </div>

            {/* ── Add-form ── */}
            {showAddForm && (
              <div className="admin-add-form">
                <input
                  className="admin-text-input"
                  placeholder="CC #"
                  style={{ width: "80px" }}
                  value={addDraft.cost_center_number}
                  onChange={e => setAddDraft(d => ({ ...d, cost_center_number: e.target.value }))}
                />
                <input
                  className="admin-text-input"
                  placeholder="Cost Center Name"
                  style={{ flex: 1 }}
                  value={addDraft.cost_center_name}
                  onChange={e => setAddDraft(d => ({ ...d, cost_center_name: e.target.value }))}
                />
                <select
                  className="admin-select"
                  value={addDraft.business_unit_id ?? ""}
                  onChange={e => setAddDraft(d => ({ ...d, business_unit_id: e.target.value ? Number(e.target.value) : null }))}
                  style={{ width: "180px" }}
                >
                  <option value="">Select Business Unit…</option>
                  {buOptions.map(bu => <option key={bu.id} value={bu.id}>{bu.name}</option>)}
                </select>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={addCostCenter}
                  disabled={addSaving}
                >
                  <span className="material-icons">add</span>
                  {addSaving ? "Adding…" : "Add"}
                </button>
                {addFeedback && (
                  <span className={`admin-toast admin-toast--${addFeedback.ok ? "ok" : "err"}`}>
                    <span className="material-icons" style={{ fontSize: "13px" }}>
                      {addFeedback.ok ? "check_circle" : "error"}
                    </span>
                    {addFeedback.msg}
                  </span>
                )}
              </div>
            )}

            {ccLoading ? (
              <div style={{ color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="admin-cc-table">
                  <colgroup>
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "38%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>CC #</th>
                      <th>Name</th>
                      <th>Business Unit</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCCs.map(cc => {
                      const origCC = cc.cost_center_number;
                      const draft = ccDrafts[origCC];
                      if (!draft) return null;
                      const isInactive = !!cc.deactivated_from_fy;
                      return (
                        <tr key={origCC} className={isInactive ? "cc-row--inactive" : ""}>
                          {/* CC # */}
                          <td>
                            {draft.editing ? (
                              <input
                                className="admin-text-input"
                                style={{ width: "70px" }}
                                value={draft.cost_center_number}
                                onChange={e => updateCCField(origCC, "cost_center_number", e.target.value)}
                              />
                            ) : (
                              <span className="admin-cc-num">{origCC}</span>
                            )}
                          </td>
                          {/* Name */}
                          <td>
                            {draft.editing ? (
                              <input
                                className="admin-text-input"
                                style={{ width: "100%" }}
                                value={draft.cost_center_name}
                                onChange={e => updateCCField(origCC, "cost_center_name", e.target.value)}
                              />
                            ) : (
                              <span style={{ fontSize: "13px", color: isInactive ? "#9ca3af" : "#1e293b" }}>
                                {cc.cost_center_name}
                                {isInactive && (
                                  <span className="cc-inactive-badge">Inactive from {cc.deactivated_from_fy}</span>
                                )}
                              </span>
                            )}
                          </td>
                          {/* Business Unit */}
                          <td>
                            {draft.editing ? (
                              <select
                                className="admin-select"
                                style={{ width: "100%", fontSize: "12px" }}
                                value={draft.business_unit_id ?? ""}
                                onChange={e => updateCCField(origCC, "business_unit_id", e.target.value ? Number(e.target.value) : null)}
                              >
                                <option value="">— None —</option>
                                {buOptions.map(bu => <option key={bu.id} value={bu.id}>{bu.name}</option>)}
                              </select>
                            ) : (
                              <span style={{ fontSize: "13px", color: "#374151" }}>{cc.business_unit}</span>
                            )}
                          </td>
                          {/* Actions */}
                          <td>
                            <div className="admin-btn-row">
                              {draft.editing ? (
                                <>
                                  <button
                                    className="admin-btn admin-btn--primary"
                                    onClick={() => saveCC(origCC)}
                                    disabled={draft.saving}
                                  >
                                    <span className="material-icons">save</span>
                                    {draft.saving ? "…" : "Save"}
                                  </button>
                                  <button
                                    className="admin-btn admin-btn--ghost"
                                    onClick={() => cancelEditingCC(origCC)}
                                    disabled={draft.saving}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  {onImpersonate && !isInactive && (
                                    <button
                                      className="admin-btn admin-btn--accent"
                                      onClick={() => onImpersonate(origCC, cc.cost_center_name, cc.cost_center_owner || "")}
                                      title="View and act as this CC's owner"
                                    >
                                      <span className="material-icons">visibility</span>
                                      View as Owner
                                    </button>
                                  )}
                                  <button
                                    className="admin-btn admin-btn--ghost"
                                    onClick={() => startEditingCC(origCC)}
                                  >
                                    <span className="material-icons">edit</span>
                                    Edit
                                  </button>
                                  <button
                                    className={`admin-btn ${isInactive ? "admin-btn--reactivate" : "admin-btn--deactivate"}`}
                                    onClick={() => toggleCCActive(origCC, isInactive)}
                                    title={isInactive ? "Reactivate cost center" : `Deactivate from ${currentFY}`}
                                  >
                                    <span className="material-icons">{isInactive ? "check_circle" : "block"}</span>
                                    {isInactive ? "Activate" : "Deactivate"}
                                  </button>
                                  <button
                                    className="admin-btn admin-btn--danger"
                                    onClick={() => deleteCC(origCC)}
                                    title="Delete cost center"
                                  >
                                    <span className="material-icons">delete</span>
                                  </button>
                                </>
                              )}
                            </div>
                            {draft.feedback && (
                              <div className={`admin-toast admin-toast--${draft.feedback.ok ? "ok" : "err"}`} style={{ marginTop: "4px" }}>
                                <span className="material-icons" style={{ fontSize: "13px" }}>
                                  {draft.feedback.ok ? "check_circle" : "error"}
                                </span>
                                {draft.feedback.msg}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredCCs.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
                          {search ? `No cost centers match "${search}"` : "No cost centers"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Accounts ────────────────────────────────────────────────── */}
      {activeTab === "accounts" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">category</span>
            <span className="admin-section-title">Account &amp; Category Manager</span>
            <div className="admin-kpi-row">
              <span className="admin-kpi-chip">
                <span className="material-icons">receipt_long</span>
                {accounts.length} Accounts
              </span>
            </div>
          </div>
          <div className="admin-section-body">

            {/* ── Auto-Calculate Salary & Benefits ── */}
            <div className="acct-auto-calc-card">
              <div className="acct-auto-calc-header">
                <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.52 0.15 260)" }}>calculate</span>
                <span className="acct-auto-calc-title">Auto-Calculate Salary & Benefits</span>
              </div>
              <div className="acct-auto-calc-body">
                <label className="acct-auto-calc-toggle">
                  <input
                    type="checkbox"
                    checked={salaryAutoCompute}
                    disabled={salaryAutoSaving}
                    onChange={async e => {
                      const enabled = e.target.checked;
                      const prev = salaryAutoCompute;
                      setSalaryAutoCompute(enabled);
                      setSalaryAutoSaving(true);
                      try {
                        await api.put("/admin/salary-auto-compute", { enabled });
                      } catch (err) {
                        console.error("salary-auto-compute toggle failed:", err);
                        setSalaryAutoCompute(prev);
                      } finally {
                        setSalaryAutoSaving(false);
                      }
                    }}
                  />
                  <span className="acct-auto-calc-switch" />
                  <span className="acct-auto-calc-label">
                    {salaryAutoCompute ? "Enabled" : "Disabled"}
                  </span>
                </label>
                <span className="acct-auto-calc-hint">
                  {salaryAutoCompute
                    ? "Accounts 60010, 60022, 60100, 60120, 60130 are computed from employee data and locked in the planner."
                    : "Auto-calculation is off. These accounts are unlocked for direct editing in the forecast planner."}
                </span>
              </div>
            </div>

            {/* ── Toolbar ── */}
            <div className="admin-cc-toolbar">
              <div className="admin-search-wrap">
                <span className="material-icons">search</span>
                <input
                  className="admin-search"
                  type="text"
                  placeholder="Search account name or category…"
                  value={acctSearch}
                  onChange={e => setAcctSearch(e.target.value)}
                />
              </div>
              <button className="admin-btn admin-btn--ghost" onClick={loadAccounts}>
                <span className="material-icons">refresh</span>
                Refresh
              </button>
            </div>

            {/* ── Category datalist ── */}
            <datalist id="admin-categories">
              {allCategories.map(c => <option key={c} value={c} />)}
            </datalist>

            {acctLoading ? (
              <div style={{ color: "oklch(0.55 0.015 250)", fontSize: "13px" }}>Loading…</div>
            ) : (
              <div className="acct-table-wrap">
                <table className="admin-cc-table">
                  <thead>
                    <tr>
                      <th style={{ width: "90px" }}>Acct #</th>
                      <th style={{ width: "40%" }}>Account Name</th>
                      <th style={{ width: "180px" }}>Category</th>
                      <th style={{ width: "100px", textAlign: "center" }}>CCs</th>
                      <th style={{ width: "140px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acctsByCategory.flatMap(({ category, accounts: catAccts }) => [
                      <tr key={`hdr-${category}`} className="admin-acct-cat-hdr">
                        <td colSpan={5}>
                          <span className="acct-cat-dot" style={{ background: CAT_COLOR(category) }} />
                          {category}
                          <span className="acct-cat-count">{catAccts.length}</span>
                        </td>
                      </tr>,
                      ...catAccts.map(acct => {
                        const draft = acctDrafts[acct.account];
                        if (!draft) return null;
                        const acctNum = acct.account.includes(" - ") ? acct.account.split(" - ")[0] : "";
                        return (
                          <tr key={`${acct.category}::${acct.account}`}>
                            <td>
                              <span className="admin-cc-num">{acctNum}</span>
                            </td>
                            <td>
                              {draft.editing ? (
                                <input
                                  className="admin-text-input"
                                  style={{ width: "100%" }}
                                  value={draft.account}
                                  onChange={e => updateAcctField(acct.account, "account", e.target.value)}
                                />
                              ) : (
                                <span style={{ color: "oklch(0.15 0.02 250)" }}>
                                  {acct.account.includes(" - ") ? acct.account.split(" - ").slice(1).join(" - ") : acct.account}
                                </span>
                              )}
                            </td>
                            <td className="acct-cat-cell">
                              {draft.editing ? (
                                <input
                                  className="admin-text-input"
                                  list="admin-categories"
                                  value={draft.category}
                                  onChange={e => updateAcctField(acct.account, "category", e.target.value)}
                                />
                              ) : (
                                acct.category
                              )}
                            </td>
                            <td className="acct-cc-count">
                              {acct.cc_count || ""}
                            </td>
                            <td>
                              <div className="admin-btn-row">
                                {draft.editing ? (
                                  <>
                                    <button
                                      className="admin-btn admin-btn--primary"
                                      onClick={() => saveAcct(acct.account)}
                                      disabled={draft.saving || !draft.account.trim() || !draft.category.trim()}
                                    >
                                      <span className="material-icons">save</span>
                                      {draft.saving ? "Saving…" : "Save"}
                                    </button>
                                    <button
                                      className="admin-btn admin-btn--ghost"
                                      onClick={() => cancelEditingAcct(acct.account)}
                                      disabled={draft.saving}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="admin-btn admin-btn--ghost"
                                      onClick={() => startEditingAcct(acct.account)}
                                    >
                                      <span className="material-icons">edit</span>
                                      Edit
                                    </button>
                                    <button
                                      className="admin-btn acct-delete-btn"
                                      onClick={() => deleteAcct(acct.account)}
                                      title="Delete account"
                                    >
                                      <span className="material-icons">delete</span>
                                    </button>
                                  </>
                                )}
                              </div>
                              {draft.feedback && (
                                <div className={`admin-toast admin-toast--${draft.feedback.ok ? "ok" : "err"}`} style={{ marginTop: "4px" }}>
                                  <span className="material-icons" style={{ fontSize: "13px" }}>
                                    {draft.feedback.ok ? "check_circle" : "error"}
                                  </span>
                                  {draft.feedback.msg}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      }),
                    ])}
                    {filteredAccts.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: "32px 24px", color: "oklch(0.55 0.015 250)", fontSize: "13px" }}>
                          {acctSearch ? "No accounts match the current filter." : "No accounts loaded."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Unmapped Accounts Panel ── */}
            <div className="admin-unmapped-panel" style={{ marginTop: 24 }}>
              <button
                className="admin-unmapped-toggle"
                onClick={() => { setUnmappedOpen(o => !o); if (!unmappedOpen && unmappedAccts.length === 0) loadUnmapped(); }}
              >
                <span className="material-icons" style={{ fontSize: 18, transition: "transform .2s", transform: unmappedOpen ? "rotate(90deg)" : "none" }}>chevron_right</span>
                <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.7 0.15 70)" }}>warning</span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>No FY Mapping</span>
                {unmappedAccts.length > 0 && (
                  <span className="admin-kpi-chip" style={{ marginLeft: 8, background: "oklch(0.97 0.04 70)", color: "oklch(0.45 0.12 55)" }}>
                    {unmappedAccts.length}
                  </span>
                )}
                <span style={{ fontSize: 12, color: "oklch(0.55 0.015 250)", marginLeft: 8 }}>
                  Accounts not assigned to any fiscal year
                </span>
              </button>

              {unmappedOpen && (
                <div style={{ marginTop: 8 }}>
                  {unmappedLoading ? (
                    <div style={{ color: "oklch(0.55 0.015 250)", fontSize: 13, padding: "12px 0" }}>Loading…</div>
                  ) : unmappedAccts.length === 0 ? (
                    <div style={{ color: "oklch(0.42 0.15 155)", fontSize: 13, padding: "12px 0" }}>
                      <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4 }}>check_circle</span>
                      All accounts are mapped to at least one fiscal year
                    </div>
                  ) : (
                    <>
                      {/* Toolbar */}
                      <div className="admin-cc-toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                          <input type="checkbox" checked={unmappedSelected.size === unmappedAccts.length && unmappedAccts.length > 0} onChange={toggleAllUnmapped} />
                          Select all ({unmappedAccts.length})
                        </label>
                        {unmappedSelected.size > 0 && (
                          <span style={{ fontSize: 12, color: "#6366f1", fontWeight: 600 }}>{unmappedSelected.size} selected</span>
                        )}
                        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                          <button
                            className="admin-btn admin-btn--primary"
                            disabled={unmappedSelected.size === 0 || unmappedAssigning}
                            onClick={() => assignUnmappedToFY("FY25")}
                          >
                            <span className="material-icons">add_circle</span>
                            Assign FY25
                          </button>
                          <button
                            className="admin-btn admin-btn--primary"
                            disabled={unmappedSelected.size === 0 || unmappedAssigning}
                            onClick={() => assignUnmappedToFY("FY26")}
                          >
                            <span className="material-icons">add_circle</span>
                            Assign FY26
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            disabled={unmappedSelected.size === 0 || unmappedAssigning}
                            onClick={deleteUnmappedAccounts}
                          >
                            <span className="material-icons">delete</span>
                            Delete
                          </button>
                          <button className="admin-btn admin-btn--ghost" onClick={loadUnmapped}>
                            <span className="material-icons">refresh</span>
                          </button>
                        </div>
                      </div>

                      {/* Table */}
                      <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto", marginTop: 8 }}>
                        <table className="admin-cc-table">
                          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr>
                              <th style={{ width: 36 }}></th>
                              <th style={{ width: 80 }}>#</th>
                              <th>Account Name</th>
                              <th style={{ width: 160 }}>Category</th>
                              <th style={{ width: 90, textAlign: "center" }}>Budgets</th>
                              <th style={{ width: 90, textAlign: "center" }}>Actuals</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const grouped: Record<string, UnmappedAccount[]> = {};
                              unmappedAccts.forEach(a => { (grouped[a.category] = grouped[a.category] || []).push(a); });
                              return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).flatMap(([cat, accts]) => [
                                <tr key={`uhdr-${cat}`} className="admin-acct-cat-hdr">
                                  <td colSpan={6}>
                                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: CAT_COLOR(cat), marginRight: 6, verticalAlign: "middle" }} />
                                    {cat}
                                    <span style={{ marginLeft: 8, fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>{accts.length}</span>
                                  </td>
                                </tr>,
                                ...accts.map(a => {
                                  const acctNum = a.account.includes(" - ") ? a.account.split(" - ")[0] : "";
                                  const acctName = a.account.includes(" - ") ? a.account.split(" - ").slice(1).join(" - ") : a.account;
                                  return (
                                    <tr key={a.id} style={{ opacity: unmappedSelected.has(a.id) ? 1 : 0.75 }}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={unmappedSelected.has(a.id)}
                                          onChange={() => toggleUnmappedSelect(a.id)}
                                        />
                                      </td>
                                      <td><span className="admin-cc-num" style={{ fontSize: 12 }}>{acctNum}</span></td>
                                      <td><span style={{ fontSize: 13 }}>{acctName}</span></td>
                                      <td><span style={{ fontSize: 13, color: "#374151" }}>{a.category}</span></td>
                                      <td style={{ textAlign: "center" }}>
                                        <span style={{ fontSize: 12, color: a.budget_rows > 0 ? "#2563eb" : "#d1d5db" }}>
                                          {a.budget_rows}
                                        </span>
                                      </td>
                                      <td style={{ textAlign: "center" }}>
                                        <span style={{ fontSize: 12, color: a.actual_rows > 0 ? "#059669" : "#d1d5db" }}>
                                          {a.actual_rows}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                }),
                              ]);
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Actuals Dashboard ───────────────────────────────────────── */}
      {activeTab === "dashboard" && (
        <ActualsDashboard apiPrefix="/admin" fiscalYear={currentFY} ccsByBU={ccsByBU} />
      )}
      {/* ── Tab: Variance Report ──────────────────────────────────────────── */}
      {activeTab === "variancereport" && (
        <div className="admin-section vr-section">
          <div className="admin-section-header vr-no-print">
            <span className="material-icons">assessment</span>
            <span className="admin-section-title">Variance Report</span>
            <span className="admin-section-subtitle">{vrMonth && vrMonth !== "YTD" && !["April","May","June"].includes(vrMonth) ? "Forecast" : "Budget"} vs Actuals · {vrMonth && vrMonth !== "YTD" && !["April","May","June"].includes(vrMonth) ? `${vrMonth} Forecast` : "Annual Budget"} {currentFY ? currentFY.toUpperCase() : ""}</span>
            <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center" }}>
              {vrBusinessUnits.length > 0 && (
                <select className="admin-select" value={vrBU}
                  onChange={e => { const bu = e.target.value; setVrBU(bu); setVrAvailableMonths([]); setVrMonth(""); doLoadVRMonths(bu); }}>
                  <option value="">All Business Units</option>
                  {vrBusinessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                </select>
              )}
              {vrAvailableMonths.length > 0 && (
                <select className="admin-select" value={vrMonth}
                  onChange={e => { const m = e.target.value; setVrMonth(m); doLoadVRData(m, vrBU); }}>
                  {vrAvailableMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  <option value="YTD">YTD</option>
                </select>
              )}
              <button className="admin-btn" onClick={handleActionsReport}>
                <span className="material-icons">fact_check</span> Actions Report
              </button>
              <button className="admin-btn admin-btn--primary" onClick={handleVRPrint}>
                <span className="material-icons">picture_as_pdf</span> Export PDF
              </button>
            </div>
          </div>

          {vrMonthsLoading || vrLoading ? (
            <div style={{ padding: "32px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>Loading variance report…</div>
          ) : vrData.length === 0 ? (
            <div style={{ padding: "32px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>
              No variance data available. Upload actuals to generate this report.
            </div>
          ) : (() => {
            const isYTD = vrMonth === "YTD";
            const periodLabel = isYTD ? "YTD" : vrMonth;
            const BUDGET_ONLY_MONTHS = new Set(["April", "May", "June"]);
            const isForecastMonth = vrMonth && vrMonth !== "YTD" && !BUDGET_ONLY_MONTHS.has(vrMonth);
            const refWord = isForecastMonth ? "Forecast" : "Budget";
            const VR_CATS = ["COMPENSATION", "OCCUPANCY", "OPERATING", "ADVERTISING", "ALLOCATED"] as const;
            const VR_CAT_COLORS: Record<string, string> = {
              COMPENSATION: "#4A90E2", OCCUPANCY: "#e84393", OPERATING: "#ea580c",
              ADVERTISING: "#7c3aed", ALLOCATED: "#475569",
            };

            // Build BU groups
            const buGroups: { bu: string; ccs: typeof vrData }[] = [];
            const buMap = new Map<string, typeof vrData>();
            vrData.forEach(cc => {
              const bu = cc.business_unit || "Other";
              if (!buMap.has(bu)) buMap.set(bu, []);
              buMap.get(bu)!.push(cc);
            });
            buMap.forEach((ccs, bu) => buGroups.push({ bu, ccs }));
            buGroups.sort((a, b) => a.bu.localeCompare(b.bu));

            // VP-level aggregates
            const totalBudget = vrData.reduce((s, c) => s + (isYTD ? c.ytd_budget : c.month_budget), 0);
            const totalActual = vrData.reduce((s, c) => s + (isYTD ? c.ytd_actual : c.month_actual), 0);
            const totalVariance = totalActual - totalBudget;
            const totalIsOver = totalVariance > 0;
            const totalYtdVariance = vrData.reduce((s, c) => s + c.ytd_variance, 0);
            const totalYtdIsOver = totalYtdVariance > 0;
            const totalAnnualBudget = vrData.reduce((s, c) => s + (c.annual_budget || 0), 0);
            const totalMpyForecast = vrData.reduce((s, c) => s + (c.mpy_forecast || c.forecast_total || c.annual_budget || 0), 0);
            const totalYtdActual = vrData.reduce((s, c) => s + c.ytd_actual, 0);
            const totalYtdBudget = vrData.reduce((s, c) => s + c.ytd_budget, 0);
            const totalYtdUtilPct = totalAnnualBudget > 0 ? (totalYtdActual / totalAnnualBudget) * 100 : 0;
            const totalProjected = totalMpyForecast;

            // Chart data: per-BU category breakdown (one chart per BU)
            const buCatCharts = buGroups.map(g => {
              const catData = VR_CATS.map(cat => {
                const budget = g.ccs.reduce((s, cc) => {
                  const cb = cc.category_breakdown?.find(c => c.category === cat);
                  return s + (cb ? (isYTD ? cb.ytd_budget : cb.month_budget) : 0);
                }, 0);
                const actual = g.ccs.reduce((s, cc) => {
                  const cb = cc.category_breakdown?.find(c => c.category === cat);
                  return s + (cb ? (isYTD ? cb.ytd_actual : cb.month_actual) : 0);
                }, 0);
                return { name: cat.charAt(0) + cat.slice(1).toLowerCase(), Budget: budget, Actual: actual };
              });
              return { bu: g.bu, data: catData };
            });

            const fmtChartAxis = (v: number) => {
              if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
              if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1_000)}k`;
              return `$${v}`;
            };

            const renderBarLabel = (props: any) => {
              const { x, y, width, height, value } = props;
              if (!value) return null;
              const label = Math.abs(value) >= 1_000_000
                ? `$${(value / 1_000_000).toFixed(1)}M`
                : Math.abs(value) >= 1_000
                ? `$${Math.round(value / 1_000)}k`
                : `$${value}`;
              return (
                <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={9} fill="#1e293b" fontWeight={700} className="vr-bar-label"
                  stroke="#ffffff" strokeWidth={3} paintOrder="stroke">
                  {label}
                </text>
              );
            };

            const totalMPYVar = totalProjected - totalAnnualBudget;
            const totalMPYIsOver = totalMPYVar > 0;

            const buAggs = buGroups.map(g => {
              const bBud = g.ccs.reduce((s, c) => s + (isYTD ? c.ytd_budget : c.month_budget), 0);
              const bAct = g.ccs.reduce((s, c) => s + (isYTD ? c.ytd_actual : c.month_actual), 0);
              const bVar = bAct - bBud;
              const bYtdAct = g.ccs.reduce((s, c) => s + c.ytd_actual, 0);
              const bYtdBud = g.ccs.reduce((s, c) => s + c.ytd_budget, 0);
              const bYtdVar = bYtdAct - bYtdBud;
              const bAB = g.ccs.reduce((s, c) => s + (c.annual_budget || 0), 0);
              const bMPY = g.ccs.reduce((s, c) => s + (c.mpy_forecast || c.forecast_total || c.annual_budget || 0), 0);
              const bMPYVar = bMPY - bAB;
              return { ...g, bBud, bAct, bVar, bYtdAct, bYtdVar, bMPYVar };
            });

            const headlines: string[] = [];
            const varDir = (v: number) => v > 0 ? "over" : "under";
            const absFmt = (v: number) => fmtMoney(Math.abs(v));
            const favUnfav = (v: number) => v > 0 ? "unfavorable" : "favorable";
            headlines.push(`Consolidated spend finished ${absFmt(totalVariance)} ${varDir(totalVariance)} ${periodLabel} ${refWord.toLowerCase()}`
              + (buAggs.length > 1 ? `, driven by ${buAggs.sort((a, b) => Math.abs(b.bVar) - Math.abs(a.bVar))[0].bu}` : ""));
            buAggs.forEach(b => {
              headlines.push(`${b.bu} came in ${absFmt(b.bVar)} ${varDir(b.bVar)} ${refWord.toLowerCase()} and is ${absFmt(b.bYtdVar)} ${favUnfav(b.bYtdVar)} YTD`);
            });
            headlines.push(`MPY projection of ${fmtMoney(totalProjected)} vs Budget of ${fmtMoney(totalAnnualBudget)} is ${absFmt(totalMPYVar)} ${favUnfav(totalMPYVar)}`);

            const varCls = (v: number) => v > 0 ? "vr-val--red" : v < 0 ? "vr-val--green" : "";
            const kpiHighlight = (v: number) => v > 0 ? "vr-kpi-box--red" : "vr-kpi-box--green";
            const kpiLabelCls  = (v: number) => v > 0 ? "vr-kpi-label--red" : "vr-kpi-label--green";
            const kpiValueCls  = (v: number) => v > 0 ? "vr-kpi-value--red" : "vr-kpi-value--green";

            const renderKpiStrip = (budget: number, actual: number, periodVar: number, ytdActuals: number, ytdVar: number, mpyVar: number) => (
              <div className="vr-kpi-strip">
                <div className="vr-kpi-box">
                  <span className="vr-kpi-label">{periodLabel} {refWord}</span>
                  <span className="vr-kpi-value">{fmtMoney(budget)}</span>
                </div>
                <div className="vr-kpi-box">
                  <span className="vr-kpi-label">{periodLabel} Actual</span>
                  <span className="vr-kpi-value">{fmtMoney(actual)}</span>
                </div>
                <div className={`vr-kpi-box ${kpiHighlight(periodVar)}`}>
                  <span className={`vr-kpi-label ${kpiLabelCls(periodVar)}`}>{periodLabel} Variance</span>
                  <span className={`vr-kpi-value ${kpiValueCls(periodVar)}`}>{fmtVar(periodVar)}</span>
                </div>
                <div className="vr-kpi-box">
                  <span className="vr-kpi-label">YTD Actuals</span>
                  <span className="vr-kpi-value">{fmtMoney(ytdActuals)}</span>
                </div>
                <div className={`vr-kpi-box ${kpiHighlight(ytdVar)}`}>
                  <span className={`vr-kpi-label ${kpiLabelCls(ytdVar)}`}>YTD Variance</span>
                  <span className={`vr-kpi-value ${kpiValueCls(ytdVar)}`}>{fmtVar(ytdVar)}</span>
                </div>
                <div className={`vr-kpi-box ${kpiHighlight(mpyVar)}`}>
                  <span className={`vr-kpi-label ${kpiLabelCls(mpyVar)}`}>MPY Variance</span>
                  <span className={`vr-kpi-value ${kpiValueCls(mpyVar)}`}>{fmtVar(mpyVar)}</span>
                </div>
              </div>
            );

            const renderFooter = (mpyLine?: string) => (
              <>
                {mpyLine && <div className="vr-mpy-line">{mpyLine}</div>}
                <div className="vr-footer">
                  <span className="vr-footer-copy">&copy; {new Date().getFullYear()} Daikin Applied</span>
                  <span className="vr-footer-logo"><img src="/daikin-logo.png" alt="Daikin" /></span>
                </div>
              </>
            );

            return (
              <div className="vr-report">
                {/* ═══════ PAGE 1: VP Summary ═══════ */}
                <div className="vr-page vr-vp-summary">
                  <div className="vr-title-bar">
                    <h1>{currentFY?.toUpperCase()} {periodLabel} — Variance Summary</h1>
                  </div>

                  {renderKpiStrip(totalBudget, totalActual, totalVariance, totalYtdActual, totalYtdVariance, totalMPYVar)}

                  <div className="vr-section-bar">Variance by Business Unit</div>
                  <table className="vr-data-tbl">
                    <thead>
                      <tr>
                        <th>Business Unit</th>
                        <th>{periodLabel} {refWord}</th>
                        <th>{periodLabel} Actual</th>
                        <th>{periodLabel} Variance</th>
                        <th>YTD Actuals</th>
                        <th>YTD Variance</th>
                        <th>MPY Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buAggs.map(b => (
                        <tr key={b.bu}>
                          <td>{b.bu}</td>
                          <td>{fmtMoney(b.bBud)}</td>
                          <td>{fmtMoney(b.bAct)}</td>
                          <td className={varCls(b.bVar)}>{fmtVar(b.bVar)}</td>
                          <td>{fmtMoney(b.bYtdAct)}</td>
                          <td className={varCls(b.bYtdVar)}>{fmtVar(b.bYtdVar)}</td>
                          <td className={varCls(b.bMPYVar)}>{fmtVar(b.bMPYVar)}</td>
                        </tr>
                      ))}
                      <tr className="vr-total-row">
                        <td>Total</td>
                        <td>{fmtMoney(totalBudget)}</td>
                        <td>{fmtMoney(totalActual)}</td>
                        <td className={varCls(totalVariance)}>{fmtVar(totalVariance)}</td>
                        <td>{fmtMoney(totalYtdActual)}</td>
                        <td className={varCls(totalYtdVariance)}>{fmtVar(totalYtdVariance)}</td>
                        <td className={varCls(totalMPYVar)}>{fmtVar(totalMPYVar)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="vr-section-bar">Headlines — {periodLabel} {currentFY?.toUpperCase()}</div>
                  <div className="vr-headlines">
                    <ul>{headlines.map((h, i) => <li key={i}>{h}</li>)}</ul>
                  </div>

                  {renderFooter()}
                </div>

                {/* ═══════ Per-CC Detail Pages ═══════ */}
                {buGroups.map(group => group.ccs.map(cc => {
                  const budget   = isYTD ? cc.ytd_budget   : cc.month_budget;
                  const actual   = isYTD ? cc.ytd_actual   : cc.month_actual;
                  const variance = isYTD ? cc.ytd_variance  : cc.month_variance;
                  const ccAnnBud = cc.annual_budget || 0;
                  const ccProjected = cc.mpy_forecast || cc.forecast_total || ccAnnBud;
                  const ccMPYVar = ccProjected - ccAnnBud;
                  const ccActions = vrActionPlans[cc.cost_center] || [];
                  const openActions = ccActions.filter(a => a.status === "OPEN");

                  const unfav = cc.top_unfavorable.filter(a => Math.abs(isYTD ? a.ytd_variance : a.month_variance) >= 1000);
                  const fav   = cc.top_favorable.filter(a => Math.abs(isYTD ? a.ytd_variance : a.month_variance) >= 1000);

                  return (
                    <div key={cc.cost_center} className="vr-page vr-cc-detail">
                      <div className="vr-title-bar">
                        <h1>{cc.cost_center} {cc.cost_center_name}</h1>
                      </div>

                      {renderKpiStrip(budget, actual, variance, cc.ytd_actual, cc.ytd_variance, ccMPYVar)}

                      <div className="vr-over-under">
                        <div className="vr-ou-half">
                          <div className="vr-ou-hdr vr-ou-hdr--over">Over {refWord} ({unfav.length})</div>
                          {unfav.length === 0 ? (
                            <div className="vr-ou-empty">All accounts within {refWord.toLowerCase()}</div>
                          ) : (
                            <table className="vr-ou-tbl">
                              <thead>
                                <tr>
                                  <th>Account</th>
                                  <th>{refWord}</th>
                                  <th>Actual</th>
                                  <th>Variance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {unfav.map(a => {
                                  const aBud = isYTD ? a.ytd_budget : a.month_budget;
                                  const aAct = isYTD ? a.ytd_actual : a.month_actual;
                                  const aVar = isYTD ? a.ytd_variance : a.month_variance;
                                  return (
                                    <tr key={a.account}>
                                      <td>
                                        <div className="vr-ou-acct-name">{a.account}</div>
                                        <div className="vr-ou-acct-cat">{a.category}</div>
                                      </td>
                                      <td>{fmtMoney(aBud)}</td>
                                      <td>{fmtMoney(aAct)}</td>
                                      <td className="vr-val--red">{fmtVar(aVar)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>

                        <div className="vr-ou-half">
                          <div className="vr-ou-hdr vr-ou-hdr--under">Under {refWord} ({fav.length})</div>
                          {fav.length === 0 ? (
                            <div className="vr-ou-empty">No accounts under {refWord.toLowerCase()}</div>
                          ) : (
                            <table className="vr-ou-tbl">
                              <thead>
                                <tr>
                                  <th>Account</th>
                                  <th>{refWord}</th>
                                  <th>Actual</th>
                                  <th>Variance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {fav.map(a => {
                                  const aBud = isYTD ? a.ytd_budget : a.month_budget;
                                  const aAct = isYTD ? a.ytd_actual : a.month_actual;
                                  const aVar = isYTD ? a.ytd_variance : a.month_variance;
                                  return (
                                    <tr key={a.account}>
                                      <td>
                                        <div className="vr-ou-acct-name">{a.account}</div>
                                        <div className="vr-ou-acct-cat">{a.category}</div>
                                      </td>
                                      <td>{fmtMoney(aBud)}</td>
                                      <td>{fmtMoney(aAct)}</td>
                                      <td className="vr-val--green">{fmtVar(aVar)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>

                      <div className="vr-ap-section">
                        <div className="vr-ap-hdr">Action Plan{openActions.length > 0 ? ` — ${openActions.length} Open` : ""}</div>
                        {ccActions.length > 0 ? (
                          <table className="vr-ap-tbl">
                            <thead>
                              <tr>
                                <th>Status</th>
                                <th>Issue</th>
                                <th>Action</th>
                                <th>Target</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...ccActions].sort((a, b) => {
                                if (!a.closeout_milestone && !b.closeout_milestone) return 0;
                                if (!a.closeout_milestone) return 1;
                                if (!b.closeout_milestone) return -1;
                                return a.closeout_milestone.localeCompare(b.closeout_milestone);
                              }).map((ap) => (
                                <tr key={ap.id}>
                                  <td><span className={`vr-ap-status vr-ap-status--${ap.status.toLowerCase()}`}>{ap.status}</span></td>
                                  <td>{ap.issue_text || ""}</td>
                                  <td>{ap.action_text || "(no description)"}</td>
                                  <td className="vr-ap-td-target">{ap.closeout_milestone ? new Date(ap.closeout_milestone + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="vr-ap-empty-msg">No action items recorded</div>
                        )}
                      </div>

                      {renderFooter(`MPY ${fmtMoney(ccProjected)} vs Budget ${fmtMoney(ccAnnBud)}`)}
                    </div>
                  );
                }))}
              </div>
            );
          })()}

          {/* Actions Report Modal */}
          {vrArOpen && (
            <div className="vr-ar-overlay" onClick={() => setVrArOpen(false)}>
              <div className="vr-ar-modal" onClick={e => e.stopPropagation()}>
                {/* Header with filter */}
                <div className="vr-ar-header">
                  <span className="vr-ar-title">Actions Report</span>
                  <span className="vr-ar-fy">{currentFY}{vrBU ? ` — ${vrBU}` : ""}</span>
                  <div className="vr-ar-header-right">
                    {vrArData && (
                      <div className="vr-ar-filters">
                        {(["ALL", "OPEN", "CLOSED"] as const).map(f => (
                          <button key={f} className={`vr-ar-pill ${vrArFilter === f ? "vr-ar-pill--active" : ""}`}
                            onClick={() => setVrArFilter(f)}>
                            {f === "ALL" ? "All" : f === "OPEN" ? "Open" : "Closed"}
                          </button>
                        ))}
                      </div>
                    )}
                    <button className="vr-ar-close" onClick={() => setVrArOpen(false)}>
                      <span className="material-icons">close</span>
                    </button>
                  </div>
                </div>

                {vrArLoading ? (
                  <div className="vr-ar-loading">Loading actions report...</div>
                ) : vrArData ? (() => {
                  const { items } = vrArData;
                  const filtered = vrArFilter === "ALL" ? items : items.filter(i => i.status === vrArFilter);
                  const sorted = sortArItems(filtered);

                  const arSortIcon = (col: typeof vrArSortCol) =>
                    vrArSortCol === col ? (vrArSortDir === "asc" ? "arrow_upward" : "arrow_downward") : "unfold_more";

                  return (
                    <>
                      {/* Table body */}
                      <div className="vr-ar-body">
                        {sorted.length === 0 ? (
                          <div className="vr-ar-empty">No actions match the current filter</div>
                        ) : (
                          <table className="vr-ar-table">
                            <thead>
                              <tr>
                                <th className="vr-ar-th-sort" onClick={() => handleArToggleSort("month")}>
                                  Month <span className={`material-icons vr-ar-sort-icon ${vrArSortCol === "month" ? "vr-ar-sort-icon--active" : ""}`}>{arSortIcon("month")}</span>
                                </th>
                                <th>CC</th>
                                <th>CC Name</th>
                                <th className="vr-ar-th-sort" onClick={() => handleArToggleSort("owner")}>
                                  Owner <span className={`material-icons vr-ar-sort-icon ${vrArSortCol === "owner" ? "vr-ar-sort-icon--active" : ""}`}>{arSortIcon("owner")}</span>
                                </th>
                                <th>Issue</th>
                                <th>Action</th>
                                <th className="vr-ar-th-sort" onClick={() => handleArToggleSort("category")}>
                                  Category <span className={`material-icons vr-ar-sort-icon ${vrArSortCol === "category" ? "vr-ar-sort-icon--active" : ""}`}>{arSortIcon("category")}</span>
                                </th>
                                <th className="vr-ar-th-sort" onClick={() => handleArToggleSort("closeout")}>
                                  Closeout Target <span className={`material-icons vr-ar-sort-icon ${vrArSortCol === "closeout" ? "vr-ar-sort-icon--active" : ""}`}>{arSortIcon("closeout")}</span>
                                </th>
                                <th className="vr-ar-th-sort" onClick={() => handleArToggleSort("status")}>
                                  Status <span className={`material-icons vr-ar-sort-icon ${vrArSortCol === "status" ? "vr-ar-sort-icon--active" : ""}`}>{arSortIcon("status")}</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sorted.map(item => (
                                <tr key={item.id} className={item.status === "CLOSED" ? "vr-ar-row--closed" : ""}>
                                  <td className="vr-ar-td-month">{item.month}</td>
                                  <td className="vr-ar-td-cc">{item.cost_center}</td>
                                  <td className="vr-ar-td-ccname">{item.cost_center_name}</td>
                                  <td className="vr-ar-td-owner">{item.cost_center_owner || ""}</td>
                                  <td className="vr-ar-td-issue">{item.issue_text || ""}</td>
                                  <td className="vr-ar-td-action">{item.action_text || "(no description)"}</td>
                                  <td className="vr-ar-td-category">{item.category || ""}</td>
                                  <td className="vr-ar-td-closeout">{item.closeout_milestone ? new Date(item.closeout_milestone + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</td>
                                  <td className="vr-ar-td-status">
                                    <span className={`vr-ar-badge vr-ar-badge--${item.status.toLowerCase()}`}>{item.status}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="vr-ar-footer">
                        <span className="vr-ar-count">{sorted.length} action{sorted.length !== 1 ? "s" : ""}</span>
                        <button className="admin-btn" onClick={handleArCopy}>
                          <span className="material-icons">{vrArCopied ? "check" : "content_copy"}</span>
                          {vrArCopied ? "Copied!" : "Copy to Clipboard"}
                        </button>
                      </div>
                    </>
                  );
                })() : (
                  <div className="vr-ar-loading">No data available</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Forecast Comparison ─────────────────────────────────────── */}
      {activeTab === "buforecast" && (
        <div className="admin-section">
          {/* ── Comparison Selector ── */}
          <div className="fc-toolbar">
            <div className="fc-toolbar-selectors">
              <div className="fc-dim-group">
                <span className="fc-dim-group-label">Base</span>
                <select className="fc-dim-select" value={fcDimA}
                  onChange={e => { const d = e.target.value; setFcDimA(d); setFcSelectedCCs([]); doLoadForecast(d, fcDimB, fcBU, [], fcFYA, undefined); }}>
                  {ALL_DIMS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="fc-fy-select" value={fcFYA}
                  onChange={e => { const fy = e.target.value; setFcFYA(fy); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, fcBU, [], fy, undefined); }}>
                  {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                </select>
              </div>
              <span className="fc-vs-badge">vs</span>
              <div className="fc-dim-group">
                <span className="fc-dim-group-label">Compare</span>
                <select className="fc-dim-select" value={fcDimB}
                  onChange={e => { const d = e.target.value; setFcDimB(d); setFcSelectedCCs([]); doLoadForecast(fcDimA, d, fcBU, [], undefined, fcFYB); }}>
                  {ALL_DIMS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className="fc-fy-select" value={fcFYB}
                  onChange={e => { const fy = e.target.value; setFcFYB(fy); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, fcBU, [], undefined, fy); }}>
                  {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                </select>
              </div>
            </div>
            <div className="fc-toolbar-actions">
              <select className="fc-bu-select" value={fcBU}
                onChange={e => { const bu = e.target.value; setFcBU(bu); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, bu, []); }}>
                <option value="">All Business Units</option>
                {fcBusinessUnits.map(bu => <option key={bu} value={bu}>{bu}</option>)}
              </select>
              <button className="fc-refresh-btn" onClick={() => doLoadForecast(fcDimA, fcDimB, fcBU, fcSelectedCCs)}>
                <span className="material-icons">refresh</span>
              </button>
            </div>
          </div>

          {/* ── CC filter pills (visible when a BU is selected) ── */}
          {fcBU && ccsByBU[fcBU] && ccsByBU[fcBU].length > 0 && (
            <div className="admin-cc-filter-bar">
              <span className="admin-cc-filter-label">Cost Centers:</span>
              <button
                className={`admin-cc-pill${fcSelectedCCs.length === 0 ? " admin-cc-pill--active" : ""}`}
                onClick={() => { setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, fcBU, []); }}
              >All</button>
              {ccsByBU[fcBU].map(cc => (
                <button
                  key={cc.cost_center_number}
                  className={`admin-cc-pill${fcSelectedCCs.includes(cc.cost_center_number) ? " admin-cc-pill--active" : ""}`}
                  title={cc.cost_center_name}
                  onClick={() => {
                    const next = fcSelectedCCs.includes(cc.cost_center_number)
                      ? fcSelectedCCs.filter(c => c !== cc.cost_center_number)
                      : [...fcSelectedCCs, cc.cost_center_number];
                    setFcSelectedCCs(next);
                    doLoadForecast(fcDimA, fcDimB, fcBU, next);
                  }}
                >{cc.cost_center_number}</button>
              ))}
            </div>
          )}

          {fcLoading ? (
            <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>Loading…</div>
          ) : fcData.length === 0 ? (
            <div style={{ padding: "24px", color: "#9ca3af", fontSize: "13px" }}>
              No budget data found. Upload budgets for both dimensions to compare.
            </div>
          ) : (
            <>
              {/* ── KPI Summary Cards ── */}
              {(() => {
                const cats = fcData.filter(cat => !EXCLUDED_CATS.has(cat.category));
                const totalA = cats.reduce((s, c) => s + c.dim_a, 0);
                const totalB = cats.reduce((s, c) => s + c.dim_b, 0);
                const totalChg = totalB - totalA;
                const totalPct = totalA > 0 ? (totalChg / totalA) * 100 : 0;
                const dimALabel = buildDimName(fcDimA, fcFYA);
                const dimBLabel = buildDimName(fcDimB, fcFYB);
                return (
                  <div className="fc-summary-strip">
                    <div className="fc-summary-cell">
                      <span className="fc-summary-label">Base</span>
                      <span className="fc-summary-value">{fmtMoney(totalA)}</span>
                    </div>
                    <span className="fc-summary-arrow material-icons">arrow_forward</span>
                    <div className="fc-summary-cell">
                      <span className="fc-summary-label">Compare</span>
                      <span className="fc-summary-value">{fmtMoney(totalB)}</span>
                    </div>
                    <div className="fc-summary-divider" />
                    <div className={`fc-summary-cell ${totalChg > 0 ? "fc-summary-cell--unfav" : totalChg < 0 ? "fc-summary-cell--fav" : ""}`}>
                      <span className="fc-summary-label">Net Change</span>
                      <span className="fc-summary-value">{fmtVar(totalChg)}</span>
                    </div>
                    <div className={`fc-summary-cell ${totalChg > 0 ? "fc-summary-cell--unfav" : totalChg < 0 ? "fc-summary-cell--fav" : ""}`}>
                      <span className="fc-summary-label">Change %</span>
                      <span className="fc-summary-value">{totalA > 0 ? `${totalPct > 0 ? "+" : ""}${totalPct.toFixed(1)}%` : "—"}</span>
                    </div>
                  </div>
                );
              })()}

              {/* ── BU Summary table ── */}
              {!fcBU && fcBUSummary.length > 1 && (() => {
                const maxDim = Math.max(...fcBUSummary.map(r => Math.max(r.dim_a, r.dim_b)), 1);
                return (
                  <div className="admin-dash-bu-summary">
                    <table className="admin-dash-table">
                      <thead>
                        <tr>
                          <th>Business Unit</th>
                          <th className="num">{buildDimName(fcDimA, fcFYA)}</th>
                          <th className="num">{buildDimName(fcDimB, fcFYB)}</th>
                          <th className="num">Change</th>
                          <th className="num">Change %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fcBUSummary.map(row => {
                          const barA = Math.round((row.dim_a / maxDim) * 100);
                          const barB = Math.round((row.dim_b / maxDim) * 100);
                          return (
                            <tr key={row.business_unit} className="admin-dash-cc-row"
                              onClick={() => { setFcBU(row.business_unit); setFcSelectedCCs([]); doLoadForecast(fcDimA, fcDimB, row.business_unit, []); }}
                              title={`Click to drill into ${row.business_unit}`}>
                              <td style={{ fontWeight: 600, fontSize: "13px" }}>
                                <div>{row.business_unit}</div>
                                <div className="fc-bu-inline-bars">
                                  <div className="fc-bu-bar fc-bu-bar--a" style={{ width: `${barA}%` }} />
                                  <div className="fc-bu-bar fc-bu-bar--b" style={{ width: `${barB}%` }} />
                                </div>
                              </td>
                              <td className="num" style={{ fontSize: "13px" }}>{fmtMoney(row.dim_a)}</td>
                              <td className="num" style={{ fontSize: "13px", fontWeight: 600 }}>{fmtMoney(row.dim_b)}</td>
                              <td className="num">
                                <span className={row.change > 0 ? "admin-dash-var-over" : row.change < 0 ? "admin-dash-var-under" : ""}>
                                  {fmtVar(row.change)}
                                </span>
                              </td>
                              <td className="num" style={{ fontSize: "13px", color: "#6b7280" }}>
                                {row.dim_a > 0 ? `${((row.change / row.dim_a) * 100).toFixed(1)}%` : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ fontWeight: 700, borderTop: "2px solid #e5e7eb" }}>
                          <td style={{ fontSize: "13px" }}>Total</td>
                          <td className="num" style={{ fontSize: "13px" }}>{fmtMoney(fcBUSummary.reduce((s,r)=>s+r.dim_a,0))}</td>
                          <td className="num" style={{ fontSize: "13px" }}>{fmtMoney(fcBUSummary.reduce((s,r)=>s+r.dim_b,0))}</td>
                          <td className="num">{(() => { const v=fcBUSummary.reduce((s,r)=>s+r.change,0); return <span className={v>0?"admin-dash-var-over":v<0?"admin-dash-var-under":""}>{fmtVar(v)}</span>; })()}</td>
                          <td className="num" style={{ fontSize: "13px", color: "#6b7280" }}>
                            {(() => { const a=fcBUSummary.reduce((s,r)=>s+r.dim_a,0); const c=fcBUSummary.reduce((s,r)=>s+r.change,0); return a>0?`${((c/a)*100).toFixed(1)}%`:"—"; })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })()}

              {/* ── Category cards (2×3 grid) ── */}
              <div className="fc-cat-grid">
                {fcData.filter(cat => !EXCLUDED_CATS.has(cat.category)).map(cat => {
                  const color   = CAT_COLOR(cat.category);
                  const chgPct  = cat.dim_a > 0 ? (cat.change / cat.dim_a) * 100 : 0;
                  const isActive = fcDrillCategory === cat.category;
                  return (
                    <div
                      key={cat.category}
                      className={`dash-cat-card${isActive ? " dash-cat-card--active" : ""}`}
                      style={{ borderTopColor: color, cursor: "pointer" }}
                      onClick={() => setFcDrillCategory(isActive ? null : cat.category)}
                      title={`Click to focus ${cat.category}`}
                    >
                      <div className="dash-cat-name" style={{ color }}>{cat.category}</div>
                      <div className="dash-cat-rows">
                        <div className="dash-cat-row">
                          <span className="dash-cat-row-label">{buildDimName(fcDimA, fcFYA)}</span>
                          <span className="dash-cat-row-val">{fmtMoney(cat.dim_a)}</span>
                        </div>
                        <div className="dash-cat-row">
                          <span className="dash-cat-row-label">{buildDimName(fcDimB, fcFYB)}</span>
                          <span className="dash-cat-row-val">{fmtMoney(cat.dim_b)}</span>
                        </div>
                        <div className={`dash-cat-row dash-cat-row--var ${cat.change <= 0 ? "dash-fav" : "dash-unfav"}`}>
                          <span className="dash-cat-row-label">Change</span>
                          <span className="dash-cat-row-val">{fmtMoney(Math.abs(cat.change))}</span>
                        </div>
                      </div>
                      <div className="dash-progress-wrap">
                        <div className="dash-progress-fill" style={{ transform: `scaleX(${Math.min(100, Math.abs(chgPct)) / 100})`, background: cat.change > 0 ? "#dc2626" : color }} />
                      </div>
                      <div className="dash-progress-label" style={{ color: cat.change > 0 ? "#dc2626" : cat.change < 0 ? "#15803d" : "#4b5563" }}>
                        {cat.dim_a > 0 ? `${Math.abs(chgPct).toFixed(1)}% change` : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── Per-category sections ── */}
              {fcData.filter(cat => !EXCLUDED_CATS.has(cat.category) && (!fcDrillCategory || fcDrillCategory === cat.category)).map(cat => {
                const color     = CAT_COLOR(cat.category);
                const increased = cat.top_increased.filter(r => r.change > 0);
                const decreased = cat.top_decreased.filter(r => r.change < 0);
                const dimALabel = buildDimName(fcDimA, fcFYA);
                const dimBLabel = buildDimName(fcDimB, fcFYB);
                return (
                  <div key={cat.category} className="fc-category-frame" style={{ borderTopColor: color }}>
                    <div className="fc-category-header">
                      <span className="fc-category-swatch" style={{ background: color }} />
                      <span className="fc-category-name">{cat.category}</span>
                      <span className="fc-category-meta">
                        {fmtMoney(cat.dim_a)} → {fmtMoney(cat.dim_b)}
                        <span className="fc-category-meta-sep">·</span>
                        <span className={cat.change > 0 ? "dash-unfav" : cat.change < 0 ? "dash-fav" : ""}>{fmtVar(cat.change)}</span>
                      </span>
                    </div>
                    <div className="dash-tables-row">
                      {/* Increased */}
                      <div className="dash-table-card dash-table-card--unfav">
                        <div className="dash-table-head">
                          <span className="material-icons">trending_up</span>
                          Top 5 Increased ({dimBLabel})
                        </div>
                        {increased.length === 0 ? (
                          <div style={{ padding: "16px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>No cost centers increased</div>
                        ) : (
                          <table className="dash-tbl">
                            <thead><tr>
                              <th className="dash-th-left">CC</th>
                              <th className="dash-th-left">Name</th>
                              <th>{dimALabel}</th><th>{dimBLabel}</th><th>↑ Change</th>
                            </tr></thead>
                            <tbody>
                              {increased.map((r, i) => (
                                <tr key={r.cost_center} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                                  <td><span className="admin-cc-num" style={{ fontSize: "13px" }}>{r.cost_center}</span></td>
                                  <td className="dash-td-account">{r.cost_center_name}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_a)}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_b)}</td>
                                  <td className="dash-td-num dash-unfav">{fmtMoney(r.change)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                      {/* Decreased */}
                      <div className="dash-table-card dash-table-card--fav">
                        <div className="dash-table-head">
                          <span className="material-icons">trending_down</span>
                          Top 5 Decreased ({dimBLabel})
                        </div>
                        {decreased.length === 0 ? (
                          <div style={{ padding: "16px", color: "#9ca3af", fontSize: "13px", textAlign: "center" }}>No cost centers decreased</div>
                        ) : (
                          <table className="dash-tbl">
                            <thead><tr>
                              <th className="dash-th-left">CC</th>
                              <th className="dash-th-left">Name</th>
                              <th>{dimALabel}</th><th>{dimBLabel}</th><th>↓ Change</th>
                            </tr></thead>
                            <tbody>
                              {decreased.map((r, i) => (
                                <tr key={r.cost_center} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                                  <td><span className="admin-cc-num" style={{ fontSize: "13px" }}>{r.cost_center}</span></td>
                                  <td className="dash-td-account">{r.cost_center_name}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_a)}</td>
                                  <td className="dash-td-num">{fmtMoney(r.dim_b)}</td>
                                  <td className="dash-td-num dash-fav">{fmtMoney(Math.abs(r.change))}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>

                    {/* ── Bar chart — cost center breakdown (vertical) ── */}
                    {(() => {
                      const allCCs = (cat.all_ccs || []);
                      if (allCCs.length === 0) return null;
                      const maxVal = Math.max(...allCCs.flatMap(r => [r.dim_a, r.dim_b]), 1);
                      const BAR_H = 130;
                      return (
                        <div className="fc-bar-chart">
                          <div className="fc-bar-chart-title">
                            <span className="material-icons">bar_chart</span>
                            Cost Center Breakdown
                            <span className="fc-bar-legend">
                              <span className="fc-bar-legend-swatch" style={{ background: color }} />{fcDimA}
                              <span className="fc-bar-legend-swatch" style={{ background: '#94a3b8' }} />{fcDimB}
                            </span>
                          </div>
                          <div className="fc-bar-cols">
                            {allCCs.map(r => {
                              const hA = Math.max(Math.round((r.dim_a / maxVal) * BAR_H), r.dim_a > 0 ? 2 : 0);
                              const hB = Math.max(Math.round((r.dim_b / maxVal) * BAR_H), r.dim_b > 0 ? 2 : 0);
                              return (
                                <div key={r.cost_center} className="fc-bar-col">
                                  <div className="fc-bar-col-bars" style={{ height: `${BAR_H}px` }}>
                                    <div className="fc-bar-v-wrap">
                                      <span className="fc-bar-v-val">{fmtMoney(r.dim_a)}</span>
                                      <div className="fc-bar-v" style={{ height: `${hA}px`, background: color }} />
                                    </div>
                                    <div className="fc-bar-v-wrap">
                                      <span className="fc-bar-v-val">{fmtMoney(r.dim_b)}</span>
                                      <div className="fc-bar-v" style={{ height: `${hB}px`, background: '#94a3b8' }} />
                                    </div>
                                  </div>
                                  <div className="fc-bar-col-label" title={r.cost_center_name || r.cost_center}>
                                    <span className="admin-cc-num" style={{ fontSize: "13px", display: "block" }}>{r.cost_center}</span>
                                    {r.cost_center_name || r.cost_center}
                                  </div>
                                  <div className={`fc-bar-col-change ${r.change > 0 ? 'dash-unfav' : r.change < 0 ? 'dash-fav' : ''}`}>
                                    {r.change !== 0 ? (r.change > 0 ? '+' : '') + fmtMoney(r.change) : '—'}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ── Tab: Data Upload ─────────────────────────────────────────────── */}
      {activeTab === "upload" && (
        <div className="upload-layout">
          {/* Sub-tab toggle */}
          <div className="upload-subtabs">
            <button
              className={`upload-subtab${uploadSubTab === "actuals" ? " upload-subtab--active" : ""}`}
              onClick={() => setUploadSubTab("actuals")}
            >
              <span className="material-icons">receipt_long</span>
              Upload Actuals
            </button>
            <button
              className={`upload-subtab${uploadSubTab === "budget" ? " upload-subtab--active" : ""}`}
              onClick={() => setUploadSubTab("budget")}
            >
              <span className="material-icons">table_chart</span>
              Upload Budget
            </button>
          </div>

          {/* ── Actuals sub-tab ── */}
          {uploadSubTab === "actuals" && (
            <div className="admin-section">
              <div className="admin-section-header">
                <span className="material-icons">receipt_long</span>
                <span className="admin-section-title">Upload Actuals</span>
                <span className="admin-section-subtitle">Corp BU · monthly actuals from finance report</span>
              </div>
              <div className="admin-section-body">
                <ActualsUploader fiscalYear={currentFY} onUploadSuccess={() => refreshInventory()} onBUChange={(bu) => { setActualsInvBU(bu); refreshInventory(bu); }} />
              </div>

              {/* ── Actuals inventory ── */}
              <div className="upload-inv-panel">
                <div className="upload-inv-title">
                  <span className="material-icons">inventory_2</span>
                  Actuals in Database
                  {uploadInvLoad && <span className="material-icons upload-inv-spin">sync</span>}
                </div>
                {actualsInv.length === 0 && !uploadInvLoad ? (
                  <p className="upload-inv-empty">No actuals uploaded yet.</p>
                ) : (() => {
                  const fyList = Array.from(new Set<string>(actualsInv.map(r => r.fiscal_year).filter(Boolean))).sort().reverse();
                  const activeFY = actualsInvFY || fyList[0] || "";
                  const fyRows = actualsInv.filter(r => r.fiscal_year === activeFY);
                  return (
                    <>
                      {/* FY pills */}
                      {fyList.length > 0 && (
                        <div className="upload-inv-fy-pills">
                          {fyList.map(fy => {
                            const count = actualsInv.filter(r => r.fiscal_year === fy).length;
                            return (
                              <button
                                key={fy}
                                className={`upload-inv-fy-pill${activeFY === fy ? " upload-inv-fy-pill--active" : ""}`}
                                onClick={() => setActualsInvFY(fy)}
                              >
                                {fy}
                                <span className="upload-inv-fy-pill-count">{count} mo</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* Month strip for selected FY */}
                      <div className="upload-inv-months">
                        {FISCAL_MONTHS.map(month => {
                          const row = fyRows.find(r => r.month === month);
                          const uploaded = !!row;
                          return (
                            <div key={month} className={`upload-inv-month ${uploaded ? "upload-inv-month--done" : "upload-inv-month--empty"}`}>
                              <div className="upload-inv-month-icon">
                                <span className="material-icons">{uploaded ? "check_circle" : "radio_button_unchecked"}</span>
                              </div>
                              <div className="upload-inv-month-name">{month.slice(0,3).toUpperCase()}</div>
                              {uploaded && (
                                <>
                                  <div className="upload-inv-month-cc">{row.cc_count} CCs</div>
                                  <div className="upload-inv-month-date">
                                    {new Date(row.last_upload).toLocaleDateString("en-US", { month:"short", day:"numeric" })}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── Budget sub-tab ── */}
          {uploadSubTab === "budget" && (
            <div className="admin-section">
              <div className="admin-section-header">
                <span className="material-icons">table_chart</span>
                <span className="admin-section-title">Upload Annual Budget</span>
                <span className="admin-section-subtitle">Per cost center · annual budget template</span>
              </div>
              <div className="admin-section-body">
                <BudgetUploader fiscalYear={currentFY} onUploadSuccess={() => refreshInventory()} />
              </div>

              {/* ── Budget inventory ── */}
              <div className="upload-inv-panel">
                <div className="upload-inv-title">
                  <span className="material-icons">inventory_2</span>
                  Budget in Database
                  {uploadInvLoad && <span className="material-icons upload-inv-spin">sync</span>}
                </div>
                {budgetInv.length === 0 && !uploadInvLoad ? (
                  <p className="upload-inv-empty">No budgets uploaded yet.</p>
                ) : (() => {
                  const fyDims = budgetInv.filter(d => /FY\d+/i.test(d.dimension));
                  const activeDim = budgetInv.find(d => d.dimension === uploadInvFY);
                  const allBUs = activeDim?.business_units ?? [];
                  const filterLower = budgetInvFilter.toLowerCase();
                  const totalCCs = allBUs.reduce((s, b) => s + b.ccs.length, 0);
                  const grandTotal = allBUs.reduce((s, b) => s + b.total, 0);

                  return (
                    <>
                      {/* FY pills */}
                      <div className="upload-inv-fy-pills">
                        {fyDims.map(d => {
                          const fyLabel = d.dimension.replace(/^Annual Budget\s*/i, "");
                          return (
                            <button
                              key={d.dimension}
                              className={`upload-inv-fy-pill${uploadInvFY === d.dimension ? " upload-inv-fy-pill--active" : ""}`}
                              onClick={() => setUploadInvFY(d.dimension)}
                            >
                              {fyLabel}
                              <span className="upload-inv-fy-pill-count">
                                {d.business_units.reduce((s, b) => s + b.ccs.length, 0)} CCs
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Summary + filter row */}
                      {allBUs.length > 0 && (
                        <div className="upload-inv-budget-toolbar">
                          <div className="upload-inv-budget-stats">
                            <span>{allBUs.length} business units</span>
                            <span className="upload-inv-budget-dot" />
                            <span>{totalCCs} cost centers</span>
                            <span className="upload-inv-budget-dot" />
                            <span className="upload-inv-budget-grand">{fmtMoney(grandTotal)}</span>
                          </div>
                          {totalCCs > 15 && (
                            <div className="upload-inv-budget-filter">
                              <span className="material-icons">search</span>
                              <input
                                type="text"
                                placeholder="Filter cost centers..."
                                value={budgetInvFilter}
                                onChange={e => setBudgetInvFilter(e.target.value)}
                                className="upload-inv-budget-filter-input"
                              />
                              {budgetInvFilter && (
                                <button className="upload-inv-budget-filter-clear" onClick={() => setBudgetInvFilter("")}>
                                  <span className="material-icons">close</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* BU table sections */}
                      {allBUs.map(bu => {
                        const buKey = bu.business_unit;
                        const isCollapsed = collapsedBUs.has(buKey);
                        const filteredCCs = filterLower
                          ? bu.ccs.filter(cc =>
                              cc.cost_center_number.toLowerCase().includes(filterLower) ||
                              cc.cost_center_name.toLowerCase().includes(filterLower)
                            )
                          : bu.ccs;

                        if (filterLower && filteredCCs.length === 0) return null;

                        return (
                          <div key={buKey} className={`upload-inv-bu${isCollapsed ? " upload-inv-bu--collapsed" : ""}`}>
                            <div
                              className="upload-inv-bu-header"
                              onClick={() => setCollapsedBUs(prev => {
                                const next = new Set(prev);
                                next.has(buKey) ? next.delete(buKey) : next.add(buKey);
                                return next;
                              })}
                            >
                              <span className="material-icons upload-inv-bu-chevron">expand_more</span>
                              <span className="upload-inv-bu-name">{bu.business_unit}</span>
                              <span className="upload-inv-bu-meta">
                                {filterLower ? `${filteredCCs.length} of ${bu.ccs.length}` : bu.ccs.length} cost centers
                              </span>
                              <span className="upload-inv-bu-total">{fmtMoney(bu.total)}</span>
                            </div>
                            <div className="upload-inv-cc-table-wrap">
                              <table className="upload-inv-cc-table">
                                <thead>
                                  <tr>
                                    <th className="upload-inv-cc-th-num">CC #</th>
                                    <th className="upload-inv-cc-th-name">Cost Center</th>
                                    <th className="upload-inv-cc-th-budget">Annual Budget</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredCCs.map(cc => (
                                    <tr key={cc.cost_center_number} className="upload-inv-cc-row">
                                      <td className="upload-inv-cc-td-num">{cc.cost_center_number}</td>
                                      <td className="upload-inv-cc-td-name">{cc.cost_center_name}</td>
                                      <td className="upload-inv-cc-td-budget">{fmtMoney(cc.total_budget)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

        </div>
      )}

      {/* ── Tab: Salary Config ───────────────────────────────────────────── */}
      {activeTab === "salary" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">payments</span>
            <span className="admin-section-title">Salary & BIP Configuration</span>
            <span className="admin-section-subtitle">Raise percentages & rate tables per fiscal year</span>
            <div className="sal-fy-picker">
              <span className="sal-fy-label">Fiscal Year</span>
              <select
                className="admin-select"
                value={salaryCfgFY}
                onChange={e => { const fy = e.target.value; setSalaryCfgFY(fy); loadSalaryCfg(fy); }}
              >
                {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
              </select>
            </div>
          </div>
          <div className="admin-section-body">
            {salaryCfgLoading ? (
              <p className="sal-loading">Loading…</p>
            ) : (
              <>
                {/* ── Raise % inputs ── */}
                <div className="sal-card">
                  <div className="sal-card-title">Raise % Settings</div>
                  <div className="sal-field-list">
                    <div className="sal-field-row">
                      <label className="sal-field-label" title="Applied to salary calculations for April, May, and June">
                        Raise % before Month 4 <span className="sal-field-hint">(Apr – Jun)</span>
                      </label>
                      <div className="sal-field-input">
                        <input
                          className="sal-pct-input"
                          type="number" min={0} max={100} step={0.01}
                          value={salaryRaiseBefore}
                          onChange={e => { setSalaryRaiseBefore(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-pct-sign">%</span>
                      </div>
                    </div>
                    <div className="sal-field-row">
                      <label className="sal-field-label" title="Applied to salary calculations from July through March">
                        Raise % from Month 4 onward <span className="sal-field-hint">(Jul – Mar)</span>
                      </label>
                      <div className="sal-field-input">
                        <input
                          className="sal-pct-input"
                          type="number" min={0} max={100} step={0.01}
                          value={salaryRaiseAfter}
                          onChange={e => { setSalaryRaiseAfter(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-pct-sign">%</span>
                      </div>
                    </div>
                    <div className="sal-field-row">
                      <label className="sal-field-label" title="Bonus Incentive Plan raise applied uniformly across all 12 months">
                        BIP Raise % <span className="sal-field-hint">(flat · all 12 months)</span>
                      </label>
                      <div className="sal-field-input">
                        <input
                          className="sal-pct-input"
                          type="number" min={0} max={100} step={0.01}
                          value={salaryBipRaise}
                          onChange={e => { setSalaryBipRaise(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-pct-sign">%</span>
                      </div>
                    </div>
                  </div>
                  <div className="sal-save-row">
                    <button className="admin-fy-btn" onClick={saveSalaryCfg}>Save</button>
                    {salaryCfgFeedback && (
                      <span className={`sal-feedback ${salaryCfgFeedback.ok ? "sal-feedback--ok" : "sal-feedback--err"}`}>
                        {salaryCfgFeedback.msg}
                      </span>
                    )}
                  </div>
                </div>

                {/* ── Rate Reference Table (editable) ── */}
                <div className="sal-rates-panel">
                  <div className="sal-card-title">Rate Reference Table</div>

                  {/* 60120 Payroll Tax — all 12 months in one row */}
                  <div className="sal-rate-section">
                    <div className="sal-rate-label" title="Percentage of (Salary + BIP) withheld for payroll taxes each month">
                      60120 – Payroll Tax (% of Salary + BIP)
                    </div>
                    <div className="sal-month-grid">
                      {PAYROLL_TAX_MONTHS.map(({ key, label }) => (
                        <div key={key} className="sal-month-cell" title={`Payroll tax rate for ${label}`}>
                          <div className="sal-month-label">{label}</div>
                          <div className="sal-month-input-wrap">
                            <input
                              className="sal-month-input"
                              type="number" min={0} max={100} step={0.01}
                              value={payrollTax[key] ?? ""}
                              onChange={e => { setPayrollTax(p => ({ ...p, [key]: e.target.value })); setSalaryCfgFeedback(null); }}
                            />
                            <span className="sal-month-sign">%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 60100 Insurance + 60130 Retirement */}
                  <div className="sal-benefits-row">
                    <div className="sal-benefit-card" title="Monthly cost per full-time employee for health and insurance benefits">
                      <div className="sal-rate-label">60100 – Insurance Benefits</div>
                      <div className="sal-benefit-input-wrap">
                        <span className="sal-benefit-prefix">$</span>
                        <input
                          className="sal-benefit-input"
                          type="number" min={0} step={0.01}
                          value={insurancePerHead}
                          onChange={e => { setInsurancePerHead(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-benefit-suffix">/ FT head / month</span>
                      </div>
                    </div>
                    <div className="sal-benefit-card" title="Retirement plan contribution as a percentage of Salary + BIP, applied to all 12 months">
                      <div className="sal-rate-label">60130 – Retirement Plans</div>
                      <div className="sal-benefit-input-wrap">
                        <input
                          className="sal-benefit-input"
                          type="number" min={0} max={100} step={0.0001}
                          value={retirementRate}
                          onChange={e => { setRetirementRate(e.target.value); setSalaryCfgFeedback(null); }}
                        />
                        <span className="sal-benefit-suffix">% of (Salary + BIP) · all 12 months</span>
                      </div>
                    </div>
                  </div>
                </div>

              </>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Finance View ────────────────────────────────────────────── */}
      {activeTab === "finance" && (
        <div className="admin-section">
          {financeLoading ? (
            <div className="fv-loading">
              <span className="material-icons fv-spin" style={{ fontSize: 20, marginBottom: 8 }}>sync</span>
              <div>Loading forecast data...</div>
            </div>
          ) : (() => {
            const unsorted = financeRows.filter(r => !financeFilterBU || r.business_unit === financeFilterBU);
            const visRows = [...unsorted].sort((a, b) => {
              const dir = fvSortDir === "asc" ? 1 : -1;
              switch (fvSortCol) {
                case "cc":       return a.cost_center_number.localeCompare(b.cost_center_number) * dir;
                case "annual":   return ((a.annual_budget_total ?? 0) - (b.annual_budget_total ?? 0)) * dir;
                case "ab":       return ((a.ref_forecast_total ?? 0) - (b.ref_forecast_total ?? 0)) * dir;
                case "forecast": return ((a.dimension_total ?? 0) - (b.dimension_total ?? 0)) * dir;
                case "variance": {
                  const refA = a.ref_forecast_total ?? a.annual_budget_total ?? 0;
                  const refB = b.ref_forecast_total ?? b.annual_budget_total ?? 0;
                  return (((a.dimension_total ?? 0) - refA) - ((b.dimension_total ?? 0) - refB)) * dir;
                }
                case "status": {
                  const rank = (s: string | null) => s === "submitted" ? 2 : s === "draft" ? 1 : 0;
                  return (rank(a.submission_status) - rank(b.submission_status)) * dir;
                }
                default: return 0;
              }
            });
            const sample  = unsorted[0];
            const abLabel  = sample?.annual_budget_dim  ?? "Annual Budget";
            const refLabel = sample?.ref_dimension      ?? "October Forecast";
            const curLabel = sample?.dimension          ?? "Forecast Total";
            const showRef  = refLabel !== abLabel;
            const showCur  = curLabel !== abLabel;  // hide duplicate when current dim IS Annual Budget
            // Dynamic previous/current labels & totals
            const prevLabel = showRef ? refLabel : abLabel;
            const totalAB       = visRows.reduce((s, r) => s + (r.annual_budget_total ?? 0), 0);
            const totalRef      = visRows.reduce((s, r) => s + (r.ref_forecast_total ?? 0), 0);
            const totalForecast = visRows.reduce((s, r) => s + (r.dimension_total ?? 0), 0);
            const totalPrev     = showRef ? totalRef : totalAB;
            const metCount  = visRows.filter(r => {
              const prev = showRef ? (r.ref_forecast_total ?? 0) : (r.annual_budget_total ?? 0);
              const av   = (r as any)[activeAVKey] ?? 0;
              return Math.round(r.dimension_total ?? 0) <= Math.round(prev + av);
            }).length;
            const overCount = visRows.length - metCount;
            const buList = Array.from(new Set(financeRows.map(r => r.business_unit).filter(Boolean))).sort();

            return (
              <>
                {/* KPI Summary */}
                {(() => {
                  const totalVariance = totalForecast - totalPrev;
                  const totalIsOver = totalVariance > 0;
                  const submittedCount = visRows.filter(r => r.submission_status === "submitted").length;
                  const draftCount = visRows.filter(r => r.submission_status === "draft").length;
                  const notStarted = visRows.length - submittedCount - draftCount;
                  return (
                    <div className="fv-kpi-row">
                      <div className="fv-kpi-card">
                        <span className="fv-kpi-label">Annual Budget</span>
                        <span className="fv-kpi-value">${(totalAB / 1_000_000).toFixed(1)}M</span>
                      </div>
                      {showRef && (
                        <div className="fv-kpi-card">
                          <span className="fv-kpi-label">{refLabel}</span>
                          <span className="fv-kpi-value">${(totalRef / 1_000_000).toFixed(1)}M</span>
                        </div>
                      )}
                      {showCur && (
                        <div className="fv-kpi-card">
                          <span className="fv-kpi-label">{curLabel}</span>
                          <span className="fv-kpi-value">${(totalForecast / 1_000_000).toFixed(1)}M</span>
                        </div>
                      )}
                      {showCur && (
                        <div className={`fv-kpi-card ${totalIsOver ? "fv-kpi-card--over" : "fv-kpi-card--met"}`}>
                          <span className="fv-kpi-label">Total Variance</span>
                          <span className="fv-kpi-value">
                            {totalIsOver
                              ? `($${(totalVariance / 1_000_000).toFixed(1)}M)`
                              : `$${(Math.abs(totalVariance) / 1_000_000).toFixed(1)}M`}
                          </span>
                          <span className="fv-kpi-helper">vs {prevLabel}</span>
                        </div>
                      )}
                      <div className="fv-kpi-card fv-kpi-card--met">
                        <span className="fv-kpi-label">On Target</span>
                        <span className="fv-kpi-value">{metCount}</span>
                      </div>
                      <div className="fv-kpi-card fv-kpi-card--over">
                        <span className="fv-kpi-label">Over Target</span>
                        <span className="fv-kpi-value">{overCount}</span>
                      </div>
                      <div className="fv-kpi-card">
                        <span className="fv-kpi-label">Submitted</span>
                        <span className="fv-kpi-value">{submittedCount} / {visRows.length}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Toolbar: BU filter + refresh */}
                <div className="fv-toolbar">
                  <div className="fv-toolbar-left">
                    {buList.length > 1 && (
                      <select
                        className="fv-bu-select"
                        value={financeFilterBU}
                        onChange={e => setFinanceFilterBU(e.target.value)}
                      >
                        <option value="">All Business Units</option>
                        {buList.map(bu => <option key={bu} value={bu}>{bu}</option>)}
                      </select>
                    )}
                    {sample?.dimension && (
                      <span className="fv-dimension-badge">{sample.dimension}</span>
                    )}
                  </div>
                  <div className="fv-toolbar-right" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button
                      onClick={() => { setFvAdminNoteDim(activeDimForAV); setFvAdminNoteDraft(fvAdminNotes[activeDimForAV] || ""); setFvAdminNoteOpen(true); }}
                      title="Open admin notes panel"
                      style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 14px", fontSize: 12, fontWeight: 600, border: "1px solid oklch(0.50 0.14 250)", background: "oklch(0.50 0.14 250)", color: "#fff", borderRadius: 6, cursor: "pointer" }}
                    >
                      <span className="material-icons" style={{ fontSize: 16 }}>sticky_note_2</span>
                      Admin Notes
                      {Date.now() < new Date("2026-10-29").getTime() && <span style={{ position: "absolute", top: -7, right: -10, background: "oklch(0.55 0.22 30)", color: "#fff", fontSize: 8, fontWeight: 800, padding: "2px 5px", borderRadius: 4, letterSpacing: "0.05em", lineHeight: 1, textTransform: "uppercase" }}>New</span>}
                    </button>
                    <button className="admin-btn admin-btn--ghost" onClick={loadFinanceTargets}>
                      <span className="material-icons" style={{ fontSize: 16 }}>refresh</span>
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Floating Admin Notes overlay */}
                {fvAdminNoteOpen && (() => {
                  const NOTE_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];
                  const switchDim = (d: string) => {
                    if (d === fvAdminNoteDim) return;
                    setFvAdminNotes(prev => ({ ...prev, [fvAdminNoteDim]: fvAdminNoteDraft }));
                    setFvAdminNoteDim(d);
                    setFvAdminNoteDraft(fvAdminNotes[d] || "");
                  };
                  return (
                  <>
                    <div onClick={() => { setFvAdminNoteOpen(false); }} style={{ position: "fixed", inset: 0, background: "oklch(0.15 0.02 250 / 0.20)", zIndex: 999 }} />
                    <div style={{ position: "fixed", top: "8%", right: 24, width: "38vw", minWidth: 480, maxWidth: 700, height: "48vh", minHeight: 340, zIndex: 1000, background: "oklch(1 0 0)", borderRadius: 10, boxShadow: "0 24px 64px oklch(0.06 0.02 250 / 0.22)", display: "flex", flexDirection: "column", border: "1.5px solid oklch(0.9 0.01 250)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid oklch(0.92 0.01 250)" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "oklch(0.12 0.02 250)", display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.45 0.14 250)" }}>sticky_note_2</span> Admin Notes
                        </span>
                        <button onClick={() => setFvAdminNoteOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                          <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.5 0.02 250)" }}>close</span>
                        </button>
                      </div>
                      <div style={{ display: "flex", gap: 6, padding: "10px 18px 0", flexWrap: "wrap" }}>
                        {NOTE_DIMS.map(d => (
                          <button key={d} onClick={() => switchDim(d)}
                            style={{ padding: "4px 12px", fontSize: 11, borderRadius: 20, border: d === fvAdminNoteDim ? "1px solid oklch(0.45 0.14 250)" : "1px solid oklch(0.92 0.01 250)", background: d === fvAdminNoteDim ? "oklch(0.45 0.14 250)" : "oklch(0.97 0.005 250)", color: d === fvAdminNoteDim ? "oklch(0.95 0.005 250)" : "oklch(0.5 0.02 250)", cursor: "pointer", fontWeight: 700, letterSpacing: "0.3px", transition: "all 0.15s ease" }}
                          >{d.replace(" Forecast", " Fcst")}</button>
                        ))}
                      </div>
                      <textarea
                        autoFocus
                        style={{ flex: 1, margin: "10px 18px", padding: "10px 12px", fontSize: 13, lineHeight: 1.6, border: "1px solid oklch(0.88 0.01 250)", borderRadius: 8, resize: "none", fontFamily: "inherit", color: "oklch(0.12 0.02 250)", background: "oklch(0.99 0.003 250)" }}
                        value={fvAdminNoteDraft}
                        onChange={e => setFvAdminNoteDraft(e.target.value)}
                        placeholder={`Notes for ${fvAdminNoteDim}…`}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "0 18px 14px" }}>
                        {fvAdminNoteSaved && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "oklch(0.42 0.15 155)", transition: "opacity 0.3s", marginRight: "auto" }}>
                            <span className="material-icons" style={{ fontSize: 16 }}>check_circle</span> Note saved
                          </span>
                        )}
                        <button
                          onClick={() => setFvAdminNoteOpen(false)}
                          style={{ padding: "7px 14px", fontSize: 13, fontWeight: 600, borderRadius: 7, border: "1px solid oklch(0.88 0.01 250)", background: "oklch(1 0 0)", color: "oklch(0.25 0.02 250)", cursor: "pointer", transition: "background 0.15s" }}
                        >Cancel</button>
                        <button
                          onClick={saveFvAdminNote}
                          disabled={fvAdminNoteSaving}
                          style={{ padding: "7px 14px", fontSize: 13, fontWeight: 600, borderRadius: 7, border: "none", background: "oklch(0.45 0.14 250)", color: "oklch(0.95 0.005 250)", cursor: "pointer", transition: "background 0.15s" }}
                        >{fvAdminNoteSaving ? "Saving…" : "Save"}</button>
                      </div>
                    </div>
                  </>
                  );
                })()}

                {/* Floating Per-CC Note overlay */}
                {fvCCNoteCC && (() => {
                  const ccVal = fvCCNoteCC;
                  const CC_NOTE_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];
                  const switchCCDim = (d: string) => {
                    if (d === fvCCNoteDim) return;
                    setFvCCNoteDim(d);
                    api.get("/admin/forecast-comments", { params: { dimension: d } })
                      .then(r => { setFvCCNoteDraft((r.data.comments || {})[ccVal] || ""); })
                      .catch(() => setFvCCNoteDraft(""));
                  };
                  const saveCCNote = () => {
                    setFvCCNoteSaving(true);
                    api.put("/admin/forecast-comment", { dimension: fvCCNoteDim, cost_center: ccVal, comment: fvCCNoteDraft })
                      .then(() => {
                        if (fvCCNoteDim === activeDimForAV) setFvComments(prev => ({ ...prev, [ccVal]: fvCCNoteDraft }));
                        setFvCCNoteSaved(true);
                        setTimeout(() => setFvCCNoteSaved(false), 2500);
                      })
                      .catch(() => {})
                      .finally(() => setFvCCNoteSaving(false));
                  };
                  return (
                  <>
                    <div onClick={() => setFvCCNoteCC(null)} style={{ position: "fixed", inset: 0, background: "oklch(0.15 0.02 250 / 0.20)", zIndex: 999 }} />
                    <div style={{ position: "fixed", top: "8%", right: 24, width: "38vw", minWidth: 480, maxWidth: 700, height: "48vh", minHeight: 340, zIndex: 1000, background: "oklch(1 0 0)", borderRadius: 10, boxShadow: "0 24px 64px oklch(0.06 0.02 250 / 0.22)", display: "flex", flexDirection: "column", border: "1.5px solid oklch(0.9 0.01 250)" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid oklch(0.92 0.01 250)" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "oklch(0.12 0.02 250)", display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.45 0.14 250)" }}>comment</span> Notes for CC {ccVal}
                        </span>
                        <button onClick={() => setFvCCNoteCC(null)} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                          <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.5 0.02 250)" }}>close</span>
                        </button>
                      </div>
                      <div style={{ display: "flex", gap: 6, padding: "10px 18px 0", flexWrap: "wrap" }}>
                        {CC_NOTE_DIMS.map(d => (
                          <button key={d} onClick={() => switchCCDim(d)}
                            style={{ padding: "4px 12px", fontSize: 11, borderRadius: 20, border: d === fvCCNoteDim ? "1px solid oklch(0.45 0.14 250)" : "1px solid oklch(0.92 0.01 250)", background: d === fvCCNoteDim ? "oklch(0.45 0.14 250)" : "oklch(0.97 0.005 250)", color: d === fvCCNoteDim ? "oklch(0.95 0.005 250)" : "oklch(0.5 0.02 250)", cursor: "pointer", fontWeight: 700, letterSpacing: "0.3px", transition: "all 0.15s ease" }}
                          >{d.replace(" Forecast", " Fcst")}</button>
                        ))}
                      </div>
                      <textarea
                        autoFocus
                        style={{ flex: 1, margin: "10px 18px", padding: "10px 12px", fontSize: 13, lineHeight: 1.6, border: "1px solid oklch(0.88 0.01 250)", borderRadius: 8, resize: "none", fontFamily: "inherit", color: "oklch(0.12 0.02 250)", background: "oklch(0.99 0.003 250)" }}
                        value={fvCCNoteDraft}
                        onChange={e => setFvCCNoteDraft(e.target.value)}
                        placeholder={`Notes for CC ${ccVal}, ${fvCCNoteDim}…`}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "0 18px 14px" }}>
                        {fvCCNoteSaved && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "oklch(0.42 0.15 155)", transition: "opacity 0.3s", marginRight: "auto" }}>
                            <span className="material-icons" style={{ fontSize: 16 }}>check_circle</span> Note saved
                          </span>
                        )}
                        <button
                          onClick={() => setFvCCNoteCC(null)}
                          style={{ padding: "7px 14px", fontSize: 13, fontWeight: 600, borderRadius: 7, border: "1px solid oklch(0.88 0.01 250)", background: "oklch(1 0 0)", color: "oklch(0.25 0.02 250)", cursor: "pointer", transition: "background 0.15s" }}
                        >Cancel</button>
                        <button
                          onClick={saveCCNote}
                          disabled={fvCCNoteSaving}
                          style={{ padding: "7px 14px", fontSize: 13, fontWeight: 600, borderRadius: 7, border: "none", background: "oklch(0.45 0.14 250)", color: "oklch(0.95 0.005 250)", cursor: "pointer", transition: "background 0.15s" }}
                        >{fvCCNoteSaving ? "Saving…" : "Save"}</button>
                      </div>
                    </div>
                  </>
                  );
                })()}

                {/* Main Table */}
                <div style={{ overflowX: "auto" }}>
                  <table className="fv-table">
                    <thead>
                      <tr>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleFvSort("cc")}>
                          Cost Center <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle", opacity: fvSortCol === "cc" ? 1 : 0.3 }}>{fvSortCol === "cc" && fvSortDir === "desc" ? "arrow_downward" : "arrow_upward"}</span>
                        </th>
                        <th className="fv-th-right fv-th--ab" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleFvSort("annual")}>
                          Annual Budget <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle", opacity: fvSortCol === "annual" ? 1 : 0.3 }}>{fvSortCol === "annual" && fvSortDir === "desc" ? "arrow_downward" : "arrow_upward"}</span>
                        </th>
                        {showRef && <th className="fv-th-right" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleFvSort("ab")}>
                          {refLabel} <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle", opacity: fvSortCol === "ab" ? 1 : 0.3 }}>{fvSortCol === "ab" && fvSortDir === "desc" ? "arrow_downward" : "arrow_upward"}</span>
                        </th>}
                        {showCur && <th className="fv-th-right" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleFvSort("forecast")}>
                          {curLabel} <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle", opacity: fvSortCol === "forecast" ? 1 : 0.3 }}>{fvSortCol === "forecast" && fvSortDir === "desc" ? "arrow_downward" : "arrow_upward"}</span>
                        </th>}
                        {showCur && <th className="fv-th-right" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleFvSort("variance")}>
                          Variance <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle", opacity: fvSortCol === "variance" ? 1 : 0.3 }}>{fvSortCol === "variance" && fvSortDir === "desc" ? "arrow_downward" : "arrow_upward"}</span>
                        </th>}
                        <th className="fv-th-right">Variance Allowed</th>
                        <th className="fv-th-right">Target</th>
                        <th className="fv-th-center" style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleFvSort("status")}>
                          Status <span className="material-icons" style={{ fontSize: 13, verticalAlign: "middle", opacity: fvSortCol === "status" ? 1 : 0.3 }}>{fvSortCol === "status" && fvSortDir === "desc" ? "arrow_downward" : "arrow_upward"}</span>
                        </th>
                        <th className="fv-th-center"></th>
                        <th className="fv-th-center" style={{ fontSize: 10, width: 40, padding: "4px 2px" }}>Edit</th>
                        <th className="fv-th-center" style={{ fontSize: 10, width: 40, padding: "4px 2px" }}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visRows.map(row => {
                        const cc       = row.cost_center_number;
                        const abTotal  = row.annual_budget_total    ?? 0;
                        const refTotal = row.ref_forecast_total     ?? 0;
                        const dimTotal = row.dimension_total        ?? 0;
                        const prevTotal = showRef ? refTotal : abTotal;
                        const variance  = dimTotal - prevTotal;
                        const janAV    = (row as any)[activeAVKey]   ?? 0;
                        const target   = prevTotal + janAV;
                        const isMet    = Math.round(dimTotal) <= Math.round(target);
                        const pct      = target > 0 ? Math.min((dimTotal / target) * 100, 150) : 0;
                        const isEditingJanAV = editingJanAV[cc] !== undefined;
                        const avDraft  = editingJanAV[cc] ?? String(janAV);
                        const isSaving = savingTarget === cc;
                        const isDling  = downloadingCC === cc;
                        const isChartOpen = fvChartCC === cc;
                        return (
                          <React.Fragment key={cc}>
                          <tr className={isChartOpen ? "fv-row--chart-open" : undefined}>
                            {/* Cost Center: CC#, name, owner, BU combined */}
                            <td className="fv-cc-cell">
                              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span className="fv-cc-num">{cc}</span>
                                  <span className="fv-cc-name">{row.cost_center_name}</span>
                                  {row.submission_status && (
                                    <span className={`fv-submission-pill fv-submission-pill--${row.submission_status}`}>
                                      {row.submission_status === "submitted" ? "Submitted" : "Draft"}
                                    </span>
                                  )}
                                  {onImpersonate && (
                                    <button
                                      className="fv-view-btn fv-view-btn--hover-only"
                                      onClick={() => onImpersonate(cc, row.cost_center_name, row.cost_center_owner || "")}
                                      title="View as CC Owner"
                                    >
                                      <span className="material-icons" style={{ fontSize: 14 }}>visibility</span>
                                    </button>
                                  )}
                                  <button
                                    className={`fv-view-btn${fvChartCC === cc ? "" : " fv-view-btn--hover-only"}`}
                                    onClick={(e) => { e.stopPropagation(); handleFvChart(cc); }}
                                    title="Annual Budget vs Forecast"
                                    style={fvChartCC === cc ? { background: "oklch(0.93 0.04 285)", color: "#7c3aed" } : undefined}
                                  >
                                    <span className="material-icons" style={{ fontSize: 14 }}>{fvChartType === "bar" ? "bar_chart" : "show_chart"}</span>
                                  </button>
                                </div>
                                <div className="fv-cc-meta">
                                  {row.cost_center_owner}{row.business_unit ? ` · ${row.business_unit}` : ""}
                                </div>
                              </div>
                            </td>
                            {/* Annual Budget — always shown */}
                            <td className="fv-amount fv-amount--dim fv-amount--ab">
                              ${(Math.round(abTotal) || 0).toLocaleString()}
                            </td>
                            {/* Reference Forecast (e.g. July Forecast) */}
                            {showRef && (
                              <td className="fv-amount fv-amount--dim">
                                ${(Math.round(refTotal) || 0).toLocaleString()}
                              </td>
                            )}
                            {/* Current Forecast — hidden when same as AB */}
                            {showCur && (
                              <td className="fv-amount fv-amount--primary">
                                ${(Math.round(dimTotal) || 0).toLocaleString()}
                              </td>
                            )}
                            {/* Variance = Current - Previous (over = red brackets, under = green plain) */}
                            {showCur && (
                              <td className={`fv-amount ${variance > 0 ? "fv-amount--red" : variance < 0 ? "fv-amount--green" : ""}`}>
                                {variance > 0 ? `($${Math.round(variance).toLocaleString()})` : variance < 0 ? `$${Math.round(Math.abs(variance)).toLocaleString()}` : "$0"}
                              </td>
                            )}
                            {/* Variance Allowed — editable */}
                            <td className="fv-amount">
                              {isEditingJanAV ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                  <input
                                    autoFocus
                                    className="fv-target-input"
                                    type="number" step={1000}
                                    value={avDraft}
                                    onChange={e => setEditingJanAV(prev => ({ ...prev, [cc]: e.target.value }))}
                                    onBlur={() => saveJanAV(cc)}
                                    onKeyDown={e => {
                                      if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                      if (e.key === "Escape") setEditingJanAV(e2 => { const n = { ...e2 }; delete n[cc]; return n; });
                                    }}
                                  />
                                  {isSaving && <span className="material-icons fv-spin" style={{ fontSize: 14, color: "#6b7280" }}>sync</span>}
                                </div>
                              ) : (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                                  <span className={janAV === 0 ? "fv-amount--dim" : janAV < 0 ? "fv-amount--red" : "fv-amount--green"}>
                                    {janAV === 0 ? "$0" : janAV < 0 ? `($${Math.round(Math.abs(janAV)).toLocaleString()})` : `$${Math.round(janAV).toLocaleString()}`}
                                  </span>
                                  <button
                                    className="fv-edit-btn"
                                    title="Edit allowed variance"
                                    onClick={() => setEditingJanAV(prev => ({ ...prev, [cc]: String(janAV) }))}
                                  >
                                    <span className="material-icons" style={{ fontSize: 14 }}>edit</span>
                                  </button>
                                </div>
                              )}
                            </td>
                            {/* Target */}
                            <td className="fv-amount fv-amount--bold">
                              ${(Math.round(target) || 0).toLocaleString()}
                            </td>
                            {/* Status */}
                            <td style={{ textAlign: "center" }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                {isMet
                                  ? <span className="fv-chip fv-chip--met"><span className="material-icons" style={{ fontSize: 12 }}>check_circle</span> Met</span>
                                  : <span className="fv-chip fv-chip--over"><span className="material-icons" style={{ fontSize: 12 }}>warning</span> Over</span>
                                }
                                <div className="fv-progress-wrap">
                                  <div
                                    className={`fv-progress-bar ${isMet ? "fv-progress-bar--met" : "fv-progress-bar--over"}`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            {/* Download */}
                            <td style={{ textAlign: "center" }}>
                              <button
                                className="fv-dl-btn"
                                onClick={() => handleFinanceDownload(cc)}
                                disabled={isDling}
                                title={`Download ${cc} plan`}
                              >
                                <span className="material-icons" style={{ fontSize: 16 }}>
                                  {isDling ? "hourglass_empty" : "download"}
                                </span>
                              </button>
                            </td>
                            {/* Per-CC Edit Override — disabled when master toggle is ON */}
                            <td style={{ textAlign: "center", width: 40, padding: "4px 2px" }}>
                              <button
                                onClick={() => !forecastActive && toggleCCEditOverride(cc)}
                                disabled={forecastActive}
                                title={forecastActive ? "Per-CC overrides are not needed when editing is globally enabled" : ccEditOverrides[cc] ? `Click to lock editing for CC ${cc} — currently unlocked so CC owner can edit` : `Click to unlock editing for CC ${cc} — currently locked, CC owner cannot edit`}
                                style={{ border: "1px solid", borderColor: forecastActive ? "oklch(0.88 0.01 250)" : ccEditOverrides[cc] ? "oklch(0.70 0.14 155)" : "oklch(0.82 0.02 250)", background: forecastActive ? "oklch(0.96 0.005 250)" : ccEditOverrides[cc] ? "oklch(0.94 0.04 155)" : "oklch(0.97 0.005 250)", borderRadius: 5, cursor: forecastActive ? "default" : "pointer", padding: "3px 5px", display: "inline-flex", alignItems: "center", justifyContent: "center", opacity: forecastActive ? 0.45 : 1 }}
                              >
                                <span className="material-icons" style={{ fontSize: 19, color: forecastActive ? "oklch(0.70 0.01 250)" : ccEditOverrides[cc] ? "oklch(0.45 0.18 155)" : "oklch(0.45 0.03 250)" }}>
                                  {ccEditOverrides[cc] ? "lock_open" : "lock"}
                                </span>
                              </button>
                            </td>
                            {/* CC Comment */}
                            <td style={{ textAlign: "center", width: 40, padding: "4px 2px" }}>
                              <button
                                style={{ border: "1px solid", borderColor: fvComments[cc] ? "oklch(0.72 0.10 250)" : "oklch(0.82 0.02 250)", background: fvComments[cc] ? "oklch(0.94 0.03 250)" : "oklch(0.97 0.005 250)", borderRadius: 5, cursor: "pointer", padding: "3px 5px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                                onClick={() => { setFvCCNoteCC(cc); setFvCCNoteDim(activeDimForAV); setFvCCNoteDraft(fvComments[cc] || ""); }}
                                title={fvComments[cc] ? `View or edit admin notes for CC ${cc}` : `Add admin notes for CC ${cc}`}
                              >
                                <span className="material-icons" style={{ fontSize: 19, color: fvComments[cc] ? "oklch(0.40 0.15 250)" : "oklch(0.45 0.03 250)" }}>
                                  {fvComments[cc] ? "comment" : "add_comment"}
                                </span>
                              </button>
                            </td>
                          </tr>
                          {isChartOpen && (() => {
                            const colCount = 8 + (showCur ? 2 : 0);
                            const fmtAxis = (v: number) => {
                              if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
                              if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
                              return `$${v}`;
                            };
                            const fmtBarLabel = (v: number) => {
                              if (!v) return "";
                              if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
                              if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1_000)}K`;
                              return `$${v}`;
                            };
                            if (fvChartLoading) return (
                              <tr><td colSpan={colCount} style={{ padding: "16px 24px", color: "#9ca3af", fontStyle: "italic", fontSize: 12, background: "oklch(0.975 0.005 250)" }}>Loading chart…</td></tr>
                            );
                            if (!fvChartData || fvChartData.length === 0) return (
                              <tr><td colSpan={colCount} style={{ padding: "16px 24px", color: "#9ca3af", fontSize: 12, background: "oklch(0.975 0.005 250)" }}>No data available</td></tr>
                            );
                            const chartPoints = fvChartData.map((p: any) => ({
                              name: p.short,
                              annualBudget: Math.round(p.annualBudget || 0),
                              forecast: Math.round(p.budget || 0),
                              actual: p.hasActuals ? Math.round(p.actual || 0) : null,
                              delta: Math.round((p.budget || 0) - (p.annualBudget || 0)),
                            }));
                            const abTotal = fvChartData.reduce((s: number, p: any) => s + (p.annualBudget || 0), 0);
                            const fcTotal = fvChartData.reduce((s: number, p: any) => s + (p.budget || 0), 0);
                            const actTotal = fvChartData.filter((p: any) => p.hasActuals).reduce((s: number, p: any) => s + (p.actual || 0), 0);
                            const delta = fcTotal - abTotal;
                            const dimLabel = varActiveDim.replace(/ FY\d+$/i, "");
                            return (
                              <tr>
                                <td colSpan={colCount} style={{ padding: 0, background: "oklch(0.975 0.005 250)", borderBottom: "2px solid oklch(0.88 0.02 250)" }}>
                                  <div style={{ padding: "12px 20px 16px" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13, fontWeight: 700, color: "oklch(0.25 0.02 250)" }}>
                                        <span>{cc} — Annual Budget vs {dimLabel}</span>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: delta > 0 ? "#dc2626" : delta < 0 ? "#059669" : "#64748b", background: delta > 0 ? "oklch(0.95 0.04 25)" : delta < 0 ? "oklch(0.95 0.04 160)" : "oklch(0.95 0.005 250)", padding: "2px 8px", borderRadius: 4 }}>
                                          {delta > 0 ? "+" : ""}{fmtMoney(delta)} net change
                                        </span>
                                      </div>
                                      <button
                                        className="dash-trend-type-toggle"
                                        onClick={() => setFvChartType(t => t === "bar" ? "line" : "bar")}
                                        style={{ fontSize: 11 }}
                                      >
                                        <span className="material-icons" style={{ fontSize: 14 }}>{fvChartType === "bar" ? "show_chart" : "bar_chart"}</span>
                                        {fvChartType === "bar" ? "Line" : "Bar"}
                                      </button>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "center", gap: 24, padding: "4px 0 6px", fontSize: 12, fontWeight: 600, color: "oklch(0.35 0.02 250)" }}>
                                      <span>Annual Budget: <span style={{ color: "#4A90E2" }}>{fmtMoney(abTotal)}</span></span>
                                      <span>{dimLabel}: <span style={{ color: "#7c3aed" }}>{fmtMoney(fcTotal)}</span></span>
                                      {actTotal > 0 && <span>YTD Actuals: <span style={{ color: "#0891b2" }}>{fmtMoney(actTotal)}</span></span>}
                                      <span>Delta: <span style={{ color: delta > 0 ? "#dc2626" : "#059669" }}>{delta > 0 ? "+" : ""}{fmtMoney(delta)}</span></span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={220}>
                                      {fvChartType === "bar" ? (
                                        <BarChart data={chartPoints} margin={{ top: 16, right: 12, left: 0, bottom: 0 }} barGap={2} barCategoryGap="18%">
                                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                                          <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: "#64748b" }} width={56} />
                                          <Tooltip
                                            formatter={(v: any, name: string) => [fmtMoney(Number(v) || 0), name]}
                                            labelFormatter={(label: string) => {
                                              const pt = chartPoints.find((d: any) => d.name === label);
                                              return pt ? `${label}  |  Delta: ${pt.delta > 0 ? "+" : ""}${fmtMoney(pt.delta)}` : label;
                                            }}
                                          />
                                          <Legend wrapperStyle={{ fontSize: 11 }} />
                                          <Bar dataKey="annualBudget" name="Annual Budget" fill="#4A90E2" radius={[3, 3, 0, 0]}>
                                            <LabelList dataKey="annualBudget" position="top" formatter={fmtBarLabel} style={{ fontSize: 9, fill: "#4A90E2", fontWeight: 600 }} />
                                          </Bar>
                                          <Bar dataKey="forecast" name={dimLabel} fill="#7c3aed" radius={[3, 3, 0, 0]}>
                                            <LabelList dataKey="forecast" position="top" formatter={fmtBarLabel} style={{ fontSize: 9, fill: "#7c3aed", fontWeight: 600 }} />
                                          </Bar>
                                          <Bar dataKey="actual" name="Actuals" fill="#0891b2" radius={[3, 3, 0, 0]}>
                                            <LabelList dataKey="actual" position="top" formatter={fmtBarLabel} style={{ fontSize: 9, fill: "#0891b2", fontWeight: 600 }} />
                                          </Bar>
                                        </BarChart>
                                      ) : (
                                        <LineChart data={chartPoints} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                                          <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: "#64748b" }} width={56} />
                                          <Tooltip
                                            formatter={(v: any, name: string) => [fmtMoney(Number(v) || 0), name]}
                                            labelFormatter={(label: string) => {
                                              const pt = chartPoints.find((d: any) => d.name === label);
                                              return pt ? `${label}  |  Delta: ${pt.delta > 0 ? "+" : ""}${fmtMoney(pt.delta)}` : label;
                                            }}
                                          />
                                          <Legend wrapperStyle={{ fontSize: 11 }} />
                                          <Line type="monotone" dataKey="annualBudget" name="Annual Budget" stroke="#4A90E2" strokeWidth={2} dot={{ r: 3, fill: "#4A90E2", strokeWidth: 1.5, stroke: "#fff" }} />
                                          <Line type="monotone" dataKey="forecast" name={dimLabel} stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 3, fill: "#7c3aed", strokeWidth: 1.5, stroke: "#fff" }} />
                                          <Line type="monotone" dataKey="actual" name="Actuals" stroke="#0891b2" strokeWidth={2} dot={{ r: 3, fill: "#0891b2", strokeWidth: 1.5, stroke: "#fff" }} connectNulls={false} />
                                        </LineChart>
                                      )}
                                    </ResponsiveContainer>
                                  </div>
                                </td>
                              </tr>
                            );
                          })()}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Variance Allowances sub-section */}
                {varRows.length > 0 && (
                  <div className="fv-variance-section">
                    <div className="fv-variance-header">
                      <span className="fv-variance-title">Forecast Variance Allowances</span>
                      <span className="fv-variance-desc">Max $ a forecast may exceed the Annual Budget per cost center</span>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table className="fv-table">
                        <thead>
                          <tr>
                            <th>Cost Center</th>
                            {["July Forecast", "October Forecast", "January Forecast"].map(dim => (
                              <th key={dim} className={`fv-th-right${dim === varActiveDim ? " fv-th-active" : ""}`}>
                                {dim.replace(" Forecast", "")} Variance
                                {dim === varActiveDim && (
                                  <span className="fv-dimension-badge" style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px" }}>Active</span>
                                )}
                              </th>
                            ))}
                            <th className="fv-th-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {varRows.filter(r => !financeFilterBU || r.business_unit === financeFilterBU).map(row => {
                            const totalVar = (row.dims["July Forecast"] ?? 0) + (row.dims["October Forecast"] ?? 0) + (row.dims["January Forecast"] ?? 0);
                            return (
                              <tr key={row.cost_center_number}>
                                <td>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span className="fv-cc-num">{row.cost_center_number}</span>
                                    <span className="fv-cc-name">{row.cost_center_name}</span>
                                  </div>
                                </td>
                                {["July Forecast", "October Forecast", "January Forecast"].map(dim => {
                                  const val = row.dims[dim] ?? 0;
                                  const isActive = dim === varActiveDim;
                                  return (
                                    <td key={dim} className={`fv-amount${isActive ? " fv-var-active-cell" : " fv-var-inactive-cell"}`}>
                                      {val === 0 ? "$0" : `$${val.toLocaleString()}`}
                                    </td>
                                  );
                                })}
                                <td className="fv-amount fv-var-total-cell">
                                  {totalVar === 0 ? "$0" : `$${totalVar.toLocaleString()}`}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* ── Export Template Modal ────────────────────────────────────────── */}
      {exportModalCC && (
        <div className="export-modal-overlay" onClick={() => !exporting && setExportModalCC(null)}>
          <div className="export-modal" onClick={e => e.stopPropagation()}>
            <div className="export-modal-header">
              <span className="material-icons">file_download</span>
              <span>Export &mdash; CC {exportModalCC}</span>
              <button className="export-modal-close" onClick={() => setExportModalCC(null)} disabled={exporting}>
                <span className="material-icons">close</span>
              </button>
            </div>

            {/* Step 1: File Upload */}
            {!exportValidation && !exportNeedsPassword && (
              <div className="export-modal-body">
                <p className="export-modal-desc">
                  Upload the budget file for CC {exportModalCC}. The system will write the current forecast data into the Plan, Salary, and BIP tabs, preserving your formulas and automations.
                </p>
                <label
                  className={`export-drop-zone ${exportValidating ? "export-drop-zone--loading" : ""}`}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("export-drop-zone--hover"); }}
                  onDragLeave={e => e.currentTarget.classList.remove("export-drop-zone--hover")}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("export-drop-zone--hover"); const f = e.dataTransfer.files[0]; if (f) handleExportValidate(f); }}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleExportValidate(f); }}
                  />
                  <span className="material-icons export-drop-icon">upload_file</span>
                  <span className="export-drop-text">
                    {exportValidating ? "Validating..." : "Drop budget file here or click to browse"}
                  </span>
                  <span className="export-drop-hint">.xlsx budget template for CC {exportModalCC}</span>
                </label>
                {exportError && <div className="export-error">{exportError}</div>}
              </div>
            )}

            {/* Step 1b: Password Required */}
            {!exportValidation && exportNeedsPassword && (
              <div className="export-modal-body">
                <p className="export-modal-desc">
                  This file is password-protected. Enter the password to continue.
                </p>
                <div className="export-pw-row">
                  <span className="material-icons export-pw-icon">lock</span>
                  <input
                    type="password"
                    className="export-pw-input"
                    placeholder="File password"
                    value={exportPassword}
                    onChange={e => setExportPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleExportPasswordSubmit(); }}
                    autoFocus
                  />
                </div>
                {exportFile && (
                  <div className="export-pw-file">
                    <span className="material-icons" style={{ fontSize: 14 }}>description</span>
                    {exportFile.name}
                  </div>
                )}
                {exportError && <div className="export-error">{exportError}</div>}
                <div className="export-modal-actions">
                  <button
                    className="export-btn-secondary"
                    onClick={() => { setExportNeedsPassword(false); setExportFile(null); setExportPassword(""); setExportError(null); }}
                  >
                    Change File
                  </button>
                  <button
                    className="export-btn-primary"
                    onClick={handleExportPasswordSubmit}
                    disabled={!exportPassword || exportValidating}
                  >
                    {exportValidating ? "Unlocking..." : "Unlock & Continue"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Verification */}
            {exportValidation && (
              <div className="export-modal-body">
                {!exportValidation.ccMatch && (
                  <div className="export-error" style={{ marginBottom: 14 }}>
                    <strong>Cost center mismatch:</strong> This file belongs to CC {exportValidation.fileCC}, but you are exporting CC {exportModalCC}. Please upload the correct file.
                  </div>
                )}
                <p className="export-modal-desc">
                  Select the dimension to export, verify the tabs below, then click Export.
                </p>

                <div className="export-dim-selector">
                  {["Annual Budget", "July Forecast", "October Forecast", "January Forecast"].map(dim => (
                    <button
                      key={dim}
                      className={`export-dim-btn ${exportDimension === dim ? "export-dim-btn--active" : ""}`}
                      onClick={() => setExportDimension(dim)}
                      disabled={exporting}
                    >
                      {dim}
                    </button>
                  ))}
                </div>

                <div className="export-verify-heading">Sheets to write:</div>
                <div className="export-verify-grid">
                  <div className={`export-verify-card ${exportValidation.sheets.salary ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportValidation.sheets.salary ? "check_circle" : "cancel"}</span>
                    <span className="export-verify-label">Salary60010</span>
                    <span className="export-verify-detail">{exportValidation.sheets.salary ? `${exportValidation.counts.employees} employees` : "Not found"}</span>
                  </div>
                  <div className={`export-verify-card ${exportValidation.sheets.bip ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportValidation.sheets.bip ? "check_circle" : "cancel"}</span>
                    <span className="export-verify-label">BIP60022</span>
                    <span className="export-verify-detail">{exportValidation.sheets.bip ? `${exportValidation.counts.employees} employees` : "Not found"}</span>
                  </div>
                  <div className={`export-verify-card ${exportPlanSheet ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportPlanSheet ? "check_circle" : "cancel"}</span>
                    {exportValidation.sheets.planSheets && exportValidation.sheets.planSheets.length > 1 ? (
                      <select
                        className="export-plan-select"
                        value={exportPlanSheet}
                        onChange={e => setExportPlanSheet(e.target.value)}
                      >
                        {exportValidation.sheets.planSheets.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="export-verify-label">{exportPlanSheet || "PLANFY__ (missing)"}</span>
                    )}
                    <span className="export-verify-detail">{exportPlanSheet ? `${exportValidation.counts.budgetAccounts} accounts` : "No plan sheet found"}</span>
                  </div>
                </div>

                <div className="export-meta">
                  <div><strong>Dimension:</strong> {exportDimension} {exportValidation.fy}</div>
                  <div><strong>Output file:</strong> {exportFile?.name?.replace(/\.[^.]+$/, "")}_{({"Annual Budget":"ab","July Forecast":"jff","October Forecast":"off","January Forecast":"janff"} as Record<string,string>)[exportDimension] || "ab"}_{new Date().toISOString().slice(0,10).replace(/-/g,"")}.xlsx</div>
                  {exportFile && <div><strong>Template:</strong> {exportFile.name}</div>}
                </div>

                {!exportValidation.valid && (
                  <div className="export-error">
                    Template is missing expected sheets. Found: {exportValidation.allSheets.join(", ")}
                  </div>
                )}
                {exportError && <div className="export-error">{exportError}</div>}

                <div className="export-modal-actions">
                  <button
                    className="export-btn-secondary"
                    onClick={() => { setExportValidation(null); setExportFile(null); setExportPassword(""); setExportNeedsPassword(false); setExportError(null); }}
                    disabled={exporting}
                  >
                    Change File
                  </button>
                  <button
                    className="export-btn-primary"
                    onClick={handleExportDownload}
                    disabled={exporting || !exportValidation.valid || !exportValidation.ccMatch}
                  >
                    {exporting ? "Exporting..." : "Export & Download"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Temp password confirmation modal ─────────────────────────────── */}
      {tempPwModal && (
        <div className="dl-summary-overlay" onClick={() => !tempPwSetting && setTempPwModal(null)}>
          <div className="dl-summary-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
              <span className="material-icons" style={{ color: "#2563eb", fontSize: 22, marginTop: 2 }}>key</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Reset Temporary Password</div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>
                  You are about to reset the password for <strong>{tempPwModal.owner}</strong> (CC {tempPwModal.cc}).
                  A temporary password will be generated and the following email message will be copied to your clipboard.
                </div>
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              Email preview
            </div>
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "12px 14px", fontSize: 13, color: "#1e293b", lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 16, fontFamily: "inherit" }}>
              <span style={{ color: "#64748b" }}>Subject: </span>Temp QFT Password{"\n\n"}
              Hello {tempPwModal.owner},{"\n\n"}
              Your temporary QFT password is: <strong style={{ fontFamily: "monospace", letterSpacing: "0.05em" }}>{tempPwModal.password}</strong>{"\n\n"}
              You will be prompted to create a new password the next time you log in. Please keep this password secure.{"\n\n"}
              Best regards,{"\n"}QFT Admin
            </div>

            {tempPwModalErr && (
              <div className="admin-toast admin-toast--err" style={{ marginBottom: 12 }}>
                <span className="material-icons" style={{ fontSize: 13 }}>error</span>
                {tempPwModalErr}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="admin-btn admin-btn--ghost" onClick={() => setTempPwModal(null)} disabled={tempPwSetting}>
                Cancel
              </button>
              <button className="admin-btn admin-btn--primary" onClick={doSetTempPassword} disabled={tempPwSetting}>
                <span className="material-icons" style={{ fontSize: 16 }}>
                  {tempPwSetting ? "hourglass_empty" : "content_copy"}
                </span>
                {tempPwSetting ? "Setting…" : "Set Password & Copy Message"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Temp password success toast ───────────────────────────────────── */}
      {tempPwDoneMsg && (
        <div className="admin-selection-toast" style={{ background: "#f0fdf4", borderColor: "#86efac", color: "#15803d" }}>
          <span className="material-icons" style={{ color: "#16a34a" }}>mark_email_read</span>
          {tempPwDoneMsg}
          <button className="admin-selection-toast-close" onClick={() => setTempPwDoneMsg(null)}>
            <span className="material-icons">close</span>
          </button>
        </div>
      )}

      {/* ── Selection toast ───────────────────────────────────────────────── */}
      {selectionMsg && (
        <div className="admin-selection-toast">
          <span className="material-icons">check_circle</span>
          {selectionMsg}
          <button className="admin-selection-toast-close" onClick={() => setSelectionMsg(null)}>
            <span className="material-icons">close</span>
          </button>
        </div>
      )}

      {/* ── Delete confirmation modal ──────────────────────────────────────── */}
      {deleteConfirm && (
        <div className="dl-summary-overlay" onClick={() => !deletePwBusy && setDeleteConfirm(null)}>
          <div className="dl-summary-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span className="material-icons" style={{ color: "#dc2626", fontSize: 22 }}>warning</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>Confirm Delete</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {deleteConfirm.type === "cc"
                    ? `Cost center ${deleteConfirm.key} and all its data will be permanently deleted.`
                    : `Account "${deleteConfirm.key}" and all associated budget/actuals rows will be permanently deleted.`}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
              Enter your admin password to proceed:
            </div>
            <input
              type="password"
              className="admin-text-input"
              style={{ width: "100%", marginBottom: 8 }}
              placeholder="Admin password"
              value={deletePw}
              onChange={e => { setDeletePw(e.target.value); setDeletePwErr(""); }}
              onKeyDown={e => { if (e.key === "Enter") confirmDeleteAction(); }}
              autoFocus
            />
            {deletePwErr && (
              <div className="admin-toast admin-toast--err" style={{ marginBottom: 8 }}>
                <span className="material-icons" style={{ fontSize: 13 }}>error</span>
                {deletePwErr}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="admin-btn admin-btn--ghost" onClick={() => setDeleteConfirm(null)} disabled={deletePwBusy}>
                Cancel
              </button>
              <button className="admin-btn admin-btn--danger" onClick={confirmDeleteAction} disabled={deletePwBusy || !deletePw}>
                {deletePwBusy ? "Verifying…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Business Units ──────────────────────────────────────────── */}
      {activeTab === "businessunits" && (
        <div className="admin-section">
          <AdminBusinessUnits />
        </div>
      )}

      {/* ── Tab: Users ───────────────────────────────────────────────────── */}
      {activeTab === "users" && (
        <div className="admin-section">
          <AdminUsers />
        </div>
      )}

      {/* ── Tab: Audit Log ──────────────────────────────────────────────── */}
      {activeTab === "auditlog" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <span className="material-icons">history</span>
            <span className="admin-section-title">Audit Log</span>
            <span className="admin-section-subtitle">
              Track all budget & actuals database write events
            </span>
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Filter by Cost Center"
              value={auditFilter.cc}
              onChange={e => setAuditFilter(f => ({ ...f, cc: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", width: 160, fontSize: 13 }}
            />
            <select
              value={auditFilter.action}
              onChange={e => setAuditFilter(f => ({ ...f, action: e.target.value }))}
              style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
            >
              <option value="">All Actions</option>
              <option value="budget_upload">Budget Upload</option>
              <option value="actuals_upload">Actuals Upload</option>
              <option value="grid_save_budget">Grid Save (Budget)</option>
              <option value="grid_save_actuals">Grid Save (Actuals)</option>
              <option value="cascade_budget">Cascade Budget</option>
              <option value="salary_employee_save">Salary Save</option>
              <option value="salary_employee_delete">Salary Delete</option>
              <option value="salary_auto_compute">Salary Auto-Compute</option>
            </select>
            <button
              className="admin-btn admin-btn--primary"
              onClick={loadAuditLog}
              disabled={auditLoading}
              style={{ fontSize: 13 }}
            >
              {auditLoading ? "Loading..." : "Refresh"}
            </button>
            <span style={{ color: "#888", fontSize: 12 }}>
              {auditEntries.length} entries
            </span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="admin-cc-table" style={{ fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Timestamp</th>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Action</th>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Cost Center</th>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Dimension</th>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>FY</th>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Month</th>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>User</th>
                  <th style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>Rows</th>
                  <th style={{ padding: "8px 10px" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {auditEntries.length === 0 && !auditLoading && (
                  <tr><td colSpan={9} style={{ textAlign: "center", padding: 24, color: "#999" }}>No audit entries found</td></tr>
                )}
                {auditEntries.map((e: any) => (
                  <tr key={e.id}>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 11 }}>
                      {new Date(e.timestamp).toLocaleString()}
                    </td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        background: e.action.includes("upload") ? "oklch(0.92 0.04 145)" :
                                    e.action.includes("delete") ? "oklch(0.92 0.04 25)" :
                                    e.action.includes("cascade") ? "oklch(0.92 0.04 280)" :
                                    "oklch(0.93 0.03 220)",
                        color: e.action.includes("upload") ? "oklch(0.35 0.1 145)" :
                               e.action.includes("delete") ? "oklch(0.35 0.1 25)" :
                               e.action.includes("cascade") ? "oklch(0.35 0.1 280)" :
                               "oklch(0.35 0.08 220)",
                      }}>
                        {e.action.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td style={{ padding: "6px 10px", fontWeight: 600 }}>{e.cost_center || "—"}</td>
                    <td style={{ padding: "6px 10px", fontSize: 11 }}>{e.dimension || "—"}</td>
                    <td style={{ padding: "6px 10px" }}>{e.fiscal_year || "—"}</td>
                    <td style={{ padding: "6px 10px" }}>{e.month || "—"}</td>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
                      {e.user_name || e.user_email || "system"}
                      {e.user_role && <span style={{ color: "#999", fontSize: 10, marginLeft: 4 }}>({e.user_role})</span>}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>{e.rows_affected ?? "—"}</td>
                    <td style={{ padding: "6px 10px", fontSize: 11, color: "#666", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>{e.details || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Journal overlay — rendered at root so it works from any tab */}
      {fvJournalOpen && (() => {
        const J_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];
        const ccLookup: Record<string, { name: string; owner: string }> = {};
        financeRows.forEach(r => { ccLookup[r.cost_center_number] = { name: r.cost_center_name, owner: r.cost_center_owner || "" }; });
        return (
        <>
          <div onClick={() => setFvJournalOpen(false)} style={{ position: "fixed", inset: 0, background: "oklch(0.15 0.02 250 / 0.24)", zIndex: 999 }} />
          <div style={{ position: "fixed", top: "4%", left: "50%", transform: "translateX(-50%)", width: "60vw", minWidth: 520, maxWidth: 900, height: "82vh", minHeight: 400, zIndex: 1000, background: "oklch(1 0 0)", borderRadius: 10, boxShadow: "0 24px 64px oklch(0.06 0.02 250 / 0.22)", display: "flex", flexDirection: "column", border: "1.5px solid oklch(0.9 0.01 250)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid oklch(0.92 0.01 250)" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "oklch(0.12 0.02 250)", display: "flex", alignItems: "center", gap: 6 }}>
                <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.45 0.14 250)" }}>auto_stories</span> Forecast Notes Journal
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  onClick={() => {
                    const lines: string[] = [];
                    J_DIMS.forEach(dim => {
                      lines.push(`\n=== ${dim} ===`);
                      const an = fvAdminNotes[dim];
                      if (an) { lines.push(`\n[Admin Note]\n${an}`); }
                      const ccC = fvJournalData?.[dim] || {};
                      const keys = Object.keys(ccC).filter(k => ccC[k]).sort();
                      if (keys.length > 0) {
                        lines.push("");
                        keys.forEach(cc => {
                          const info = ccLookup[cc];
                          lines.push(`CC ${cc}${info ? ` – ${info.name}` : ""}: ${ccC[cc]}`);
                        });
                      }
                      if (!an && keys.length === 0) lines.push("  No notes");
                    });
                    navigator.clipboard.writeText(lines.join("\n").trim()).then(() => {
                      setFvJournalCopied(true);
                      setTimeout(() => setFvJournalCopied(false), 2000);
                    });
                  }}
                  title="Copy all notes to clipboard"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", fontSize: 11, fontWeight: 500, border: "1px solid", borderColor: fvJournalCopied ? "oklch(0.65 0.14 155)" : "oklch(0.85 0.02 250)", background: fvJournalCopied ? "oklch(0.94 0.04 155)" : "oklch(0.97 0.005 250)", borderRadius: 5, cursor: "pointer", color: fvJournalCopied ? "oklch(0.40 0.14 155)" : "oklch(0.40 0.03 250)", transition: "all 0.2s" }}
                >
                  <span className="material-icons" style={{ fontSize: 14 }}>{fvJournalCopied ? "check" : "content_copy"}</span> {fvJournalCopied ? "Copied!" : "Copy All"}
                </button>
                <button onClick={() => setFvJournalOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", padding: 2 }}>
                  <span className="material-icons" style={{ fontSize: 20, color: "oklch(0.5 0.02 250)" }}>close</span>
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              {fvJournalLoading ? (
                <div style={{ textAlign: "center", padding: 40, color: "oklch(0.55 0.02 250)", fontSize: 13 }}>Loading notes…</div>
              ) : J_DIMS.map(dim => {
                const adminNote = fvAdminNotes[dim] || "";
                const ccComments = fvJournalData?.[dim] || {};
                const ccKeys = Object.keys(ccComments).filter(k => ccComments[k]).sort();
                const hasContent = adminNote || ccKeys.length > 0;
                return (
                  <div key={dim} style={{ marginBottom: 24 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingBottom: 6, borderBottom: "2px solid oklch(0.50 0.12 250)" }}>
                      <span className="material-icons" style={{ fontSize: 16, color: "oklch(0.50 0.12 250)" }}>folder</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "oklch(0.30 0.06 250)" }}>{dim}</span>
                      {!hasContent && <span style={{ fontSize: 11, color: "oklch(0.55 0.02 250)", fontStyle: "italic", marginLeft: "auto" }}>No notes</span>}
                    </div>
                    {adminNote && (
                      <div style={{ background: "oklch(0.97 0.01 280)", border: "1px solid oklch(0.90 0.03 280)", borderRadius: 6, padding: "10px 14px", marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: "oklch(0.45 0.08 280)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: 4 }}>
                          <span className="material-icons" style={{ fontSize: 12 }}>shield</span> Admin Note
                        </div>
                        <div style={{ fontSize: 12, color: "oklch(0.30 0.02 250)", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{adminNote}</div>
                      </div>
                    )}
                    {ccKeys.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: "oklch(0.55 0.02 250)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 2, display: "flex", alignItems: "center", gap: 4 }}>
                          <span className="material-icons" style={{ fontSize: 12 }}>people</span> Cost Center Notes ({ccKeys.length})
                        </div>
                        {ccKeys.map(cc => (
                          <div key={cc} style={{ background: "oklch(0.98 0.005 250)", border: "1px solid oklch(0.92 0.01 250)", borderRadius: 5, padding: "8px 12px", display: "flex", gap: 12, alignItems: "flex-start" }}>
                            <div style={{ minWidth: 90, flexShrink: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "oklch(0.35 0.05 250)" }}>{cc}</div>
                              {ccLookup[cc] && <div style={{ fontSize: 10, color: "oklch(0.55 0.02 250)", lineHeight: 1.3 }}>{ccLookup[cc].name}</div>}
                              {ccLookup[cc]?.owner && <div style={{ fontSize: 10, color: "oklch(0.65 0.015 250)", lineHeight: 1.3 }}>{ccLookup[cc].owner}</div>}
                            </div>
                            <div style={{ fontSize: 12, color: "oklch(0.30 0.02 250)", whiteSpace: "pre-wrap", lineHeight: 1.5, flex: 1 }}>{ccComments[cc]}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
        );
      })()}

    </div>
  );
};

export default AdminPanel;
